import { messages, type Locale, type MessageKey } from '@morales/shared';

/**
 * Strings the screens need that `messages` in packages/shared does not carry yet. Same EN/ES rule:
 * the ES table is typed against the EN one, so a missing translation fails the typecheck.
 * Promote these into packages/shared `messages` the next time the contract changes.
 */
const webEn = {
  greetingMorning: 'Good morning',
  newInvoice: 'Create invoice',
  invoicesTitle: 'Invoices',
  saveNNumbers: 'Save {n} number(s)',
  orderDayUnsaved: 'Store numbers are not saved. Leave anyway?',
  lockedTyped: 'Numbers typed for {stores} cannot be saved here: the order moved on. Change it on the order desk.',
  dropThem: 'Drop them',
  poWrittenShort: 'Orders written',
  storesCol: 'Stores',
  othersCol: 'Others',
  nNotSaved: '{n} number(s) not saved yet',
  nBadNumbers: '{n} cell(s) in red are not numbers: fix them to save',
  allSaved: 'All saved',
  onlyWithNumbers: 'Only rows with numbers',
  saveFirst: 'Save the store numbers first',
  legendUnsaved: 'typed, not saved',
  invoicesHint: 'Every invoice in the dates you pick. Click a row and press Enter (or double-click) to open it.',
  fromDate: 'From',
  toDate: 'To',
  stateAll: 'All',
  stateOpen: 'Open',
  stateOverdue: 'Overdue',
  statePaid: 'Paid',
  stateVoid: 'Void',
  colNo: 'No.',
  colStatus: 'Status',
  colPaid: 'Paid',
  colBalance: 'Balance',
  findInvoice: 'Customer or invoice number…',
  invoicesCountCol: 'Invoices',
  billed: 'Billed',
  noInvoicesHere: 'No invoices match these dates and filters.',
  totalAllInvoices: 'Total of these invoices',
  addInvoice: 'Add another invoice',
  createNInvoices: 'Create {n} invoice(s)',
  alreadyInvoicedOn: 'This customer already has invoice {no} on this date ({total}). Make another only if you mean to.',
  twoCardsSameDay: 'Two invoices here for the same customer and date: put the lines on one.',
  perDay: 'Per day',
  keepCol: 'Keep',
  suggestedCol: 'Suggested',
  costCol: 'Cost',
  findItem: 'Find an item…',
  onlyToBuy: 'Only items to purchase',
  xOfY: '{x} of {y} items',
  ordersByVendor: 'Orders by vendor',
  linesCount: 'Lines',
  unitCol: 'Unit',
  tierCol: 'Price tier',
  productService: 'Product/service',
  pickProduct: 'Pick a product',
  typeToSearch: 'Type to search',
  lineDescription: 'Description',
  rate: 'Rate',
  tierPrice: 'Tier price',
  lastPaid: 'Last paid',
  marginPct: 'Margin',
  dueOnReceipt: 'Due on receipt',
  cameIn: 'Channel',
  invoiceLines: 'Invoice lines',
  pickCustomerFirst: 'Choose a customer to start the lines. Their prices fill in by themselves.',
  netDays: 'Net {n}',
  qtyNeeded: 'A line has a product but no quantity: type the cases (the red cell).',
  changeCustomerClears: 'Changing the customer clears the lines. Go on?',
  clearAllLines: 'Clear all lines',
  pressAgainToClear: 'Press again to clear',
  addUsualItems: 'Add usual items',
  removeLastLine: 'Remove last line',
  messageOnInvoice: 'Message on invoice',
  approveUnderFloor: 'I approve these prices under the floor or under cost',
  invoiceAnyway: 'Invoice anyway (past the hold or the credit limit)',
  saveAndNew: 'Save and new',
  saveAndClose: 'Save and close',
  saveAndPrint: 'Save and print',
  email: 'Email',
  getsInvoicesLabel: 'Send invoices and statements to this email',
  getsInvoicesBadge: 'invoices',
  emailInvoice: 'Email the invoice',
  emailButton: 'Email',
  emailCompose: 'Write the email',
  emailPreviewTitle: 'What the customer gets',
  emailSubject: 'Subject',
  emailPayOff_square: 'No Pay button yet: paying online (Square) is off until the switch. Once it is on, the button reads "View and pay the invoice".',
  emailPayOff_paid: 'No Pay button: nothing is owed on this invoice.',
  emailPayOff_pending: 'No Pay button: a payment on this invoice is still clearing.',
  emailPayOff_test: 'No Pay button: a TEST invoice is never paid online.',
  emailNotePlaceholder: 'A note of your own, if you want one (the email already says the invoice is available)',
  emailStatement: 'Email the statement',
  emailNotSetUp: 'Email is not set up on the server yet.',
  emailSettingsTitle: 'Email',
  lostSalesTitle: 'Lost sales',
  lostSalesHint: 'A sale that did not happen and why. Record it when you hear it: what we did not have, a price that lost it, a competitor. The report shows what to carry more of and where the price loses business.',
  lostSaleRecord: 'Record a lost sale',
  lostSaleWhy: 'Why',
  lostReason_out_of_stock: 'We did not have it',
  lostReason_price: 'Price',
  lostReason_quality: 'Quality',
  lostReason_late: 'Late',
  lostReason_competitor: 'Went to a competitor',
  lostReason_not_carried: 'We do not carry it',
  lostReason_other: 'Other',
  lostSaleItem: 'Item',
  lostSaleItemPlaceholder: 'Pick from the list, or type its name',
  lostSaleNotInList: 'Not in our list: kept by its name',
  lostSaleCustomer: 'Customer (if known)',
  lostSaleCustomerPlaceholder: 'Pick a customer, or leave empty',
  lostSaleUnknownCustomer: 'Not a customer in the list: pick one or leave it empty',
  lostSaleQty: 'How many',
  lostSaleDay: 'When',
  lostSalesUnvalued: '{n} of them have no quantity or no price, so they are not in the value.',
  lostSalesTruncated: 'More entries than the report sums at once: the totals cover the newest 5,000. Pick a shorter period.',
  lostSaleQuoted: 'Price we quoted',
  lostSaleTheirs: 'Price they wanted',
  lostSaleCompetitor: 'Competitor',
  lostSaleChannel: 'How',
  lostChannel_phone: 'Phone',
  lostChannel_visit: 'Visit',
  lostChannel_text: 'Text',
  lostChannel_whatsapp: 'WhatsApp',
  lostChannel_walkin: 'Walk-in',
  lostChannel_email: 'Email',
  lostChannel_other: 'Other',
  lostSaleNote: 'Note',
  lostSaleBadNumber: 'Quantities and prices are numbers (a quantity above 0).',
  lostSaleSave: 'Save lost sale',
  lostSaleSaved: 'Saved. Thank you: this is how we learn what we are missing.',
  lostSalesCount: 'Lost sales',
  lostSalesValue: 'What it would have brought',
  lostSalesNone: 'No lost sale recorded in this period.',
  lostSalesByItem: 'The items we lose most',
  lostSalesTimes: 'Times',
  lostSalesEvery: 'Every lost sale',
  paymentsTitle: 'Payments',
  paymentsHint: 'Customers pay an emailed invoice online with Square (card or bank account); the money is applied to the invoice when Square says it is in.',
  squareServer: 'Square',
  squareConfigured: 'Square is set up.',
  squareSandbox: "Square is set up with Square's test account (sandbox): no real money moves.",
  squareNotSetUp: 'Square is not set up on the server yet.',
  squareSetupHint: 'Claude walks you through it: an application in Square\'s developer site, then SQUARE_ACCESS_TOKEN, SQUARE_LOCATION_ID and SQUARE_WEBHOOK_SIGNATURE_KEY in GitHub secrets and Deploy → deploy.',
  squareWebhookUrl: 'Webhook address (give it to Square)',
  squareWebhookHint: 'In Square\'s developer site: Webhooks → Add subscription, this address, the event payment.updated.',
  squarePayButton: 'The Pay button',
  squareLiveLabel: 'On: emailed invoices have a Pay button',
  squareLiveHint: 'Leave it off until the switch: until then real invoices go out from QuickBooks with its own pay link, and a payment here would not reach QuickBooks.',
  squareTestTitle: 'Try it with $1',
  squareTestHint: 'Opens a $1 Square page: pay it with your own card, see it arrive below, then refund it in Square. Nothing is recorded in the books.',
  squareTestButton: 'Make a $1 test link',
  squareRecent: 'Last payments from Square',
  squarePaidAt: 'Paid',
  squareCustomer: 'Customer',
  squareFee: 'Square fee',
  squareNoPayments: 'No payment from Square yet.',
  payOnline: 'Pay {amount} online',
  payOpening: 'Opening the payment page…',
  payPending: 'A payment for this invoice is being processed (a bank payment takes a few days). Thank you.',
  payOnlineHint: 'Card or bank account, on Square’s secure page.',
  paidThanksTitle: 'Thank you',
  paidThanksBody: 'Your payment is in. Square emails you the receipt, and we apply it to your invoice as soon as it clears (a bank payment takes a few days).',
  emailSettingsHint: 'Invoices and statements by email, with a private link the customer opens without signing in.',
  emailOffNote: 'Email to customers is off until it is turned on in Administration → Email. Meanwhile "Send a test" goes to an address typed below, marked TEST: type your own.',
  emailTestHint: 'A test goes only to the addresses typed, and only to staff (a user\'s address or the company\'s domain), never to a customer.',
  emailStatusUnknown: 'No answer from the email service: it may have gone. Check before sending again',
  emailRevoke: 'Stop the link',
  emailRevoked: 'link stopped',
  moneyOnAccount: 'Payments and credits on account',
  emailTo: 'To',
  emailNoContacts: 'No contact of this customer has an email yet: add one on the customer, or type an address.',
  emailOther: 'Other addresses (comma between them)',
  emailBadAddress: 'Not an email address: {list}',
  emailMessage: 'A note (optional)',
  emailSendTest: 'Send a test',
  emailSend: 'Send to {n}',
  emailSent: 'Sent.',
  emailTestSent: 'Test sent.',
  emailTestBadge: 'test',
  emailStatusSent: 'sent',
  emailStatusFailed: 'not sent',
  emailOpened: 'opened {when} ({n}×)',
  emailNotOpened: 'not opened yet',
  emailServer: 'The server',
  emailConfigured: 'Email is set up: it goes out from {from}.',
  emailSetupHint: 'Claude walks you through it: a Postmark account, the records it gives you added to the domain, then POSTMARK_SERVER_TOKEN and EMAIL_FROM in GitHub secrets and Deploy → deploy.',
  emailToCustomers: 'Email to customers',
  emailLiveLabel: 'On: invoices and statements may be emailed to customers',
  emailLiveHint: 'Leave it off while QuickBooks is the book of record: invoices made in the app are practice until the switch, and a customer must not get one.',
  emailFromName: 'Sender name',
  emailReplyTo: 'Replies go to (optional)',
  asOf: 'As of {date}',
  unitPriceCol: 'Price',
  amountCol: 'Amount',
  newProduct: 'New product',
  packDescLabel: 'Pack (as the vendor sells it)',
  homeVendor: 'Bought from',
  noHomeVendor: '— no vendor yet —',
  homeVendorHint: 'Its tab on the order day, and the vendor whose cost prices it',
  packUnitLabel: 'Pack unit',
  newProductHint: 'The SKU is made from the name; prices come from the engine once a cost arrives (a vendor bill or receiving).',
  addProduct: 'Add product',
  newProductNameNeeded: 'Type the product name',
  workloadTitle: 'Delivery workload',
  workloadHint: 'The days ahead: delivery stops, orders, cases and pick lines booked and expected, and the drivers each day takes. Plan the trucks and the store from the same people.',
  wlNextDays: 'Next {n} days',
  wlBooked: 'Booked',
  wlBookedHint: 'Stops with an order in already (drafts included)',
  wlUsual: 'Usual',
  wlUsualHint: 'The average of this weekday over the last weeks',
  wlBusy: 'Busy day',
  wlBusyHint: 'A busy day for this weekday: 4 weeks in 5 stay at or under it',
  wlStops: 'Stops',
  wlStopsHint: 'Expected stops: the larger of booked and usual',
  wlOrders: 'Orders',
  wlCases: 'Cases',
  wlLines: 'Lines',
  wlLinesHint: 'Order lines to pick',
  wlDrivers: 'Drivers',
  wlDriversHint: 'Drivers for the expected stops, and for a busy day when it takes more',
  wlNotes: 'Notes',
  wlNotInYet: 'Not in yet',
  wlBusiest: 'Busiest day ahead',
  wlStopsWord: 'stops',
  wlDriversWord: 'driver(s) on a busy day',
  wlMostDrivers: 'Most drivers needed',
  wlAtStops: 'at {n} stops a driver',
  wlStopsAhead: 'Stops ahead',
  wlOverDays: 'over {n} delivery days',
  wlAccuracy: 'Forecast within 2 stops',
  wlAccuracyHint: 'Over the last 4 weeks: how often the usual day was within 2 stops of what happened',
  wlOffBy: 'off by {n} stops on average',
  wlNotEnough: 'not enough history yet',
  wlNothing: 'No deliveries booked or usual in these days.',
  wlStopsByDay: 'Stops by day',
  wlByWeekday: 'Usual stops by weekday',
  wlStopsPerDriver: 'Stops a driver covers',
  wlFootnote: 'Usual = the average of the same weekday over the last {weeks} weeks. A stop is a customer delivered to that day. Walk-ins and transfers are not deliveries; the retail store (Clover) is not counted. Not in yet = regulars who order that weekday at least half the weeks.',
  chooseCustomer: 'Choose a customer',
  backdated: 'Not today',
  setToToday: 'today',
  unsavedLeave: 'This invoice is not saved. Leave anyway?',
  rateHint: 'Grey is the customer price. Type a rate to change it for this invoice; it turns green. Delete puts the customer price back. A rate below zero (-16) is a credit: pick why under the lines.',
  creditLineTag: 'Credit · {reason}',
  unitLbSetBox: 'lb (set the box weight)',
  unitEaSetBox: 'each (set the box count)',
  boxTooBig: 'A box of more than 2,000 lb or pieces? Nothing was saved: pick lb again and type what one box holds.',
  boxHoldsLbAsk: 'How many lb in one box of {name}? (saved on the product: stock and pound sales count from it)',
  boxHoldsEaAsk: 'How many in one box of {name}? (saved on the product: stock and each sales count from it)',
  creditLinesTitle: 'Credits on this invoice',
  creditLinesHint: 'A line with a rate below zero is a credit: no case comes off the shelf and it is not picked. Say why for each.',
  creditWhy: 'Why?',
  creditReasonNeeded: 'Pick why for each credit before saving.',
  creditMoreThanInvoice: 'The credits are more than the invoice: credit the rest with the statement\'s Credit button.',
  greetingAfternoon: 'Good afternoon',
  greetingEvening: 'Good evening',
  ordersToday: "Today's orders",
  loading: 'Loading…',
  loadingPrices: 'Loading prices…',
  pricesFailed: "This customer's prices did not load, so no product can be picked yet.",
  retry: 'Try again',
  noResults: 'Nothing found',
  back: 'Back',
  more: 'More',
  less: 'Less',
  prevDay: 'Previous day',
  nextDay: 'Next day',
  pack: 'Pack',
  retailUnits: 'Retail units',
  category: 'Category',
  vendor: 'Vendor',
  shelfLife: 'Shelf life',
  shelf_delicate: 'Delicate',
  shelf_medium: 'Medium',
  shelf_hardy: 'Hardy',
  shelf_shelf_stable: 'Shelf stable',
  keepOnHand: 'Keep on hand',
  sellsSplit: 'Sells split',
  yes: 'Yes',
  no: 'No',
  inactive: 'Inactive',
  noPrice: 'no price',
  tier: 'Price tier',
  tier_mezquite: 'Mezquite',
  tier_standard: 'Standard',
  contacts: 'Contacts',
  preferred: 'Preferred',
  call: 'Call',
  recentInvoices: 'Recent invoices',
  invoice: 'Invoice',
  terms: 'Terms',
  days: 'days',
  deliveryDays: 'Delivery days',
  lang_es: 'Spanish',
  lang_en: 'English',
  notes: 'Notes',
  order: 'Order',
  lines: 'lines',
  noOrders: 'No orders for this day',
  channel: 'Channel',
  channel_phone: 'Call',
  channel_sms: 'Text',
  channel_whatsapp: 'WhatsApp',
  channel_walkin: 'Walk-in',
  channel_web: 'Web',
  channel_standing: 'Standing',
  channel_import: 'Import',
  channel_transfer: 'Transfer',
  status_draft: 'Draft',
  status_confirmed: 'Confirmed',
  status_picked: 'Picked',
  status_out_for_delivery: 'On the road',
  status_delivered: 'Delivered',
  status_invoiced: 'Invoiced',
  status_cancelled: 'Cancelled',
  change: 'Change',
  remove: 'Remove',
  pickCustomer: 'Choose a customer',
  addItems: 'Add at least one item',
  sending: 'Sending…',
  codeSent: 'We texted a code to',
  testCode: 'Test code',
  phoneInvalid: 'Enter a 10-digit phone number',
  codeInvalid: 'Wrong or expired code',
  otherPhone: 'Use another number',
  cooler: 'Cooler',
  countTitle: 'Inventory count',
  countOlder: '{n} counted on {day} and not saved. Put them back only if nothing came in or went out since:',
  countOlderKeep: 'Put them back',
  countOlderDrop: 'Discard',
  countKept: '{n} counted, not saved yet: kept on this device until you save.',
  countHint: 'Type how many are in the cooler, in each product\'s unit (cases unless it says LB or EA), with decimals for an open one: 2.5 is two and a half. Leave an item blank to skip it.',
  coolerCount: 'Count',
  salesLast4: 'Revenue, last 4 weeks (line: the 4 weeks before)',
  mixLast4: 'Sales mix, last 4 weeks',
  topCustomers4: 'Top customers, last 4 weeks',
  fourWeeksBefore: '4 weeks before',
  thisWeekSoFar: 'This week so far',
  vsFourWeekAvg: 'Average week, last 4 weeks',
  thisMonthSoFar: 'This month so far',
  last4Weeks: 'Last 4 weeks',
  vsSameDaysLastWeek: 'Same days last week',
  vsSameDaysLastMonth: 'Same days last month',
  vsFourWeeksBefore: 'The 4 weeks before',
  customerTrends: 'Customer trends',
  customerTrendsHint: 'The last 4 weeks against the 4 before: accounts that moved a quarter or more.',
  orderingLess: 'Ordering less',
  pickingUp: 'Picking up',
  noMoves: 'No big moves.',
  stoppedOrdering: 'Stopped',
  newCustomerTrend: 'New',
  wx_sun: 'Sunny',
  wx_partly: 'Partly cloudy',
  wx_cloud: 'Cloudy',
  wx_rain: 'Rain',
  wx_storm: 'Thunderstorms',
  wx_snow: 'Snow',
  wx_wind: 'Windy',
  suggestMatches: 'Suggest matches',
  suggestMatchesHint: 'The app proposes a product for each name: the same words first (units and counts set aside), then Claude for the rest. Sure matches are ticked; check the others, then link. Linked names link by themselves next time.',
  suggestAiFailed: 'Claude could not suggest for part of the list; the matches by words are below. Try again in a minute.',
  matchingProgress: 'Matching {done} of {total}…',
  linkingProgress: 'Linking {done} of {total}…',
  linkedN: '{n} linked',
  linkChecked: 'Link checked ({n})',
  checkAll: 'Tick all',
  checkNone: 'Untick all',
  nameOnDocs: 'Name',
  matchHow: 'Match',
  matchByWords: 'Same words',
  matchClaudeSure: 'Claude: sure',
  matchClaudeLikely: 'Claude: likely, check it',
  link: 'Link',
  stop: 'Stop',
  priceListFoot: 'Delivery available every day',
  countUnitCol: 'Unit',
  countUnitHint: 'What one counts as: CS a case, LB a pound, EA one piece',
  countAgain: 'Count these again',
  outOfStock: 'Out',
  onHandFlagHint: 'What the cooler has: red when we are out, gold when the invoice sells more than we have',
  coolerCountHint: 'How many in the cooler, in the product\'s unit; decimals for an open one: 2.5 is two and a half',
  onHand: 'On hand',
  lastCount: 'Last count',
  neverCounted: 'never counted',
  casesOnHand: 'Cases on hand',
  toSave: 'to save',
  countSaved: 'Count saved',
  adjusted: 'adjusted',
  nothingToSave: 'Type at least one count',
  sealed: 'Sealed',
  openUnit: 'Open unit',
  leftInOpen: 'Left in the open one',
  addOpen: '+ open',
  noOpen: 'No open unit',
  flaggedDrift: 'Check again',
  identity: 'Identity',
  buying: 'Buying',
  selling: 'Selling',
  stockBlock: 'Stock',
  sku: 'SKU',
  family: 'Family',
  variety: 'Variety',
  nameEs: 'Spanish name',
  status: 'Status',
  status_active: 'Active',
  status_seasonal: 'Seasonal',
  status_dormant: 'Dormant',
  stockUnit: 'Stock unit',
  content: 'Content',
  sellUnits: 'Sells as',
  whole: 'whole',
  half: 'half',
  loose: 'loose',
  sensitivity: 'Price sensitivity',
  sens_H: 'High (floor 10%)',
  sens_M: 'Medium (floor 14%)',
  sens_L: 'Low (floor 20%)',
  vendorCodes: 'Vendor codes',
  rules: 'Customer rules',
  noRules: 'No customer rules',
  aliases: 'Other names',
  addAlias: 'Add a name',
  edit: 'Edit',
  sealedOpen: 'Sealed · open',
  lastDrift: 'Drift at last count',
  moved28: 'Moved, last 4 weeks',
  inbox: 'Unknown names',
  inboxHint: 'Names on invoices, the register and vendor lists that are not an item yet. Tap one, pick the item.',
  inboxEmpty: 'Nothing waiting. Every name is mapped.',
  assignTo: 'Which item is',
  assigned: 'Assigned',
  name: 'Name',
  nameHint: 'What the customer sees on invoices and the price list. No weights or pack sizes here.',
  internalNote: 'Internal description',
  internalNoteHint: 'Only we see this. Never on an invoice.',
  fixed: 'fixed',
  skuFormat: 'SKU looks like FAM-VAR-PACK, e.g. ONI-YEL-50LB',
  noVendorCodes: 'No vendor codes yet',
  noAliases: 'No other names yet',
  noCount: 'Never counted',
  countedAt: 'Counted',
  openInbox: 'Unknown names inbox',
  trendsTitle: 'How customers are ordering',
  forecastTitle: 'Forecast',
  usdaBlock: 'Market prices (USDA)',
  usdaHint: 'USDA Market News, every night: the Los Angeles terminal market (what wholesalers pay there) and the Phoenix shipping point (Mexico through Nogales), for this product\'s commodity, beside our cost.',
  usdaNotSetUp: 'USDA Market News is not set up on the server yet (the free USDA key).',
  usdaNotLinked: 'Not linked to a USDA commodity yet.',
  usdaSuggested: 'USDA reports:',
  usdaLink: 'Link to USDA',
  usdaChange: 'Change',
  usdaUnlink: 'Unlink from USDA',
  usdaSearch: 'Search USDA commodities',
  usdaCommodity: 'USDA commodity',
  usdaVariety: 'Variety',
  usdaPackage: 'Package',
  usdaAny: 'any',
  usdaNoPrices: 'No USDA prices for it in the last 30 days.',
  usdaOurCost: 'Our cost',
  usdaOurCostHint: 'per case, now',
  usdaWeeks: 'Week by week',
  usdaWeekOf: 'Week of',
  usdaShowRows: 'Every USDA row',
  usdaHideRows: 'Hide the rows',
  usdaMarket: 'Market',
  usdaSize: 'Size',
  usdaOrigin: 'Origin',
  usdaRange: 'Low – high',
  usdaMostly: 'Mostly',
  usdaDate: 'Day',
  usdaCol: 'USDA',
  forecastHint: 'Who is likely to order in the days ahead, from the last 12 weeks of invoices: the chance they order that day and what their order usually comes to. A ✓ is already in. Pick a customer to see what they usually take.',
  forecastSpan: 'How far ahead',
  forecastNext7: 'Next 7 days',
  forecastNext14: 'Next 14 days',
  forecastView: 'Show',
  forecastByCustomer: 'By customer',
  forecastByProduct: 'By product',
  forecastByDay: 'By day',
  forecastPickDay: 'Pick a day',
  forecastLikelyN: '{n} likely',
  forecastDayHint: '{n} to call, {in} already in. Most likely first; tap the number to call, then Mark called (it goes on their history).',
  forecastChance: 'Chance · usual',
  forecastUsualDays: 'Usual days',
  forecastLastOrder: 'Last order',
  forecastCall: 'Call',
  forecastCalled: 'Mark called',
  forecastCalledToday: 'called today',
  forecastNoPhone: 'no number',
  forecastIn: 'in',
  forecastDaysAgo: '{n} days ago',
  forecastCalledBody: 'Called about an order for {day} (forecast).',
  forecastExpected: 'Expected',
  forecastBooked: 'Already in',
  forecastCellTitle: '{pct}% chance; their order usually comes to {usual}',
  forecastUsualOrder: 'Usual order:',
  forecastNothing: 'Nobody is expected in these days.',
  forecastHowWell: 'How well the forecast did',
  forecastHowWellHint: 'The same forecast run on each of the last {days} days as if it were that morning, against what was invoiced.',
  forecastRight: 'Right when expected',
  forecastRightOf: '{hits} of {expected} expected orders came in',
  forecastCaught: 'Orders it saw coming',
  forecastCaughtOf: '{hits} of {ordered} orders',
  forecastProductsOff: 'Products off by',
  forecastLowerBetter: 'cases per product per day, lower is better',
  forecastDollars: 'Dollars expected',
  forecastDollarsActual: 'invoiced: {actual}',
  pasteTitle: 'Paste a call or text',
  pasteHint: 'Copy the transcript of a call (the Plaud, your phone\'s recorder) or a customer\'s text, paste it here, and Claude opens the invoice filled in for you to check and create. The words are kept on the customer\'s history.',
  pasteKind: 'What it is',
  pasteCall: 'A call',
  pasteText: 'A text',
  pasteFromClipboard: 'Paste from the clipboard',
  pasteClipboardNo: 'The phone did not hand over the clipboard: press and hold in the box and choose Paste.',
  pasteBoxCall: 'The call',
  pasteBoxText: 'The text',
  pasteRead: 'Read it',
  pasteSaved: 'Kept on the history of',
  pasteNotSaved: 'Not kept: Claude could not tell which customer it is from.',
  trendsHint: 'From the invoices: the last 4 weeks against the 4-week average of the 8 weeks before. Gone quiet: no invoice for twice their usual gap. Pick a customer to add a note, a birthday or what they like.',
  trend_more: 'Ordering more',
  trend_steady: 'Steady',
  trend_less: 'Ordering less',
  trend_quiet: 'Gone quiet',
  trend_new: 'New',
  colTrend: 'Trend',
  colPrior4: '4-week avg. before',
  colUsualDays: 'Usual days',
  trendEvery: 'every {n} days',
  trendEveryDay: 'every day',
  openAccount: 'Open the account',
  noTrends: 'No invoices in the last year.',
  tasksDue: 'Tasks due',
  birthdaysNear: 'Birthdays in the next two weeks',
  quietCustomers: 'Gone quiet',
  quietHint: 'No order for longer than twice their usual gap.',
  prospectsStale: 'Prospects to touch',
  prospectsHint: 'No visit, call or note in two weeks.',
  inDays: 'in {n} days',
  today: 'today',
  tomorrow: 'tomorrow',
  daysAgo: '{n} days ago',
  lastOrder: 'Last order',
  usualGap: 'usually every {n} days',
  neverTouched: 'Never contacted',
  stage: 'Stage',
  stage_prospect: 'Prospect',
  stage_trial: 'Trial',
  stage_customer: 'Customer',
  stage_lost: 'Lost',
  allStages: 'All',
  source: 'Lead source',
  activities: 'History',
  logActivity: 'Log',
  kind: 'What',
  kind_visit: 'Visit',
  kind_call: 'Call',
  kind_text: 'Text',
  kind_note: 'Note',
  kind_offer: 'Offer',
  kind_task: 'Task',
  whatHappened: 'What happened',
  dueOn: 'Remind on',
  markDone: 'Done',
  undo: 'Undo',
  doneLabel: 'done',
  addContact: 'Add a contact',
  editContact: 'Edit',
  contactName: 'Name',
  contactRole: 'Role',
  phone: 'Phone',
  whatsappLabel: 'Uses WhatsApp',
  smsConsentLabel: 'Agreed to texts',
  consentNote: 'Consent method',
  birthday: 'Birthday',
  likes: 'Preferences',
  cancel: 'Cancel',
  exceptionsTitle: 'Exception report',
  exceptionsHint: 'What went against the rules and what it cost. Floors come from the cost at sale; tier prices are today\'s.',
  range_d7: 'Last 7 days',
  pricingExceptions: 'Sold under the floor or the tier',
  leftOnTable: 'Left on the table',
  belowCostLines: 'Lines below cost',
  underFloorBadge: 'Under floor',
  underTierBadge: 'Under tier',
  belowCostBadge: 'Below cost',
  soldAt: 'Sold at',
  floor: 'Floor',
  creditsByReason: 'Credits by reason',
  shrinkByCause: 'Shrink by cause',
  voidedInvoices: 'Voided invoices',
  cancelledOrders: 'Cancelled orders',
  unpricedLinesLabel: 'Unpriced lines',
  noCostOnShrink: 'no cost on file',
  shrinkCostPartial: 'At least this much: some of these packs have no cost on file',
  oauthTitle: 'Connect an app',
  oauthAsk: '{app} wants to use Morales Produce as you',
  oauthHint: 'It will see and do what you can, under your name. Every read and draft is recorded as yours. The owner can disconnect it any time from the Claude connector screen.',
  oauthBad: 'This link is not a valid connection request.',
  allow: 'Allow',
  deny: 'Deny',
  connectedApp: 'Claude app',
  expires: 'expires',
  readFromPhoto: 'Read from a photo or PDF',
  reading: 'Reading…',
  readingLong: 'A long price list takes a few minutes to read. Keep this page open.',
  readDone: 'Read. Check every line, then save; the file is attached with it.',
  readNotes: 'The reader says',
  readDuplicate: 'This same file was already read as document',
  vendorUnmatched: 'The letterhead says {name}; pick the vendor.',
  portalCutoffHint: 'Order by {time} for the next day. After that, the first day offered is the day after.',
  portalCancel: 'Cancel this order',
  portalCancelAsk: 'Cancel this order? Nothing will be delivered for it.',
  portalCallToChange: 'To change this order now, call us.',
  portalInvoices: 'Invoices',
  portalSettingsTitle: 'Customer portal',
  portalSettingsHint: 'Customers order (and cancel an order) on the portal until this time, Denver time, the day before delivery. After it, the first day offered is the day after. For until midnight, set 11:59 PM.',
  portalCutoffLabel: 'Order cut-off (the day before delivery)',
  stalePricesTitle: 'Prices left behind',
  stalePricesHint: 'Published prices whose cost moved since they were set, or that sit under the floor at today\'s cost. Under the floor first, then what the tier bought in 30 days. Half-case prices are not checked.',
  stalePricesThreshold: 'Cost moved by at least',
  noStalePrices: 'Every published price still matches its cost.',
  openPriceManagement: 'Open price management',
  priceSinceCol: 'Price since',
  costThenCol: 'Cost then',
  costNowCol: 'Cost now',
  costChangeCol: 'Change',
  gmNowCol: 'GM % now',
  floorCol: 'Floor',
  sold30Col: 'Tier sold 30 d',
  suggestionsTitle: 'What else they could buy',
  suggestionsHint: 'Products at least a quarter of the customers of the same price tier bought in the last 90 days, and this one did not.',
  suggestionPeers: '{n} of {of} similar customers buy it',
  lastBoughtOn: 'last bought',
  neverBought: 'never bought',
  fillRateCol: 'Fill',
  fillRateHint: 'Cases delivered of the cases ordered on their invoices (pack lines).',
  shrinkCol: 'Shrink',
  netAfterDeliveryCol: 'After delivery',
  netAfterShrinkCol: 'After shrink',
  costPerStop: 'Cost per stop',
  complianceTitle: 'Compliance and fleet',
  complianceHint: 'Temperatures for the inspector, what runs out and when, and the trucks with their fuel and service.',
  renewalsTitle: 'Licenses, permits and renewals',
  noRenewals: 'Nothing added yet: the PACA license, the business license, permits, insurance, registrations.',
  renewalKindCol: 'Kind',
  renewalHolderCol: 'For',
  renewalHolderHint: 'The company, a truck, a person',
  expiresCol: 'Runs out',
  renewalDaysLeft: '{n} days',
  renewalExpired: 'Expired',
  renewBtn: 'Renew or change',
  archive: 'Archive',
  renewalArchiveAsk: 'Archive it? It is no longer needed (it stays in the record).',
  addRenewal: 'Add a renewal',
  remindDaysLabel: 'Remind days before',
  renewal_license: 'License',
  renewal_permit: 'Permit',
  renewal_insurance: 'Insurance',
  renewal_registration: 'Registration',
  renewal_inspection: 'Inspection',
  renewal_certification: 'Certification',
  renewal_other: 'Other',
  temperaturesTitle: 'Temperature log',
  temperatureLimits: 'Limits: cooler {cmin}–{cmax} °F, dock {dmin}–{dock} °F, truck {tmin}–{truck} °F.',
  renewalToday: 'Today',
  renewalOneDay: '1 day',
  takenBack: 'Taken back',
  takeBack: 'Take back',
  takeBackWhy: 'Why is this entry wrong? (it stays in the record, marked)',
  enteredLate: 'Entered late',
  changeLimits: 'Change the limits',
  limitsHint: 'Readings already taken keep the limits they were taken under. Chill-sensitive produce (tomatoes, bananas, cucumbers, peppers) keeps best at 45–55 °F: log its room in the note until rooms have their own limits.',
  tempPlace: 'Where',
  temp_cooler: 'Cooler',
  temp_dock: 'Dock (truck received)',
  temp_truck: 'Truck',
  tempF: 'Temperature °F',
  loadCondition: 'Load condition',
  condition_good: 'Good',
  condition_fair: 'Fair',
  condition_poor: 'Poor',
  vehicle: 'Vehicle',
  logReading: 'Log the reading',
  tempOutOfRange: 'Out of range',
  noReadings: 'No readings in the last 7 days.',
  fleetTitle: 'Trucks',
  noVehicles: 'No trucks added yet.',
  renewalsDueBadge: '{n} renewal due',
  odometer: 'Odometer',
  lastService: 'last service',
  fuel30: 'fuel, 30 days',
  vehicleName: 'Truck name',
  plate: 'Plate',
  addVehicle: 'Add a truck',
  logKind: 'What to log',
  vlog_fuel: 'Fuel',
  vlog_service: 'Service',
  vlog_odometer: 'Odometer',
  gallons: 'Gallons',
  serviceWhat: 'What was done',
  weatherTitle: 'Weather at the hub (high / low · chance of rain)',
  qboKind_PaymentMethod: 'Payment methods',
  qboMirrorTitle: 'Money from QuickBooks',
  qboMirrorOffHint: 'Off: the app shows its own practice invoices. Turn it on below and QuickBooks\' invoices and payments become the app\'s, kept in step after every nightly import, so balances, aging and statements match QuickBooks. Here is what it would bring in today.',
  qboMirrorTurnedOnAt: 'Turned on {when}. Money is entered only in QuickBooks; the app copies it every night.',
  qboMirrorRun: 'Bring QuickBooks money in now',
  qboWriteTitle: 'Invoices made in the app go to QuickBooks',
  qboWriteHint: 'The app is where invoices are made. Each one is copied to QuickBooks (with its customer and products) within a minute, as the CPA\'s copy. Payments and credits are still entered in QuickBooks, and QuickBooks\' payments come back onto the app\'s invoices.',
  qboWriteOn: 'On: invoices made in the app are real and are copied to QuickBooks.',
  qboWriteFrom: 'On from {date}: until then, invoices made in the app are practice.',
  qboWritePaused: 'Paused: invoices made in the app are still real, and wait here until you resume.',
  qboWriteTurnedOn: 'turned on {when} by {who}',
  qboWriteOtherServer: 'This copy of the app was not the one turned on: it never writes to QuickBooks.',
  qboWriteReconnect: 'QuickBooks must be connected again: nothing is sent until then (nothing is lost).',
  qboWriteCountWaiting: 'Waiting',
  qboWriteCountSent: 'Copied',
  qboWriteCountFailed: 'Need a look',
  qboWriteLastSent: 'Last copied {when}',
  qboWriteRetry: 'Try again',
  qboWriteSendNow: 'Send now',
  qboWriteSentNow: 'Copied {sent}, refused {failed}, waiting {waiting}.',
  qboWritePause: 'Pause',
  qboWriteResume: 'Resume',
  pnlTitle: 'Profit and loss (QuickBooks)',
  pnlHint: 'QuickBooks\' own Profit and Loss, read from QuickBooks when you open it: what the CPA sees. Bills, expenses and payroll are entered in QuickBooks; sales are the invoices QuickBooks holds (the app\'s go there). Click a section for its accounts.',
  pnlThisMonth: 'This month',
  pnlLastMonth: 'Last month',
  pnlYearToDate: 'Year to date',
  pnlLastYear: 'Last year',
  pnlColumns: 'Columns',
  pnlOneTotal: 'One total',
  pnlByMonth: 'By month',
  pnlAccount: 'Account',
  pnlIncome: 'Income',
  pnlCogs: 'Cost of goods sold',
  pnlGrossProfit: 'Gross profit',
  pnlExpenses: 'Expenses',
  pnlNetOperating: 'Net operating income',
  pnlOtherIncome: 'Other income',
  pnlOtherExpenses: 'Other expenses',
  pnlNetOther: 'Net other income',
  pnlNetIncome: 'Net income',
  pnlGrossPct: 'Gross margin',
  pnlEmpty: 'QuickBooks has nothing for this period.',
  pnlReadAt: '{period} · read from QuickBooks {when} · {basis}.',
  pnlReading: 'Reading the new period from QuickBooks…',
  pnlTotal: 'Total',
  pnlAccrual: 'accrual basis',
  pnlCash: 'cash basis',
  switchReadyTitle: 'Ready for the day',
  switchAgingAsk: 'Every customer against QuickBooks\' A/R aging report: read it now, just before switching.',
  switchAgingButton: 'Check against QuickBooks now',
  switchAgingReading: 'Reading QuickBooks…',
  switchAgingResult: '{n} of {of} linked customers owe a different amount than QuickBooks\' A/R aging report says (its total {total}). Fix any in QuickBooks before switching.',
  switchAgingOpen: 'See them on QuickBooks matching → Balances',
  switchCounted: 'Stock last counted {when} ({n} products in the two days before). Count the night before the switch.',
  switchNotCounted: 'No stock count yet: count the night before the switch.',
  switchSquareNotSet: 'Square is not set up on the server yet.',
  switchSquareNotTested: 'Square is set up, but its $1 test payment has not come through.',
  switchSquareReady: 'Square is set up and tested: turn the Pay button on after the switch.',
  switchEmailNotSet: 'Email is not set up on the server yet.',
  switchEmailReady: 'Email is set up: turn email to customers on after the switch.',
  switchTurnOnSquare: 'Turn the Pay button on for customers now:',
  switchTurnOnEmail: 'Turn email to customers on now:',
  switchTitle: 'Switch the books to the app',
  switchHint: 'The switch day (December 1): from then on the app holds the invoices, payments and credits, and writes them all to QuickBooks for the CPA. Bills, vendor payments and the bank feed stay in QuickBooks. Do it once nobody is entering anything in QuickBooks.',
  switchPractice: 'Deletes the practice: {n} invoices ({total}), {p} payments and {c} credits. Real invoices stay.',
  switchLedger: 'The receivables ledger starts with QuickBooks\' first record: {from} → {to}.',
  switchNumbers: 'The next invoice number: {n} (after every QuickBooks number).',
  switchAfter: 'QuickBooks is read one last time first. After the switch, money is typed in the app and goes to QuickBooks within a minute; QuickBooks\' own invoices take payments and credits here, and are corrected with a credit. There is no undo except the backup.',
  switchBlocked: 'Not yet:',
  switchReady: 'Everything checks out: it can be done.',
  switchBackup: 'I ran Deploy → backup-now just before this',
  switchType: 'Type SWITCH to confirm',
  switchButton: 'Switch the books to the app',
  switchRunning: 'Switching… QuickBooks is being read one last time. This card says when it is done.',
  switchStopped: 'It did not switch, nothing was changed: {why}',
  switchDone: 'The books switched to the app on {when}. Money is typed here and goes to QuickBooks.',
  switchEnteredThere: 'Entered in QuickBooks itself since the switch (the app never brings these in): {i} invoices, {p} payments, {c} credits (numbers {docs}). Delete them in QuickBooks and enter them in the app.',
  switchOrdersStay: '{n} orders a practice invoice billed stay billed: QuickBooks billed them.',
  practiceCardTitle: 'Practice',
  practiceStatement: 'Practice invoices and payments',
  paidAndCredited: 'Paid and credited',
  statementView: 'Which money',
  statementReal: 'Real (QuickBooks)',
  statementPractice: 'Practice',
  statementRealHint: 'Until the switch, real payments and credits are entered in QuickBooks (the app copies them every hour). To practise payments and credits here, open Practice.',
  practiceCardHint: 'What was typed in the app while QuickBooks holds the books: counted nowhere, never sent to QuickBooks. Delete it when a practice run is over; real records are never touched. The counts are rows: a check on two invoices is two payments, and a reversal or an undo is one more; voided invoices are counted but not in the total.',
  practiceInvoices: 'Practice invoices',
  practicePayments: 'Practice payments',
  practiceCredits: 'Practice credits',
  practiceOrdersBack: '{n} real orders billed by a practice invoice go back to be invoiced (changes made to them through a practice invoice, lines and quantities, stay on the order).',
  practiceType: 'Type DELETE PRACTICE',
  practiceDelete: 'Delete practice',
  practiceDeleted: 'Deleted {n} practice invoices, {p} payments and {c} credits.',
  qboWriteDoneThere: 'Done in QuickBooks',
  qboWriteDoneThereConfirm: 'You did this by hand in QuickBooks, so the app will not send it. Mark it done?',
  qboWriteKindPayment: 'Payment',
  qboWriteKindCredit: 'Credit',
  qboWriteTurnOff: 'Turn off',
  qboWriteTurnOffConfirm: 'Invoices made in the app go back to practice. What already went stays in QuickBooks; what waits is sent if you turn it on again. Turn off?',
  qboWriteStepNumbers: 'In QuickBooks: ⚙ Settings → Account and settings → Sales → Sales form content → Custom transaction numbers → On (so QuickBooks keeps the app\'s invoice numbers).',
  qboWriteStepCredits: 'In QuickBooks: Account and settings → Advanced → Automation → Automatically apply credits → Off.',
  qboWriteStepSales: 'QuickBooks has one income account named Sales: a product the app makes there posts to it.',
  qboWriteStepMoney: 'Payments and credits stay in QuickBooks until the switch; they come back onto the app\'s invoices every hour.',
  qboWriteFromLabel: 'Real from',
  qboWriteTurnOn: 'Turn on',
  qboWriteConfirm: 'From {date}, invoices made in the app are real and are copied to QuickBooks. From then on make invoices only in the app, never in QuickBooks (their numbers would clash). Turn on?',
  qboWriteOff: 'Off: the owner turns it on here.',
  qboWriteOffBand: 'OFF: invoices made in the app are practice. Nothing is sent to QuickBooks.',
  qboWriteSent: 'Copied to QuickBooks as #{n}',
  qboWriteWaiting: 'Waiting to be copied to QuickBooks…',
  qboWriteFailed: 'QuickBooks refused this invoice (see Administration → QuickBooks):',
  qboRealTitle: 'Real invoice.',
  qboRealHint: 'Invoices made here count and are copied to QuickBooks. Payments and credits are still entered in QuickBooks.',
  qboMirrorRunning: 'Bringing QuickBooks money in… this card shows the run when it is done.',
  qboTurnOnTitle: 'Turn it on',
  qboTurnOnHint: 'From then on invoices, payments and credits are entered only in QuickBooks, and the app refuses them. Turning on undoes the practice money typed in the app (nothing is deleted; each undo is kept):',
  qboTurnOnInvoices: '{n} invoices made in the app voided ({total})',
  qboTurnOnSheets: '{n} invoices from the old Sheets import voided too; QuickBooks brings them back as its own',
  qboTurnOnSheetsStay: '{n} of them stay void: QuickBooks does not hold them for a matched customer, or a short was typed on them. Match the customers first to bring more back.',
  qboMirrorRunError: 'The last run did not finish: {why}. Press Bring QuickBooks money in now, or it tries again tonight.',
  qboTurnOnPayments: '{n} payments typed in the app reversed ({total})',
  qboTurnOnCredits: '{n} credits reversed ({total})',
  qboTurnOnOpening: 'Opening balances of {n} customers set to zero',
  qboTurnOnLedger: 'The ledger start moves from {from} to {to} (QuickBooks\' first record)',
  qboTurnOnAfter: 'Then QuickBooks\' invoices and payments come in. QuickBooks invoices made from now on take their cases off the stock. Cash taken at the door is kept for the count; the office enters the payment in QuickBooks.',
  qboTurnOnBackup: 'I ran Deploy → What to do: backup-now today, and it finished green',
  qboTurnOnType: 'Type TURN ON',
  qboTurnOnButton: 'Turn on QuickBooks money',
  qboTurnOnStarted: 'Turned on. QuickBooks\' invoices and payments are coming in; this card shows the run when it is done.',
  stopCashUndo: 'Undo the cash',
  stopCashUndoConfirm: 'Undo the money kept at this stop? The driver records the right amount on the next delivery.',
  qboMirrorOnHint: 'On: QuickBooks invoices and payments are the app\'s, kept in step after every import. Change them in QuickBooks.',
  qboMirrorInvoicesNew: 'Invoices to bring in or update',
  qboMirrorAdopted: 'Of those, from the Sheets import (taken over)',
  qboMirrorInvoicesHeld: 'Invoices already in step',
  qboMirrorPaymentRows: 'Payment lines to write',
  qboMirrorSkipped: 'Waiting: {n} customers not matched',
  qboMirrorSkippedHint: 'Invoices of QuickBooks customers not matched to the app\'s wait until they are matched.',
  qboMirrorProblems: '{n} things to look at',
  qboMirrorLast: 'Last run {when}',
  qboMirrorAllMatch: 'Every invoice owes in the app what it owes in QuickBooks.',
  qboMirrorDiffer: '{n} invoices owe a different amount than in QuickBooks',
  qboMirrorApp: 'app',
  portalGreeting: 'Hello',
  portalNewOrder: 'Place an order',
  portalSameAgain: 'Same as last time',
  portalMyOrders: 'My orders',
  portalStatement: 'My account',
  portalLastOrder: 'Last order',
  portalNextDelivery: 'Next delivery',
  portalDeliverOn: 'Deliver on',
  portalAsk: 'ask',
  portalLastTime: 'last time',
  portalNotes: 'Notes for the driver or the office',
  portalPlace: 'Send the order',
  portalPlaced: 'Order received. We will confirm it shortly.',
  portalEmpty: 'Add a quantity next to an item',
  portalOrderNo: 'Order',
  portalOpenInvoices: 'Open invoices',
  portalAccess: 'Can order online',
  portalAccessHint: 'Signs in with this phone and sees only their own prices, orders and account.',
  portalOrders: 'Orders',
  signedInAs: 'Signed in as',
  textsTitle: 'Messages',
  textsHint: 'Texts to the business number land here. Reply, make a draft order from one, or mark it dealt with.',
  textsOff: 'Texting is not set up yet (the business number). See the runbook.',
  textsInbox: 'Inbox',
  textsEmpty: 'No texts waiting',
  makeDraft: 'Make a draft order',
  markHandled: 'Dealt with',
  reply: 'Reply',
  sendText: 'Send text',
  textConfirmation: 'Text the confirmation',
  textSent: 'Text sent',
  textsThread: 'Texts',
  blastTitle: 'Text a group',
  blastHint: 'Each person gets their own text in their language. Placeholders: {name} {customer} {item} {price} {lastQty}. Only contacts who agreed to texts are included.',
  blastItem: 'About which item',
  blastEveryone: 'Everyone who agreed to texts',
  blastDays: 'Bought it in the last',
  blastAudience: 'Audience',
  blastEn: 'Text in English',
  blastEs: 'Text in Spanish',
  blastPreview: 'Preview',
  blastSend: 'Send to {n} people',
  blastSent: 'Sent to {n}. Skipped: {k}.',
  unknownNumber: 'Unknown number',
  textsWaiting: 'texts waiting',
  routesTitle: 'Dispatch',
  routesHint: "The day's deliveries in stop order. The office builds it from the confirmed orders and sets the driver; the driver taps through the stops.",
  buildRoute: 'Build from confirmed orders',
  addRoute: 'Add a route',
  deliveriesOnPhone: '{n} deliveries saved on the phone: they send when there is signal.',
  sendNow: 'Send now',
  deliveryNotSent: 'not sent:',
  deliveryNoRoom: 'The phone has no room left to keep this delivery. Find signal and tap Delivered again, or write it down.',
  savedOnPhone: 'Saved on the phone',
  newRouteName: 'Name of the new route',
  moveToRoute: 'Move to',
  loadSheet: 'Load sheet',
  printInvoices: 'Print the invoices',
  loadOrderHint: 'Load in this order: the last stop goes on the truck first.',
  itemTotals: 'Totals by item',
  notInvoicedYet: 'Not invoiced yet',
  usualRoute: 'Usual route',
  usualRouteHint: 'Build puts this customer on this route (Route 1 when empty).',
  codBadge: 'COD',
  owesNow: 'Owes',
  pastDueShort: 'past due',
  whyShort: 'Why short',
  short_damaged: 'Damaged',
  short_refused: 'Refused',
  short_not_loaded: 'Not loaded',
  short_out_of_stock: 'Out of stock',
  short_wrong_item: 'Wrong item',
  short_other: 'Other',
  noRoute: 'No route for this day yet',
  driver: 'Driver',
  truck: 'Truck',
  startRoute: 'Leave with the truck',
  routeOut: 'On the road',
  routeDone: 'Route done',
  notDelivered: 'Not delivered',
  notDeliveredWhy: 'Why not delivered',
  nd_closed: 'Closed',
  nd_refused: 'Refused',
  nd_nobody: 'Nobody there',
  nd_not_reached: 'Not reached',
  nd_cancelled: 'Cancelled: bring it back',
  newCustomer: 'New customer',
  customerShortName: 'Short name',
  customerTypeLabel: 'Type',
  callAheadLabel: 'Call before delivering',
  editDetails: 'Edit details',
  restoreBtn: 'Bring back',
  archivedFilter: 'Archived',
  archiveCustomerConfirm: 'Put {name} away? They leave the lists and pickers; their invoices and history stay. You can bring them back under Archived.',
  customerArchived: 'Put away: not in the lists or pickers. Their history stays.',
  vendorsTitle: 'Vendors',
  vendorsHint: 'Who we buy from: how to reach them, their terms and the days they deliver.',
  newVendor: 'New vendor',
  vendorKind: 'Kind',
  vendorKind_wholesaler: 'Wholesaler',
  vendorKind_grower: 'Grower',
  vendorKind_retail: 'Retail',
  vendorKind_other: 'Other',
  vendorTermsDays: 'Terms (days)',
  archiveVendorConfirm: 'Put {name} away? It leaves the pickers; its prices, papers and history stay.',
  receivePaymentTitle: 'Receive payment',
  receivePaymentHint: 'Pick the customer who paid; the payment goes on their oldest invoices first, as on their statement.',
  pickCustomerWhoPaid: 'Customer who paid',
  openStatement: 'Open their statement',
  undoCredit: 'Undo',
  undoCreditWhy: 'Why undo this credit?',
  creditUndone: 'Undone',
  creditUndoes: 'Undoes a credit',
  notForRole: 'This is not for your role. Ask the owner or the office if you need it.',
  confirmSure: 'Are you sure?',
  cancelTransferConfirm: 'Cancel this transfer? Nothing is sent.',
  tempTab: 'Temperatures',
  updateReady: 'A new version of the app is ready.',
  updateReload: 'Reload',
  deliveryUnsaved: 'A delivery is being filled in on this screen. Leave it without saving?',
  nd_other: 'Other',
  notDeliveredSave: 'Save: not delivered',
  notDeliveredHint: 'The order goes back to be delivered another day: the office picks the day on this stop.',
  anotherDay: 'Deliver it on',
  anotherDayBtn: 'Move to that day',
  finishWaitPhone: 'Send the deliveries saved on the phone first',
  finishRoute: 'Back at the yard: finish the route',
  finishRouteConfirm: 'Finish the route? Stops not delivered are marked not reached and their orders go back to be delivered another day.',
  collectFill: 'Collect {amount}',
  stops: 'stops',
  moveUp: 'Up',
  moveDown: 'Down',
  arrived: 'At the door',
  delivered: 'Delivered',
  deliverBtn: 'Delivered',
  receivedBy: 'Received by',
  doorNote: 'Shorts, substitutions, anything said at the door',
  qtyDelivered: 'Went off the truck',
  etaText: 'Text ETA',
  etaMinutes: 'minutes away',
  photoPod: 'Photo at the door',
  photoDone: 'Photo kept',
  navigate: 'Navigate',
  noAddress: 'No address on file',
  window: 'Window',
  freight: 'Freight',
  fees: 'Fees (fuel, pallets, broker)',
  allocate: 'Spread by',
  allocate_packs: 'packs',
  allocate_weight: 'weight',
  landed: 'landed',
  agingTitle: 'Accounts receivable aging',
  agingHint: 'Open invoices by how far past due they are, per customer, with payments and credits not tied to an invoice taken off (Unapplied), the terms, the credit limit and the collection status. Click a customer for the statement.',
  bucket_current: 'Current',
  bucket_d1to30: '1–30',
  bucket_d31to60: '31–60',
  bucket_d61to90: '61–90',
  bucket_d90plus: '90+',
  creditLimit: 'Credit limit',
  limitOwnerOnly: 'Only the owner sets the credit limit. You can put the customer on hold or take the hold off.',
  noLimit: 'no limit',
  onHold: 'On hold',
  holdReason: 'Why on hold',
  creditTitle: 'Credit',
  creditHint: 'Past the limit or on hold, no order is taken from any screen; the owner can take one anyway.',
  oldest: 'oldest',
  daysLateShort: 'days late',
  taxRate: 'Rate (%)',
  subtotal: 'Subtotal',
  tax: 'Tax',
  connectorTitle: 'Claude connector',
  connectorHint: 'Claude talks to the business through the same doors as the app, as a user of its own. Make a token for that user and paste it into the connector settings, never into a chat.',
  tokenOnce: 'Your new token',
  tokenOnceHint: 'Shown once. Copy it now; it cannot be shown again.',
  copyToken: 'Copy the token',
  copied: 'Copied',
  done: 'Done',
  makeToken: 'Make a token',
  tokenFor: 'For which user',
  tokenName: 'Name this token',
  tokens: 'Tokens',
  lastUsed: 'last used',
  revoked: 'Revoked',
  revoke: 'Revoke',
  howToConnect: 'How to connect Claude Code',
  howToConnectHint: 'On a computer with Claude Code, run this once with the token in place of <token>. Claude.ai connectors need sign-in by OAuth, which comes next.',
  money: 'Money',
  invoicedToday: 'Invoiced today',
  invoicedWeek: 'Invoiced, 7 days',
  paidWeek: 'Paid in, 7 days',
  openBalance: 'Accounts receivable',
  overdue: 'overdue',
  marginWeek: 'Margin, 7 days',
  slowPayers: 'Past due accounts',
  stockAtRisk: 'Inventory at risk',
  waiting: 'Needs attention',
  waitingHint: 'Work that stays stuck until someone acts on it. Click a line to clear it.',
  unknownNamesCount: 'names on sales or vendor lines not matched to an item',
  unmappedDocLines: 'vendor lines not an item yet',
  openPos: 'open purchase orders',
  tomorrowOrders: 'tomorrow',
  unpricedLinesCount: 'lines without a price',
  buyTitle: 'Purchase planning',
  buyHint: 'Orders on the books, plus the recent daily rate, plus keep-on-hand, minus stock. Change any number, then write the order per vendor.',
  buyFor: 'Buy for',
  buy: 'Buy',
  ordered: 'ordered',
  day: 'day',
  nothingToBuy: 'Nothing to buy for this window',
  noVendor: 'No home vendor',
  writePo: 'Write the order',
  poWritten: 'Order written',
  vendorDocs: 'Vendor documents',
  navOrderDay: 'Order day',
  priceSection_fruit: 'Fruits',
  priceSection_veg: 'Vegetables',
  priceSection_dry: 'Dry goods',
  priceSection_other: 'Other',
  vsOfferCol: 'Offer',
  offersTodayTitle: "Today's offers",
  offeredCol: 'Offered',
  offeredHint: 'An offer is good through the next day, then drops off by itself',
  offeredToday: 'today, good through tomorrow',
  offeredYesterday: 'yesterday, goes tonight',
  alsoOrdered: '{n} ordered there',
  vsAddItem: 'Add a product to this order',
  vsOfferLinesHint: "Offered items are blank: type how many you want from this vendor. The home vendor's tab then models only the rest (an order already saved there keeps its number: lower it).",
  vsOverModel: 'Ordered here above what is left to buy (another vendor has the rest): {items}',
  vsLowerToModel: 'Lower to the model',
  vsOfferHint: "This vendor's offer for the item, or another vendor's price for it this week",
  offeredAt: 'offered {price}',
  offersTitle: 'Vendor offers',
  offersHint: 'What a vendor texted today: pick each item and type the price per case. On the order day it gets a line on their tab at that price, left blank for you to fill, and stays on the home vendor\'s tab. Each line is good through tomorrow, then drops off by itself; placing their order uses it up sooner. Every offer stays in the database.',
  herPrice: 'Price per case',
  pickVendor: 'Pick a vendor',
  saveOffer: 'Save offer',
  clearOffer: 'Clear offer',
  clearOfferConfirm: 'Clear this offer? It is kept in the history.',
  offerTyped: 'Typed {when} by {who}',
  offerNone: 'No offer today.',
  offerUsed: 'used by an order {when}: drops off 36 hours later',
  offerTwice: 'An item is on the offer twice: keep one line.',
  offerSaved: 'Offer saved: the vendor tabs show it now.',
  vsCostHint: "The vendor's price per case: it goes on the order, and on receiving it is the cost the dock records. Correct it when the price changed.",
  openEarlier: 'The load of {date} has orders placed and not received yet.',
  openEarlierGo: 'Open it',
  callAheadCol: 'Other',
  moreOthersCol: 'More others',
  moreOthersHint: 'Call-ahead orders of customers beyond the two Other columns',
  callAheadPick: '+ Customer',
  noVendorWarn: 'No vendor to order these from (set a home vendor on the item): {items}',
  countMe: 'count',
  countMeHint: 'Below zero: a receipt or a count is missing, so ORDER does not count it as stock. Count this product (Inventory count).',
  noPriceWarn: 'On a store\'s order with no price for it, so the invoice will be refused: {items}. Publish a price on',
  columnOnLoads: 'Its column shows on',
  loadOrderTitle: '{load} order',
  loadWeek: 'Which load',
  prevLoad: 'Previous',
  nextLoad: 'Next load',
  laterLoad: 'Following',
  vendorTabs: 'Plan and vendors',
  planTab: 'Plan',
  orderColHint: 'What the model wants for this load: order it on the vendor tabs',
  vsReceived: 'received',
  vsPlacedNote: 'Placed. Copy the lines into the email to the vendor.',
  vsReceivedNote: 'Received: the stock is on hand and what did not come is on the shorts.',
  vsCode: 'Code',
  vsModel: 'Model says',
  vsModelHint: "The order day's ORDER for this vendor: base + safety + keep + the stores + call-aheads − on hand",
  vsOrder: 'Order cases',
  vsReceivedCol: 'Received',
  vsReceivedHint: 'Blank = all of it came, 0 = none',
  vsShort: 'Short',
  vshortTitle: 'Vendor shorts',
  vshortHint: "What a vendor will not bring for a load: change these stores' invoices on the invoice check (Invoice the accounts flags the lines), and tell them in the morning: each store has a to-do on Follow-ups with the text to send.",
  vshortComing: '{vendor}: {coming} of {ordered} coming',
  vshortFrom_invoice: 'from their invoice',
  vshortFrom_typed: 'what they said (typed in)',
  vshortFrom_dock: 'counted at the dock',
  vshortOnHand: 'on hand {n}',
  vshortLoad: 'load {day}',
  vshortNoStores: "No store's order holds it.",
  vshortOrderNo: 'order #{n}',
  vshortInvoiceNo: 'invoice {n}',
  vshortToDo: 'to-do on Follow-ups',
  vshortTold: 'to-do done',
  vshortCopy: 'Copy text',
  vshortCopied: 'Copied',
  vshortHandled: 'Handled',
  vshortReopen: 'Open again',
  vshortUndoTyped: 'Take back what was typed',
  vshortSaidTitle: 'The vendor said (a call or a text):',
  vshortSaidItem: 'Item on the order',
  vshortSaidQty: 'Coming',
  vshortOnCard: 'The vendor is short:',
  vshortSaid_invoice: 'invoice',
  vshortSaid_typed: 'said',
  vshortSaid_dock: 'received',
  unitCostCol: 'Unit cost',
  lineTotalCol: 'Line total',
  vsCopied: 'Copied: paste it into the email.',
  vsCopyPrompt: 'Copy these lines into the email:',
  vsStatusReceived: 'Received',
  vsStatusPlaced: 'Placed {when} by {who}',
  vsStatusDraft: 'Saved, not placed yet',
  vsStatusNone: 'Not ordered yet: the cases are what the model wants',
  vsAllItems: "All this vendor's items",
  vsCopy: 'Copy for email',
  vsSave: 'Save',
  editOrder: 'Change order',
  orderNotEditable: 'This order is picked or on its way: change it on its invoice (or at the door).',
  orderDatePast: 'The delivery date is in the past: pick today or later.',
  notTheirDeliveryDay: 'Not one of their delivery days ({days}).',
  needsOwnerOk: 'needs the owner',
  approveFloorSave: 'Approve as owner and save',
  overrideHoldSave: 'Take it anyway (owner) and save',
  changeOrder: 'Change',
  docCostFarAsk: 'These costs are more than 25% from what they cost now:\n{list}',
  docTotalOffAsk: 'The lines add up to {lines}, the total typed is {total}.',
  docSaveAnyway: 'A saved cost goes into Vendor prices and the order day at once and cannot be undone. Save it anyway?',
  voidOrderAsk: 'Order #{no} has not been delivered. After the void:',
  deleteOrderAsk: 'Order #{no} has not been delivered. After deleting the invoice:',
  voidOrderCancel: 'Cancel the order: nothing goes out (it leaves the pick list and the route)',
  voidOrderKeep: 'Keep the order: it still goes out and is invoiced again',
  cashShortBy: '{amount} short',
  cashOverBy: '{amount} over',
  routeCashShortBank: '{route} collected {collected} in cash and {counted} was turned in: {short} short. The cash below is what the customers paid; take {counted} of it to the bank and record the {short} as cash short in QuickBooks.',
  paysOpening: '{amount} of it pays the opening balance ({owed} still owed from before the ledger start).',
  shrinkOverLot: 'Lot {lot} holds {have}. Write off {n}? The {have} in the lot go, and the rest comes off stock without a lot.',
  lineKeptAsIs: 'kept as it is',
  orderChangedMeanwhile: 'Someone changed this order since you opened it. Load it again before saving.',
  reloadOrder: 'Load it again',
  ordersEveryDay: 'Every day: the orders matching what you typed (newest delivery first).',
  vsPlace: 'Place order',
  buyFromPriceFar: '{price} is more than double or under half of what it costs now ({cost}). A price you give moves the auto prices at once. Is it right?',
  soldByUnit: 'sold by {unit}',
  qboSoldAs: 'Sold as',
  qboSoldAsHint: 'What one QuickBooks quantity is: the product\'s box, or pounds or each out of it ("Jalapeno - weight" is lb). The product needs what one box holds.',
  qboSoldAsBox: 'box',
  qboSoldAsLb: 'lb',
  qboSoldAsEa: 'each',
  qboSoldAsDz: 'dozen',
  qboSoldAsBn: 'bunch',
  qboSoldAsConfirm: 'Change how {name} is sold? Its invoices read again in the new unit, and the stock they took is taken again.',
  unitLb: 'lb',
  unitEa: 'each',
  unitBn: 'bunch',
  unitDz: 'dozen',
  pricedOnSave: 'priced on save',
  activityTitle: 'Activity',
  activityHint: 'Who did what: every order, invoice, purchase order, count, price and change, with the person who made it. The automatic changes (the QuickBooks copy, nightly jobs) only when ticked.',
  activityPerson: 'Person',
  activityPersonNone: 'This person (no changes in these days)',
  activityEveryone: 'Everyone',
  activityArea: 'Area',
  activityAllAreas: 'All areas',
  activitySystem: 'Include automatic changes',
  activityAutomatic: 'Automatic',
  activityNone: 'Nothing done in this period.',
  activityOlder: 'Show older',
  activityWhen: 'When',
  activityWhat: 'What',
  activityWhich: 'Which',
  actTest: 'TEST',
  act_created: 'created',
  act_changed: 'changed',
  act_deleted: 'deleted',
  act_placed: 'placed',
  act_received: 'received',
  act_voided: 'voided',
  act_cancelled: 'cancelled',
  act_confirmed: 'confirmed',
  act_picked: 'picked',
  act_delivered: 'delivered',
  act_invoiced: 'invoiced',
  act_closed: 'closed',
  act_published: 'published',
  act_counted: 'counted',
  actArea_sales: 'Sales',
  actArea_buying: 'Buying',
  actArea_stock: 'Stock',
  actArea_prices: 'Prices',
  actArea_customers: 'Customers',
  actArea_products: 'Products',
  actArea_delivery: 'Delivery',
  actArea_admin: 'Administration',
  actArea_other: 'Other',
  actThing_orders: 'Order',
  actThing_order_lines: 'Order line',
  actThing_invoices: 'Invoice',
  actThing_invoice_adjustments: 'Invoice line',
  actThing_payments: 'Payment',
  actThing_credits: 'Credit',
  actThing_purchase_orders: 'Purchase order',
  actThing_purchase_order_lines: 'Purchase order line',
  actThing_receipts: 'Receipt',
  actThing_inventory_counts: 'Count',
  actThing_inventory_movements: 'Stock movement',
  actThing_price_lists: 'Price list',
  actThing_items: 'Product',
  actThing_customers: 'Customer',
  actThing_vendors: 'Vendor',
  actThing_vendor_offers: 'Vendor offer',
  actThing_vendor_items: 'Vendor code',
  actThing_qbo_links: 'QuickBooks link',
  actThing_users: 'User',
  actThing_lost_sales: 'Lost sale',
  actThing_customer_activities: 'Customer note',
  actThing_routes: 'Route',
  actThing_cost_observations: 'Cost',
  priceTierLabel: 'Price tier',
  priceTierConfirm: 'Move {name} to {tier}? Their orders not invoiced yet take the new prices.',
  productCategory: 'Category',
  productCategoryNone: 'No category',
  vsCostFarAsk: 'These costs are more than 25% from the cost on the line. A cost you give moves the auto prices and the open orders at once. Is it right?\n\n{list}',
  vsPlaceConfirm: 'Place the order of {n} cases with {vendor}?',
  costFrom: 'from {vendor}',
  vsReopen: 'Reopen',
  vsReceive: 'Receive',
  vsReceiveConfirm: 'Receive what came from {vendor}? A blank RECEIVED is taken as all of it, 0 as none; what did not come goes on the shorts.',
  vsReceiveHint: 'When the confirmation or the truck comes in, type what came in RECEIVED (blank = all of it, 0 = none), then Receive.',
  vsOrderHint: 'ORDER is pre-filled with what the model wants. Change any number you disagree with, then Place order.',
  vsNothing: 'Nothing to order from this vendor for this load. Tick "All this vendor\'s items" to add one.',
  anyDay: 'Any day',
  placedCol: 'Placed',
  loadCol: 'Load',
  orderedCol: 'Ordered',
  byCol: 'By',
  orderHistoryTitle: 'Order history',
  orderHistoryHint: 'Every order placed with a vendor, line by line: ordered, received, short, cost. Click a line to open its tab.',
  shortsTitle: 'Shorts',
  editInvoice: 'Edit',
  editInvoiceTitle: 'Edit invoice {no}',
  editInvoiceClosed: 'Only an issued invoice made in the app can be edited here, and one from an earlier month only by the owner. One from QuickBooks changes in QuickBooks; a void one is not changed.',
  editInvoiceChanged: 'Someone changed this invoice after you opened it. Saving now would be refused.',
  editInvoiceReopen: 'Open it again (what you typed here is lost)',
  adjKind_short: 'Short',
  adjKind_edit: 'Changed',
  adjKind_add: 'Added',
  adjKind_remove: 'Taken off',
  shortsHint: 'What vendors did not send, by load, vendor and item.',
  allVendors: 'All vendors',
  ordersPlaced: 'Orders placed',
  orderedValue: 'Ordered value',
  casesShort: 'Cases short',
  shortValue: 'Short value',
  vendorPricesTitle: 'Vendor prices',
  papersTitle: 'Papers',
  gmailTitle: "Gmail",
  gmailTrusted: "Trusted vendor addresses",
  gmailTrustedHint: "Papers from these mail domains go into history by themselves once Google confirms the mail really came from there (for Mata's: mataproduce.com). Any other paper from the mailbox waits for you to press \"It's real\".",
  gmailTrustedSave: "Save",
  gmailTrustedNone: "None yet: every paper from the mailbox waits for a tap.",
  paperItsReal: "It's real",
  gmailNotSetUp: "Gmail is not set up on this server (GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET).",
  gmailNotConnected: "Connect your business mailbox once: every hour the app reads the mails under the label \"Vendor papers\" and drops each PDF or photo here. It only reads; it never sends, moves or deletes a mail.",
  gmailConnect: "Connect Gmail",
  gmailConnected: "Reading {email}, label \"{label}\"",
  gmailLastRead: "Last read {when}: {what}",
  gmailNeverRead: "Not read yet: the first read is within the hour, or press Read now.",
  gmailReadNow: "Read now",
  gmailDisconnect: "Disconnect",
  gmailBack_connected: "Gmail is connected.",
  gmailBack_declined: "Gmail was not connected: the access was declined at Google.",
  gmailBack_google: "Google could not connect the mailbox. Try again.",
  gmailBack_state: "The connection timed out or came from another window. Press Connect Gmail again.",
  gmailBack_signin: "Sign in as the owner, then press Connect Gmail again.",
  gmailBack_exchange: "Google did not hand over the access. Press Connect Gmail again (allow reading the mail).",
  papersHint: 'Vendor invoices and price lists by the stack. Drop them in; the reader goes through them one at a time, newest first. One that adds up goes into the price and purchase history by itself and never becomes a bill to pay; one that does not waits here with the reason.',
  papersDrop: 'Drop PDFs or photos here, or tap to choose (several at once)',
  papersUploading: 'Sending {n} of {total}…',
  papersUploaded: '{added} added, {dupes} already in, {failed} not sent',
  paperWaiting: 'Waiting',
  paperReading: 'Reading',
  paperPosted: 'In history',
  paperHeld: 'Needs a look',
  paperSkipped: 'Skipped',
  paperFailed: 'Not read',
  papersAll: 'All',
  callAheadAdd: "Add a column",
  callAheadRemove: "— take the column off —",
  buyFromTitle: "{item}: buy it for this load from",
  buyFromHint: "Choose the vendor this load buys it from: the cases go on that vendor's order for the load, and the other tab counts them as bought.",
  buyFromPick: "choose vendor",
  buyFromCases: "cases",
  buyFromPrice: 'Price ($)',
  buyFromBadPrice: 'Type the price as a number, like 18',
  vsNoCostAsk: 'No cost on {n} line(s) ({items}): the cost will not follow this order for them. Place anyway?',
  buyFromAlways: "Buy it from this vendor from now on",
  buyFromGo: "Put it on that order",
  buyFromSaid: "{n} {item} added to the order from {vendor} for this load.",
  buyFromBadQty: "Type the cases as a number.",
  callAheadKeep: "Keep this column every week",
  callAheadUnkeep: "— stop keeping it (it shows while it has an order) —",
  buyFromOnOrders: "On orders for this load:",
  buyFromPlaced: "placed",
  buyFromTakeOff: "Take these cases off the order from {vendor} (it has {n})",
  buyFromHomePlaced: "The order from {vendor} for this load is already placed with {n}: these cases are bought on top of them. To move them instead, reopen that order on its tab first.",
  heldTitle: "Why they need a look",
  papersRecheck: "Check all again (free)",
  papersRecheckHint: "Checks every paper that needs a look again from what was already read, at no cost: after a vendor is named or added, or a unit is added.",
  papersRecheckSaid: "{posted} went into history · {held} still need a look",
  heldKeep: "{n} the reader only doubted (its own note, a price far from the last one, or a total with freight or fees)",
  heldVendor: "“{name}”: the vendor was not recognised ({n})",
  heldVendorHint: "Pick who it is once: these go in now, and later papers with this name are recognised.",
  heldVendorSave: "It's this vendor",
  heldUnit: "{n} with a unit the app does not know",
  heldUnitHint: "Units: {units}. Send them to Claude; once they are added, press Check all again.",
  heldSender: "{n} from an address not confirmed as the vendor's",
  heldSenderHint: "Open each one; if it really is from the vendor, press It's real on its line.",
  heldLine: "{n} with a line that does not add up (quantity × price)",
  heldLineHint: "The reader misread a number. Reading them again usually fixes it (a few cents each).",
  heldLineAgain: "Read these {n} again",
  heldOther: "{n} for other reasons",
  heldOtherHint: "The reason is on each line below. Open the paper from its file name to decide.",
  vendorCodesTitle: "Vendor codes",
  vendorCodesHint: "Every code a vendor prints on its invoices and price lists, and the product it is linked to. A code's prices follow its link: link one and its past prices reach the product's cost history with their dates; change a wrong one and they move. Hide a code that is not a product (freight, a fee).",
  navVendorCodes: "Vendor codes",
  codesFind: "Find a code, a description or a product",
  codesUnlinked: "Not linked",
  codesLinked: "Linked",
  codesHidden: "Hidden",
  codesNone: "No codes here.",
  vendorCodeCol: "Code",
  vendorDescCol: "Vendor's description",
  productCol: "Product",
  lastSeenCol: "Last seen",
  papersCol: "Papers",
  codeNotLinked: "Not linked",
  codeLink: "Link",
  codeChange: "Change",
  codeUnlink: "Unlink",
  codeNewProduct: "New product",
  codeHide: "Hide",
  codeShow: "Show",
  codeLinkTo: "Link to a product:",
  codeLinkedSaid: "{code} → {product}. {n} past prices added to its cost history.",
  codeUnlinkedSaid: "{code} unlinked: it is back in Unknown names.",
  codeMovedSaid: "{code} → {product}: its prices moved with it.",
  codeUnlinkAsk: "Unlink {code} from {product}? Its prices leave the product and the code goes back to Unknown names.",
  codeHiddenSaid: "{code} hidden: not a product.",
  codeShownSaid: "{code} shown again.",
  codeAdd: "Add a code",
  codeAddHint: "The code as the vendor prints it on the invoice.",
  codeLinkedHere: "Linked to this product: its prices count here.",
  codeNewFrom: "New product from the vendor's code {code}: it is linked when you save.",
  paperKeep: 'Keep it',
  papersKeepAll: 'Keep all {n} on this page',
  papersKeepHint: "Keep it is your word for a paper the reader only had a doubt about (its own note, a price far from the last one, or a total that differs when every line checks out on its own: freight, fees, a discount). Its prices go into history; the other checks still apply. Codes the reader said were cut off are not trusted: those lines match by the item's words, and the rest go to Unknown names to link.",
  papersKept: '{posted} kept into history · {held} still need a look',
  papersShowing: '{from}–{to} of {total}',
  papersNewer: 'Newer',
  papersOlder: 'Older',
  papersPause: 'Pause reading',
  papersResume: 'Resume reading',
  papersPaused: 'Reading is paused: nothing is read until the owner resumes it.',
  papersNoReader: 'The reader is not set up on this server (ANTHROPIC_API_KEY): papers wait until it is.',
  papersStopped: 'Reading stopped: {why}',
  oldMailTitle: 'Old mail (Google Takeout)',
  oldMailHint: "Drop the .mbox file from your Google Takeout download (open the .zip; it is in Takeout › Mail). The papers attached to mail of the last two years come in and are read like the mailbox's: a confirmed trusted vendor's go into history by themselves. Spam and Trash are passed over; each paper read costs a few cents. Keep this page open until it says Done. If it stops, drop the same file again: what is in already is passed over.",
  oldMailPick: 'Choose the .mbox file',
  oldMailRunning: 'Reading {read} of {total} MB · {mails} mails · {sent} papers sent',
  oldMailTally: '{sent} papers sent · {known} in already · {old} mails older than two years · {junk} in Spam or Trash · {refused} not taken · {failed} not sent',
  oldMailNoTrusted: 'No trusted vendor domain is saved on the Gmail card: every paper from old mail would wait for "It\'s real". Save the vendor\'s domain there first.',
  oldMailDone: 'Done.',
  oldMailStopped: 'Stopped. Drop the same file again to go on.',
  oldMailStop: 'Stop',
  oldMailNotMbox: 'That is not a Google Takeout mail file (.mbox). Open the Takeout .zip and take the file inside Takeout › Mail.',
  papersCost: 'Each paper read costs a few cents of Anthropic credit (a long price list up to about 30 cents).',
  paperReadAgain: 'Read again',
  papersRetryFailed: 'Read all not read again',
  docHistory: 'History',
  paperOpenDoc: 'Open',
  paperFileCol: 'File',
  paperReceivedCol: 'Received',
  paperStatusCol: 'Status',
  paperWhyCol: 'Why',
  papersNone: 'No papers yet. Drop the first ones above.',
  vendorPricesHint: 'Every price any vendor gave us: texted offers, price lists, bills and what came on the truck. The vendor\'s own price, freight not included.',
  latestByItem: 'Latest by item',
  allPrices: 'All prices',
  changeHint: 'That vendor\'s newest price list against the list before it: up, down or the same',
  priceSame: 'same',
  wasPrice: 'previous list',
  notOnLatestList: 'Not on the {date} list: not on it, or its code is not linked to this product',
  latestListLine: '{vendor}: newest price list {date}',
  staleCount: '{n} product(s) not on it (amber dates)',
  allFromLatest: 'every product is from it',
  unlinkedOnList: '{n} lines on it are not linked to a product',
  spreadCol: 'Spread',
  spreadHint: 'How far apart the vendors\' latest prices are, as a share of the cheapest',
  sourceCol: 'From',
  nItems: '{n} items',
  nPrices: '{n} prices',
  pricesTruncated: 'Only the newest 5,000 prices are shown: pick a shorter period or one vendor.',
  priceSrc_offer: 'Text offer',
  priceSrc_list: 'Price list',
  priceSrc_invoice: 'Vendor bill',
  priceSrc_receipt: 'Truck (dock)',
  priceSrc_manual: 'Typed in',
  priceSrc_import: 'Imported',
  vendorBills: 'Vendor bills',
  vendorBillsHint: 'What vendors bill or credit us: invoices, credit memos, statements and other documents. An invoice with a total and a due date is a bill to pay.',
  vendorPriceLists: 'Vendor price lists',
  vendorPriceListsHint: 'The prices vendors quote us. Each line with a known item becomes a cost in the ledger.',
  enterVendorBill: 'Enter a vendor bill',
  enterVendorPriceList: 'Enter a price list',
  dateCol: 'Date',
  costsRecordedCol: 'Costs recorded',
  unmappedCol: 'Not an item',
  totalCol: 'Total',
  openCol: 'Open',
  photoCol: 'Photo',
  billDueCol: 'Due',
  docNoCol: 'Number',
  vendorDocsHint: "Every price a vendor gives us, kept: type the lines in now, attach the photo, and the cost ledger follows.",
  enterVendorDoc: 'Enter a vendor document',
  docType: 'Type',
  doc_invoice: 'Invoice',
  doc_price_list: 'Price list',
  doc_credit_memo: 'Credit memo',
  doc_statement: 'Statement',
  doc_other: 'Other',
  docNumber: 'Number as printed',
  docDate: 'Date',
  costsRecorded: 'costs recorded',
  unmappedLines: 'not an item yet',
  unmappedLine: 'not an item yet',
  descriptionAsPrinted: 'Description as printed',
  vendorCode: "Vendor's code",
  whichItem: 'Which item?',
  photo: 'Photo or PDF',
  photoAttached: 'photo attached',
  noPhoto: 'No photo yet.',
  attachPhoto: 'Take a photo / attach',
  intelTitle: 'Price intelligence',
  intelLink: 'Price analysis',
  phLink: 'Price history',
  phTitle: 'Price history',
  phHint: 'What we paid, what we set and what we charged, for any period, before credits. Every price is kept for good. Margins count the lines whose cost is known (Cost known).',
  phSrc_order_cancelled: 'Placed order (cancelled)',
  phSrc_order_replaced: 'Placed order (placed again)',
  phSrc_order_reopened: 'Placed order (reopened, not placed yet)',
  phCharged: 'What we charged, by week',
  phCustomers: 'What each customer paid',
  phPaid: 'What we paid',
  phSet: 'What we set',
  phNone: 'Nothing in this period',
  phWeek: 'Week of',
  phPacks: 'Packs',
  phAvgPrice: 'Avg price',
  phAvgCost: 'Avg cost',
  phLastPrice: 'Last price',
  phDate: 'Date',
  phVendor: 'Vendor',
  phSource: 'From',
  phVendorPrice: 'Vendor price',
  phFreight: 'Freight',
  phFor: 'For',
  phPrice: 'Price',
  phCostBasis: 'Cost then',
  phNote: 'Note',
  phList: 'Price list',
  phRule: 'Customer price',
  phMarginRule: 'margin',
  phUntil: 'until',
  phEnded: 'ended',
  phSrc_receipt: 'Dock',
  phSrc_invoice: 'Bill',
  phSrc_list: 'Price list',
  phSrc_manual: 'Typed',
  phSrc_import: 'Import',
  phSrc_order: 'Placed order',
  thisWeek: 'This week',
  sold7: 'Sold, last 7 days',
  sold28: 'Sold, last 4 weeks',
  marginNowBefore: 'Margin, last 4 weeks · the 4 before',
  was: 'was',
  costWeeks: 'Weekly cost, last 12 weeks',
  soldFor: 'Sold at',
  soldWeeks: 'Weekly sold price, last 12 weeks',
  whoPaidWhat: 'Last price by customer',
  competitors: 'Competitor prices',
  noCompetitorPrices: 'None recorded yet. Every price you see or hear goes here.',
  seenPrice: 'Record a price you saw',
  competitor: 'Competitor',
  seenWhere: 'Seen where',
  cps_customer_invoice: "Customer's invoice",
  cps_quote: 'Quote they got',
  cps_price_list: 'Price list',
  cps_shelf: 'Store shelf',
  cps_word_of_mouth: 'Word of mouth',
  cps_other: 'Other',
  recordSeenPrice: 'Record it',
  standingOrder: 'Standing order',
  standingHint: 'What they order every week. The app drafts it for each delivery day; the office confirms with a tap.',
  retireStanding: 'Retire this standing order',
  standingSummary: 'On file',
  packs: 'packs',
  standingDue: 'Standing orders for',
  draftStanding: 'Create drafts',
  drafted: 'drafted',
  confirmOrder: 'Confirm',
  cancelOrder: 'Cancel order',
  cancelWhy: 'Why cancel?',
  markDelivered: 'Delivered',
  markOut: 'On the road',
  profitTitle: 'Profitability',
  profitHint: 'Revenue, cost and margin from issued invoices. The cost is the one at the time of sale, or the cost in force that day.',
  period: 'Period',
  range_d30: 'Last 30 days',
  range_d90: 'Last 90 days',
  range_d365: 'Last year',
  groupBy: 'By',
  revenue: 'Revenue',
  invoicesCount: 'invoices',
  withCost: 'with cost',
  costCoverage: 'Cost known for',
  costCoverageHint: 'The rest has no cost on file yet, so the margin shown is partial.',
  reports: 'Reports',
  count: 'Count',
  receive: 'Receive',
  pick: 'Pick',
  lots: 'Lots',
  receiveTitle: 'Receiving',
  receiveHint: 'What came off the truck, by the case. Each line becomes a lot.',
  vendorTicket: 'Vendor ticket / invoice no.',
  addItem: 'Add an item',
  unitCost: 'Cost per pack',
  docLines: 'Document lines',
  costNow: 'Cost now',
  costNowHint: "The product's cost in the app today (the newest from any vendor), to compare with this document",
  costChange: 'Change',
  linesNotLinked: '{n} not linked to a product',
  grade: 'Grade',
  grade_good: 'Good',
  grade_fair: 'Fair',
  grade_reject: 'Reject',
  origin: 'Origin',
  vendorLot: 'Vendor lot',
  received: 'Received',
  receivedPacks: 'packs received',
  lot: 'Lot',
  lotCode: 'Lot',
  ageDays: 'days old',
  oldestFirst: 'Oldest first: pick from the top.',
  noLots: 'No lots with stock',
  recordShrink: 'Record a loss',
  shrinkReason: 'Why',
  shrink_age: 'Age',
  shrink_damaged: 'Damaged',
  shrink_temperature: 'Temperature',
  shrink_over_ordered: 'Over-ordered',
  shrink_transfer: 'Lost in transfer',
  shrink_short_dated: 'Short-dated at receipt',
  shrink_sample: 'Sample / given',
  shrink_other: 'Other',
  shrinkSaved: 'Loss recorded',
  pickTitle: 'Pick list',
  prepTab: 'Prep card',
  prepTitle: 'Prep card',
  prepHint: "The invoices dated these days (QuickBooks' and the app's) as the order fillers build them. Tick the ones to print and press Print prep card: one letter sheet folded in four, TOTALES first, then each order.",
  prepFrom: 'From',
  prepTo: 'To',
  prepYesterday: 'Yesterday',
  prepToday: 'Today',
  prepTomorrow: 'Tomorrow',
  prepAll: 'All',
  prepNone: 'None',
  prepNotPrinted: 'Not printed yet',
  prepCount: '{n} of {total} selected · {qty} total qty',
  prepCustomer: 'Customer',
  prepInvoiceNo: '#',
  prepStatus: 'Status',
  prepNew: 'new',
  prepPrintedAt: 'card {when}',
  prepChanged: 'CHANGED since printed',
  prepTest: 'TEST',
  prepNoLines: 'no product lines',
  prepEmpty: 'No invoices are dated these days.',
  prepPrint: 'Print prep card',
  prepPrintHint: 'Letter paper, a regular printer. Fold on the dashed lines. If it says two-sided, tick "Print on both sides" (flip on long edge).',
  prepFitted: '{orders} orders on {panels} panels, {min}–{max} pt type',
  prepOneSide: 'ONE side',
  prepTwoSided: 'print TWO-SIDED (flip on long edge)',
  prepSheets: '{n} sheets',
  prepSpilled: 'one order was too long for a panel and continues',
  prepMarked: 'Sent to the printer and marked printed.',
  prepMarkFailed: 'Printed, but it could not be marked printed:',
  prepTotals: 'TOTALES',
  prepCont: '(cont.)',
  prepTickets: 'Print tickets (Zebra)',
  prepPullZebra: 'TOTALES (Zebra)',
  prepZebraHint: 'One 4 × 6 label per order, a box to tick on every line: hand them to whoever fills the orders. In the print box pick the Zebra, Margins: None, Scale: Default (100%).',
  prepTicketAt: 'ticket {when}',
  prepLabels: '{n} labels',
  prepLabel1: '1 label',
  prepSent: 'Sent to the printer.',
  prepPulled: 'Pulled',
  prepChecked: 'Checked',
  prepLinesQty: '{n} lines · {q} qty',
  prepLineQty: '1 line · {q} qty',
  prepOrdersN: '{n} orders',
  prepOrders1: '1 order',
  prepItemsN: '{n} items · {q}',
  prepItems1: '1 item · {q}',
  pickHint: "Confirmed orders for the day. Pull from the oldest lot. Tap Picked when the order is on the pallet.",
  nothingToPick: 'Nothing to pick for this day',
  totals: 'Totals for the day',
  short: 'short',
  pickedBtn: 'Picked',
  pickedAlready: 'Picked',
  deliveries: 'Deliveries',
  fromPo: 'From a purchase order',
  vendor_required: 'Choose the vendor',
  makeInvoice: 'Make the invoice',
  invoiced: 'Invoiced',
  invoiceDate: 'Invoice date',
  dueDate: 'Due',
  paid: 'Paid',
  overpaidOnAccount: 'Paid {amount} more than it comes to: kept as the customer\'s money on account',
  onAccountAmount: '{amount} on account',
  balanceDue: 'Balance due',
  print: 'Print',
  voidInvoice: 'Void this invoice',
  deleteInvoice: 'Delete this invoice',
  deleteReason: 'Why delete it?',
  deletedTag: 'Deleted',
  deleteVoidHint: 'It is already void (its cases went back then). Deleting takes it off every list, report and statement, and out of QuickBooks. Who deleted it, when and why stay on Administration → Activity.',
  deleteInvoiceHint: 'It disappears from every list, report and statement, and from QuickBooks. Its cases go back on the shelf. Who deleted it, when and why stay on Administration → Activity.',
  voidReason: 'Why is it void?',
  voidKeepCredits: "Keep this invoice's credits on the customer's account as money to apply",
  voidCreditLinesAsked: 'This invoice has credit lines (a product at a price below zero). Voiding takes them off too, and invoicing the order again does not bring them back: give the credit again on the next invoice.',
  voidDropCreditLines: 'I will give the credit again on the next invoice',
  voidCreditsAsked: 'This invoice has credits. Voiding it leaves them on the customer\'s account: tick the box, then void again.',
  voided: 'Void',
  recordPayment: 'Record a payment',
  amount: 'Amount',
  method: 'How',
  method_cash: 'Cash',
  method_check: 'Check',
  method_card: 'Card',
  method_ach: 'Bank transfer',
  method_zelle: 'Zelle',
  method_qbo_payments: 'QuickBooks Payments',
  method_other: 'Other',
  method_applied: 'Applied from account',
  method_credit_memo: 'Credit memo (QuickBooks)',
  paymentInvoices: 'Invoices this payment pays',
  paymentCol: 'Payment',
  appliedTotal: 'Applied',
  leftOnAccount: 'Left on account',
  moreThanReceived: 'The invoices take more than the amount received',
  moreThanOwes: 'That is more than the invoice owes',
  applyOnAccount: 'Apply money on account',
  applyOnAccountHint: '{amount} is on account and not on any invoice. It pays the oldest invoices first.',
  appliedSaved: 'Applied to invoices',
  reverseGroup: 'Reverses the whole payment ({n} lines)',
  reference: 'Check no. / reference',
  applyTo: 'Apply to',
  onAccount: 'On account (no invoice)',
  paymentSaved: 'Payment recorded',
  reverse: 'Reverse',
  reverseWhy: 'Why reverse it?',
  giveCredit: 'Give a credit',
  creditReason: 'Reason',
  reason_quality: 'Quality',
  reason_short: 'Short',
  reason_price: 'Price',
  reason_returned: 'Returned',
  reason_goodwill: 'Goodwill',
  reason_other: 'Other',
  creditSaved: 'Credit given',
  statement: 'Statement',
  openInvoices: 'Open invoices',
  daysLate: 'days late',
  onTime: 'not due yet',
  payments: 'Payments',
  credits: 'Credits',
  ledgerNotStarted: 'Balances start when the owner sets the ledger start (Finance, Ledger and opening balances); what each customer owed on that day goes in there as their opening balance.',
  startLedger: 'Count balances from',
  billTo: 'Bill to',
  thankYou: 'Thank you for your business',
  unpricedLines: 'Every line needs a price before invoicing',
  source_invoice: 'Invoice',
  source_clover: 'Register',
  source_vendor: 'Vendor',
  untilDate: 'until',
  margin: 'margin',
  // sites and transfers
  site: 'Site',
  sites: 'sites',
  sitesTitle: 'Sites and companies',
  sitesHint: 'Every place stock sits: the hub, the stores, the trucks. A store that belongs to another company buys from the hub through transfers.',
  addSite: 'Add a site',
  siteCode: 'Code',
  kind_hub: 'Hub',
  kind_store: 'Store',
  kind_truck: 'Truck',
  company: 'Company',
  companies: 'Companies',
  companyCode: 'Code',
  homeCompany: 'Our company',
  addCompany: 'Add a company',
  address: 'Address',
  storeOfOtherCompanyHint: 'A store of another company gets a customer record: its transfers are priced on that record and land as delivered orders, ready to invoice.',
  billsAsCustomer: 'Bills as a customer',
  closeSite: 'Close',
  reopenSite: 'Reopen',
  transfers: 'Transfers',
  transfersHint: 'Stock moving between sites. Send it and it leaves the lots here; receive it and it becomes lots there.',
  oneSiteOnly: 'There is only the hub. The owner adds stores and trucks under Sites and companies.',
  newTransfer: 'New transfer',
  noTransfers: 'No transfers yet',
  fromSite: 'From',
  toSite: 'To',
  status_sent: 'Sent',
  sendTransfer: 'Send',
  receiveAll: 'Received in full',
  receiveShort: 'Received short',
  confirmReceived: 'Confirm what arrived',
  qtyReceived: 'Received',
  oldestLotFirst: 'Oldest lot first',
  intercompanyOrder: 'Order',
  saveDraft: 'Save draft',
  // sign-in with a password, users
  loginLabel: 'Phone, email or username',
  username: 'Username',
  usernameRule: 'A letter first; 2 to 30 letters, digits, dots or dashes',
  password: 'Password',
  loginNeeded: 'Type your phone, email or username and your password',
  phoneOrEmail: 'Phone or email',
  usernameOptional: 'Username (optional)',
  wrongLogin: 'Wrong phone, email or password',
  signInWithCode: 'Sign in with a text code instead',
  signInWithPassword: 'Sign in with a password instead',
  usersTitle: 'Users',
  usersHint: 'The people who sign in. Add a person with a first password and tell it to them in person; the app asks them to pick their own the first time.',
  addPerson: 'Add a person',
  role: 'Role',
  userRole_owner: 'Owner',
  userRole_office: 'Office',
  userRole_warehouse: 'Warehouse',
  userRole_driver: 'Driver',
  userRole_sales: 'Sales',
  userRole_readonly: 'Read only',
  homeSite: 'Home site',
  anySite: 'The hub',
  firstPassword: 'First password',
  setPassword: 'Set password',
  resetPassword: 'Reset password',
  newPassword: 'New password',
  currentPassword: 'Current password',
  changePassword: 'Change password',
  passwordChanged: 'Password changed. Use it the next time you sign in.',
  passwordRule: 'At least 8 characters.',
  passwordsDiffer: 'The two passwords are not the same',
  repeatPassword: 'Repeat the new password',
  mustChangeHint: 'Your password was reset: pick a new one now.',
  noPasswordNudge: 'Set a password so you can sign in without a text code.',
  lastSeen: 'Last seen',
  never: 'never',
  switchOff: 'Switch off',
  switchOn: 'Switch on',
  noPasswordYet: 'no password yet',
  saved: 'Saved',
  switchedOff: 'Off',
  // the prices screen and the price list
  pricesTitle: 'Price management',
  pricesHint: 'Every active item with its cost, the lowest competitor price and one column per tier. Type over a tier price; the Engine column is what the cost and the tier margin would set. Publish writes a dated price list.',
  soldAvgCol: 'Sold avg 28 d',
  competitorLowCol: 'Competitor low',
  engineCol: 'Engine',
  gmCol: 'GM %',
  changesCol: 'Changes',
  onlyChanged: 'Only changed rows',
  publishN: 'Publish {n} price(s)',
  publishConfirm: 'Publish {n} price(s)? Invoices charge them from today ({floor} under the floor).',
  pricesUnsaved: 'Prices are typed but not published. Leave anyway?',
  itemsCol: 'Items',
  packsSold: '{n} packs sold',
  typedHidden: '{n} typed price(s) hidden by the search',
  vsPrior30: 'vs prior 30 d',
  customersHint: 'Every account with its price tier and stage, sales and gross margin over the last 30 days, how they are ordering, 90-day sales, the last invoice, the open balance and what is past due. The totals compare these 30 days with the 30 before.',
  sales30Col: 'Sales 30 d',
  trendCol: 'Trend',
  customersTabAccounts: 'Accounts',
  customersTabTrends: 'How they are ordering',
  customersTabFollowups: 'Follow-ups',
  trendHint: 'How they are ordering: the last 4 weeks against the 4-week average of the 8 weeks before (the same trend as the How they are ordering tab)',
  itemsHint: 'Every product with its SKU, home vendor, cost in force, pack, box and where it comes from this month. Prices are on Price management.',
  costDateCol: 'Cost date',
  noCostCol: 'no cost',
  showInactive: 'Show inactive',
  countGridHint: 'Type how many are in the cooler under Count, in the product\'s unit, with decimals for an open one (2.5 is two and a half). Save records the count; leave an item blank to skip it.',
  countedCol: 'Counted',
  varianceCol: 'Variance',
  openLeftCol: 'Open, left',
  saveCountN: 'Save {n} count(s)',
  typedCol: 'Typed',
  netVariance: 'Net variance',
  countUnsaved: 'Counts are typed but not saved. Leave anyway?',
  countInvalid: 'Fix the counts in red first: a count is a number such as 3 or 2.5.',
  cannotCount: 'To fix',
  unappliedCol: 'Unapplied',
  paymentsAndCredits: 'Payments and credits',
  unappliedHint: 'Payments on account and credits not tied to an invoice; they lower the balance',
  collectionCol: 'Collection',
  reportTotal: 'Total',
  overdueTotal: 'Past due',
  billNoCol: 'Bill no.',
  billDateCol: 'Bill date',
  notDue: 'Not due',
  dueToday: 'Due today',
  daysLateN: '{n} d late',
  vendorsCol: 'Vendors',
  payHint: 'Select a bill and press Enter (or double-click) to pay it.',
  payBillTitle: 'Pay {vendor} {no}',
  close: 'Close',
  claudeTitle: 'Claude',
  claudeWorking: 'Claude is working on it…',
  claudeTalk: 'Talk to Claude',
  claudeStop: 'Stop listening',
  claudeListening: 'Listening… tap when done',
  claudeFilled: 'Filled in by Claude: check every line, then save.',
  claudeYou: 'You:',
  boxCol: 'Box',
  boxHint: 'The box as bought: what a full box weighs and how many pieces it holds',
  countShort: 'ct',
  originNow: 'Comes from now',
  originNowHint: 'Where it is sourced this month (set on the product, by season)',
  boxBlock: 'The box',
  boxWeight: 'Full box weighs (lb)',
  boxCount: 'Pieces in a box',
  sourcingBlock: 'Where it comes from',
  sourcingHint: 'By season: the region it comes from and the months. The list shows this month\'s.',
  sourcingNone: 'Not set yet.',
  addSource: '+ Add a region',
  regionLabel: 'Region',
  regionPlaceholder: 'Michoacán, Mexico',
  monthsLabel: 'Months',
  sourceVendor: 'Usually from',
  anyVendor: 'Any vendor',
  sourceNote: 'Note',
  vendorsPriced: 'Vendors that priced it',
  vendorsNone: 'No vendor price on file yet.',
  pricesCount: '{n} prices',
  priceOne: '1 price',
  receiveQtyBad: 'Every line needs a quantity above 0: type it, or remove the line',
  receiveCostBad: 'A cost is not a number: fix it, or leave it blank',
  consentNotePlaceholder: 'asked at the counter on Monday',
  newRoutePlaceholder: 'Route 2',
  boxWeightBad: 'Type the pounds as a number, like 25 or 40.5',
  boxCountBad: 'Type a whole number of pieces, like 48',
  sourceHalf: 'Give this region its months (or remove the row)',
  pricesListLink: 'Open the price list',
  navPriceLists: 'Price lists',
  navPinned: 'Pinned',
  navPin: 'Pin to the top',
  navUnpin: 'Unpin',
  seasonBlock: 'Seasonality',
  seasonHint: 'From our own invoices, register sales and vendor prices, by month of the year (a year on average).',
  seasonSold: 'Sold a year',
  seasonRetail: 'Register (units)',
  seasonPrice: 'Avg price',
  seasonCost: 'Avg cost',
  seasonNoData: 'No sales yet.',
  pricesLiveOnPm: 'Prices are on Price management.',
  claudeListeningShort: 'Listening',
  claudeTapWhenDone: 'Tap Claude when you are done.',
  claudeTapToSend: 'Tap to send',
  claudePausedHint: 'The phone stopped the microphone. Tap Claude to send what it heard.',
  claudeSayIt: 'Say it now…',
  claudeNoSpeech: 'Did not hear anything. Tap Claude and talk.',
  claudeMicBlocked: 'The microphone is blocked: allow it for this site in the browser (the lock by the address), then tap Claude again.',
  confirmReverse: 'Reverse the {amount} payment to {vendor}? A reversal cannot be undone; the payment would be entered again.',
  shareCol: 'Share',
  grossMarginCol: 'Gross margin',
  costKnownCol: 'Cost known',
  registerCol: 'Register',
  registerMarginCol: 'Register margin',
  registerTitle: 'The register (Clover)',
  registerHint: "What the counter sold in the period, at the case's cost of its day by the share of a case each sale is; the margin is on the sales with a cost. Shown beside the invoices, never added into them.",
  gapNoConvert: 'Sold in a unit its box content does not convert (pounds of a box counted in pieces, or the other way): check the unit on its sales or what one box holds.',
  gapsTitle: 'Why some sales have no margin',
  gapsHint: 'Fix these and the margin covers more of your sales. Biggest first.',
  gapNoProduct: 'of invoice sales have no product (lines: {n}).',
  gapRegisterNoProduct: 'of register sales have no product (lines: {n}).',
  gapLinkMatching: 'Link QuickBooks products',
  gapLinkInbox: 'Name unknown names',
  linkingTitle: 'Linking sheet',
  linkingHint: 'Everything not linked to a product yet (vendor codes, QuickBooks products, names on sales) in one Excel file. Fill it in Excel at your own pace, then upload it here.',
  linkingStep1: '1. Download the sheet',
  linkingStep1Hint: 'One tab each: Vendor codes, QuickBooks, Sales names, Box content (what one box holds, for sales by the pound or each). Where the words match, the Product column already has a guess, marked "guess: check it".',
  linkingDownload: 'Download the linking sheet',
  linkingMaking: 'Making the sheet…',
  linkingStep2: '2. Fill the Product column',
  linkingStep2Hint: 'Pick the product from the list in the yellow column, or pick "ignore" for something that is not a product (freight, a fee, a deposit). Leave a row blank to skip it. Check the guesses. You do not have to finish: upload what you have and download again later.',
  linkingStep3: '3. Upload it and check',
  linkingStep3Hint: 'Nothing changes on upload: you see what each row would do first, then press Apply.',
  linkingUpload: 'Upload the filled sheet',
  linkingReading: 'Reading the sheet…',
  linkingPreviewTitle: 'What it would do',
  linkingNothingFilled: 'No row has a product filled in.',
  linkingRowCol: 'Row',
  linkingNameCol: 'Name',
  linkingProductCol: 'Product',
  linkingDoesCol: 'Does',
  linkingWhyCol: 'Why',
  linkKindCode: 'Vendor code',
  linkKindQbo: 'QuickBooks product',
  linkKindName: 'Sales name',
  linkKindBox: 'Box content',
  linkActSet: 'Set the box',
  linkingIgnoredTitle: 'Marked as not a product ({n})',
  linkingIgnoredHint: 'QuickBooks products and sales names you said are not products. Ask again brings one back on the next sheet. Hidden vendor codes come back on Vendor codes → Hidden.',
  linkingAskAgain: 'Ask again',
  linkActLink: 'Link',
  linkActGuess: 'Guess, not checked',
  linkActIgnore: 'Not a product',
  linkActDone: 'Already done',
  linkActProblem: 'Needs a fix',
  linkingApply: 'Apply {n} rows',
  linkingApplying: 'Applying {n} of {total}…',
  linkingGuessesChecked: 'Also link the {n} guesses left as the app wrote them: I checked them',
  linkingNotTried: 'Not tried: upload the same sheet again to finish',
  linkingProblemsNote: 'Rows that need a fix are left out. Fix them in the sheet and upload it again: what is done already is passed over.',
  linkingDoneTitle: 'Applied',
  linkingDoneSummary: '{ok} done, {failed} not done.',
  linkingFailedTitle: 'Not done',
  linkingAfter: 'Linked codes bring their past prices into the cost history, and linked QuickBooks products and names bring their sales into the margins. See it on',
  linkingOneAtATime: 'One at a time instead:',
  gapLinkSheet: 'Link them all in Excel',
  cloverTitle: 'Register (Clover)',
  cloverHint: "Which of Clover's categories the app keeps. Only fruits, vegetables and wholesale items matter here: a register line in a category left out (a soda, a barcoded snack) is taken out and never pulled again. An item since deleted in Clover is always kept.",
  cloverNotConnected: 'Clover is not connected (CLOVER_API_TOKEN and CLOVER_MERCHANT_ID, see the runbook).',
  cloverAll: 'Every category comes in today.',
  cloverFiltered: '{n} categories kept.',
  cloverLastPull: 'last pull',
  cloverKeep: 'Keep',
  cloverCategory: 'Clover category',
  cloverItems: 'Items',
  cloverSold90: 'Sold, 90 days',
  cloverNoCategory: 'Items in no category',
  cloverNew: 'new in Clover: kept until you choose',
  cloverKeptSold: 'Kept: {kept} of {all} sold at the register in the last 90 days.',
  cloverSave: 'Keep only these',
  cloverKeepAll: 'Keep everything again',
  cloverAsk: 'Register lines outside the ticked categories are taken out of the app now and not pulled again. Go on?',
  cloverSaved: 'Saved: {n} register lines outside them were taken out.',
  cloverSavedAgain: 'Saved: {n} register lines taken out; what is brought back is read again over the next hourly pulls.',
  cloverOwnerOnly: 'Only the owner changes this.',
  gapFillBoxes: 'Fill what every box holds in Excel (Linking sheet → Box content)',
  gapWhy: 'Why no cost',
  gapNoPrice: 'No price on file for the days it sold: link its vendor code (Products → Vendor codes) or record a price.',
  gapNoContent: "Sold by the pound or each, but the box content is empty: open the product and fill what one box holds.",
  linesCol: 'Lines',
  rankCol: '#',
  reasonCol: 'Reason',
  countCol: 'Count',
  qtyCol: 'Qty',
  priceCol: 'Price',
  flagCol: 'Flag',
  packsCol: 'Packs',
  salesCol: 'Sales',
  ordersHint: 'Every order for the delivery date with its status, channel and value. Enter opens the order; the date arrows move a day.',
  unpricedCol: 'Unpriced',
  ordersCol: 'Orders',
  orderValue: 'Order value',
  createdCol: 'Entered',
  findOrder: 'Find a customer or order no.…',
  todayStrip: 'Today',
  likelihoodCol: 'Likelihood',
  usualItemsCol: 'Usual items',
  typicalCol: 'Typical order',
  shelfLifeCol: 'Shelf life',
  ageCol: 'Age (days)',
  lotCol: 'Lot',
  pastDueCustomers: 'Past due ({n} customers)',
  unpricedLinesCol: 'Unpriced lines',
  pastDueCustomer: 'Past due ({n} customer)',
  ledgerTitle: 'Ledger and opening balances',
  ledgerHint: 'The app keeps its own receivables from the ledger start on. Type what each customer owed on that day (from QuickBooks: Reports, A/R aging summary, as of the day before the start). A balance below zero is money they had paid ahead. Payments after the start settle the opening balance first. Every change is kept: a correction is a new entry, never an edit.',
  ledgerStart: 'Ledger start',
  setStart: 'Set the start',
  openingCol: 'Opening balance',
  balanceTodayCol: 'Balance today',
  dueDateCol: 'Oldest due',
  noteCol: 'Note',
  entriesCol: 'Entries',
  saveOpeningN: 'Save {n} opening balance(s)',
  withOpening: 'With an opening balance',
  openingTotal: 'Opening total',
  openingUnsaved: 'Opening balances are typed but not saved. Leave anyway?',
  openingSaved: 'Opening balances saved',
  notADate: 'not a date (YYYY-MM-DD, on or before the start)',
  moveStartConfirm: 'Move the ledger start to {date}? Invoices and payments before it stop counting toward balances.',
  openingLine: 'Opening balance on {date}',
  sales90Col: 'Sales 90 d',
  gm30Col: 'GM % 30 d',
  lastInvoiceCol: 'Last invoice',
  pastDueCol: 'Past due',
  daysLateCol: 'Days late',
  customersCol: 'Customers',
  activeOnly: 'Active only',
  invalidPrices: 'not a price',
  useEngineAll: "Use the engine's prices for all",
  published: 'Published',
  pricesPublished: 'prices published',
  nothingChanged: 'Nothing changed',
  lastPublished: 'Last published',
  costBefore: 'before',
  costWas: 'was {n}',
  priceAuto: 'auto',
  priceHand: 'hand',
  priceHeld: 'held: {n}',
  followsCol: 'Follows',
  followsHint: 'Auto: the price follows cost by itself (the engine price, every hour and when an order is placed). Hand: your price, kept until you change it',
  priceAutoShort: 'follows cost',
  priceHandShort: 'my price, kept',
  fillHeldNote: '{n} prices whose cost moved more than the hold were left as they are: look at them (marked held) and publish them yourself.',
  priceByAuto: 'auto (followed cost)',
  phBy: 'By',
  costsPricesTitle: 'Costs and prices',
  costsPricesHint: "A vendor's prices (Mata's first) against USDA and the other vendors, our cost, our prices and the margins, for the period. Click a product for its weeks.",
  cpVendor: 'Vendor',
  cpWeeksTitle: 'Sales and gross profit by week',
  cpOpenHint: "Click a product to chart the vendor's price against USDA by week.",
  cpVendorMove: '{vendor}: price change in the period',
  cpNProducts: '{n} products',
  cpVsUsda: '{vendor} against USDA',
  cpNoUsda: 'No product linked to USDA yet (the product page, USDA)',
  cpUnder: 'Under cost · under the floor',
  cpUnderSub: 'tier prices of what sold',
  cpModes: 'Prices auto · hand',
  cpChanges: '{n} price changes in the period',
  cpItemTitle: '{item}: {vendor} against USDA',
  cpUsdaMid: 'USDA (middle)',
  cpOurPrice: 'Our price',
  cpItemTable: 'The weeks',
  cpWeek: 'Week of',
  cpSold: 'Sold',
  cpWas: 'was {price}',
  cpChange: 'Change',
  cpBestOther: 'Cheapest other',
  cpVsUsdaCol: 'vs USDA',
  priceChangesTitle: 'Price changes',
  priceChangesHint: 'Every tier price that moved in the period, all products, newest first: the price before, the price now, the cost it was set on, and who set it (auto when it followed cost by itself).',
  priceChangesNone: 'No price moved in this period.',
  priceChangesTruncated: 'The first 5,000 changes are shown: pick a shorter period to see the rest.',
  priceBeforeCol: 'Before',
  priceNowCol: 'Now',
  costFromCol: 'Cost from',
  costDaysOld: '{n} days old',
  costSrcList: 'price list',
  costSrcOrder: 'order',
  costSrcDock: 'dock',
  costSrcBill: 'bill',
  costSrcTyped: 'typed',
  costSrcOther: 'other',
  underFloor: 'under the floor',
  belowCost: 'below cost',
  approveFloorPrices: 'Publish anyway (under the floor)',
  priceList: 'Price list',
  priceListHint: 'Choose a tier or a customer; then print, download the spreadsheet, or copy as text.',
  forTier: 'Tier',
  forCustomer: 'Customer',
  download: 'Download',
  copyText: 'Copy as text',
  pricesFrom: 'Prices from',
  perPack: 'per pack',
  // payables and cash on the route
  payablesTitle: 'Accounts payable',
  payablesHint: 'Every vendor invoice on file with a balance, oldest due first. Pay from the row; a mistake is reversed, never edited.',
  openBills: 'Open bills',
  dueThisWeek: 'Due this week',
  noBills: 'No open bills',
  recentPayments: 'Recent payments',
  reversed: 'reversed',
  pay: 'Pay',
  collected: 'Collected',
  checkNo: 'Check no.',
  cashOnRoute: 'Cash on the route',
  checks: 'Checks',
  turnedIn: 'Turned in',
  turnInCash: 'Cash turned in',
  countedAmount: 'Cash counted',
  notTurnedIn: 'Not turned in yet',
  // passkeys and the system screen
  signInWithPasskey: 'Use face or fingerprint',
  passkeyUnknown: 'This phone is not registered yet. Sign in another way, then add it under Change password.',
  passkeysTitle: 'Face or fingerprint sign-in',
  passkeysHint: 'Add this phone once; from then on the sign-in screen opens with its own lock, no code and no typing.',
  noPasskeys: 'No phone added yet',
  added: 'Added',
  forget: 'Forget',
  addThisPhone: 'Add this phone',
  passkeysUnsupported: 'This browser cannot do face or fingerprint sign-in. Use the phone itself.',
  systemTitle: 'System',
  systemHint: 'The app about itself: version, database, errors, who signs in, what is switched on. When a phone shows an error, look here first.',
  appVersion: 'Version',
  devBuild: 'development build',
  startedAt: 'Up since',
  database: 'Database',
  migrations: 'migrations',
  dbDown: 'not answering',
  people: 'People',
  withPassword: 'with a password',
  withPasskey: 'with a phone added',
  seenThisWeek: 'signed in this week',
  switches: 'Switched on',
  storage: 'Files',
  readerShort: 'Reader',
  textingShort: 'Texting',
  switchedOn: 'on',
  switchedOffState: 'off',
  errorsTitle: 'Errors',
  noErrors: 'No errors recorded',
  server: 'server',
  browser: 'phone',
  // traceability
  scan: 'Scan',
  scanHint: 'Point the camera at a lot label, or type the code, to open where it came from and who got it.',
  camera: 'Camera',
  cameraOff: 'The camera could not start',
  noCamera: 'This browser cannot scan. Type the code below.',
  open: 'Open',
  wentTo: 'Went to',
  notSoldYet: 'Not sold yet',
  cameFrom: 'Came from',
  labels: 'Labels',
  labelsHint: 'One label per lot with stock at this site: the code, a QR, the item, the date, the vendor. Print and stick on the pallet or the first case.',
  traceExport: 'Trace export',
  dateFrom: 'From',
  dateTo: 'To',
  traceExportHint: 'Every invoice line with the lot behind it, for the dates (and one lot). The spreadsheet a recall or an auditor asks for.',
  // the office layout
  navSell: 'Sales',
  navBuyStock: 'Purchasing',
  navCustomers: 'Customers',
  navWarehouse: 'Operations',
  navProducts: 'Products',
  navReports: 'Reports',
  navSettings: 'Administration',
  navDashboard: 'Dashboard',
  navSalesOrders: 'Sales orders',
  navReceivable: 'Accounts receivable',
  endOfDayTitle: 'End of day',
  worklistsTitle: 'Worklists',
  draftsToConfirm: 'Drafts to confirm',
  noDrafts: 'No drafts waiting',
  confirmAll: 'Confirm all',
  confirmAllAsk: 'Confirm all {n} drafts? Each is checked as if confirmed one by one: a customer on hold or over the limit stays a draft.',
  staleDrafts: 'drafts for a day already gone: open each to change the day or cancel it',
  confirmedCount: 'confirmed',
  collectionsTitle: 'Collections',
  collectionsHint: 'Past due after money on account, most first. Call, then write down what they promised: it shows kept or broken by itself.',
  nothingPastDue: 'Nobody is past due',
  lastCall: 'Last call',
  logCall: 'log a call',
  promiseCol: 'Promise to pay',
  addPromise: '+ Promise',
  promiseAmount: 'Promised amount',
  promiseBy: 'Promised by',
  promise_open: 'promised',
  promise_kept: 'kept',
  promise_broken: 'broken',
  complaintsTitle: 'Complaints',
  complaintsHint: 'Logged on the customer as a complaint; open ones first, resolved ones for 30 days.',
  noComplaints: 'No complaints open',
  markResolved: 'Resolved',
  resolved: 'resolved',
  noCostTitle: 'Selling without a cost',
  noCostHint: 'Products sold in the last 60 days with no cost on file: their margin is unknown. Add the cost or map the vendor code.',
  linesSold: 'lines',
  kind_complaint: 'Complaint',
  vendorClaimsTitle: 'Vendor claims',
  trucksNoBillTitle: 'Trucks with no bill',
  trucksNoBillHint: 'A bill is checked against what its trucks brought. The bill number typed at the dock matches it; otherwise pick the bill here (a weekly bill can cover several trucks).',
  itsBill: 'Its bill',
  noBillNear: 'No bill of that vendor near its day yet',
  linkBillConfirm: 'This truck came on bill {bill}?',
  billsAndTrucks: 'Bills and the trucks they cover',
  matchLinked: 'picked',
  matchNumber: 'by number',
  unlink: 'Unlink',
  receiptLines: 'lines',
  billNumber: 'Bill no.',
  dismissClaim: 'Dismiss',
  noBillYet: 'No bill yet',
  claimCreditedConfirm: 'Mark it credited? The vendor gave the credit (or it is on their credit memo). This is kept.',
  vendorClaimsHint: 'What vendors owe back: on each bill, cases charged that its truck did not bring (rejected or short) and a price above the order. Before the bill comes, the rejects and shorts of a truck show as what to watch it for. Mark each one credited when the vendor credits it, or dismiss it with a reason.',
  noVendorClaims: 'No claims open',
  claimWhy: 'Why',
  date: 'Date',
  qty: 'Qty',
  claim_reject: 'Rejected at the dock',
  claim_short: 'Short against the order',
  claim_billed: 'Charged, did not come',
  claim_price: 'Billed above the order',
  claimCredited: 'Credited',
  dismissWhy: 'Why dismiss it',
  printClaim: 'Print the claim',
  claimLetterTo: 'Claim to',
  claimLetterBody: 'Please credit our account for the following. Each line shows the day, the reason, the product and the amount.',
  monthEndTitle: 'Month-end',
  booksClosedTitle: 'Books closed',
  booksClosedHint: 'Once a month is reported, close it: no invoice, void, shorts, payment, reversal, vendor document, receipt or count dated on or before that day can change. The owner closes and reopens.',
  booksClosedThrough: 'The books are closed through',
  booksOpen: 'No month is closed yet.',
  closeThrough: 'Close through',
  closeBooks: 'Close the books',
  closeBooksConfirm: 'Close the books through {date}? Nothing dated on or before it will change until you reopen.',
  reopenBooks: 'Reopen',
  reopenBooksConfirm: 'Reopen the books? Closed months can change again.',
  inventoryValueTitle: 'Inventory value',
  asOfDay: 'At the end of',
  inventoryValueHint: 'What the stock was worth at the end of the day: each lot on hand at its landed cost (its bought cost, else the cost on file that day).',
  product: 'Product',
  value: 'Value',
  noCostShort: 'without a cost',
  downloadCsv: 'Spreadsheet (CSV)',
  statementsTitle: 'Statements',
  statementsHint: 'Every customer with a balance, one statement per page, to print or save as PDF.',
  printStatements: 'Print every statement',
  onAccountLine: 'Payments and credits on account',
  forTheBank: 'For the bank',
  nothingToDeposit: 'No cash or checks waiting for the bank',
  inDeposit: 'In the deposit',
  bankAccount: 'Bank account',
  makeDeposit: 'Make the deposit and print the slip',
  depositsMade: 'Deposits',
  deposit: 'Deposit',
  depositSlip: 'Deposit slip',
  receivedTotal: 'Received in all',
  variance: 'Over / short',
  stopsNotDelivered: 'stops not delivered',
  deliveredNotInvoiced: 'delivered orders not invoiced',
  dayClosed: 'Day closed',
  cashCounted: 'Cash counted',
  closeDay: 'Close the day',
  changedSinceClose: 'Changed since it was closed',
  navFinance: 'Finance',
  greetingLine: '{greeting}, {name}',
  sortBy: 'Sort',
  sortedAsc: 'sorted ascending',
  sortedDesc: 'sorted descending',
  orderNo: 'Order',
  // the order day and customer patterns
  orderDayTitle: 'Order planning',
  orderDayHint: 'What to buy for a load: a normal week of our own customers, a safety margin, the floor, every key account as a column, the other call-aheads, what is on hand. Type what each account ordered into its column (that is their order), type over the order column, then write the purchase orders and the invoices.',
  invoiceAccounts: 'Invoice the accounts',
  nothingToInvoice: 'Nothing to invoice for this load',
  noLines: 'no lines',
  orderDayToCheck: 'The order day\'s invoices, one per order: check quantities and prices, change what a store called about, then Create invoices. Nothing is invoiced until then.',
  orderDaySkipped: 'Not here',
  fromOrderNo: 'Order #{no}',
  backToOrderDay: 'Back to the order day',
  orderLockedNote: 'already picked: it is invoiced as it is; change the invoice afterwards if something changed',
  keyMoreHint: 'Includes {n} more from the stores: another order of theirs in this load, or their standing order on a day with no order yet',
  preview: 'Preview',
  takeAnyway: 'Owner: take it even on hold or over the limit',
  alreadyMade: 'Those invoices were already made before the answer got lost. Check Invoices before trying again.',
  invoicesWritten: 'Invoices',
  accountSaved: 'Order saved',
  load: 'Load',
  lands: 'Lands',
  sells: 'sells',
  itemCol: 'Item',
  invShortsTitle: 'Shorts',
  invShortsHint: 'Type what each line really delivered. The totals, the stock and the order follow; every change is kept below.',
  shortsRecord: 'Record shorts',
  shortsOnInvoice: 'On the invoice',
  shortsDelivered: 'Delivered',
  shortsBad: 'A delivered quantity is a number from 0 up to what the invoice says (more goes on a new invoice).',
  shortsNote: 'Why (optional)',
  shortsNotePlaceholder: 'e.g. 2 cases short from Mata\'s, driver brought 1 back',
  shortsNewTotal: '{n} line(s) change; the new total is {total}.',
  shortsSave: 'Save shorts',
  shortsConfirmCredits: 'These are other cases, not the ones already credited',
  shortLine: 'ordered {ordered}, short {short}',
  base: 'Base',
  baseHint: 'A normal sell window of our own customers (not the key accounts), last 8 weeks',
  safety: 'Safety',
  safetyHint: '1.65 × the window-to-window swing',
  keep: 'Keep',
  accountsTotal: 'Accounts',
  othersCallAhead: 'Others',
  orderCol: 'Order',
  orderIn: 'order in',
  expected: 'expected',
  noOrderYet: 'no order yet',
  legendOrder: 'a real order',
  legendStanding: 'the standing order',
  legendPattern: 'what the pattern predicts',
  writePurchaseOrders: 'Write the purchase orders',
  patternTitle: 'Pattern',
  patternOrdersOn: 'Orders on',
  patternNoDays: 'No usual days yet',
  patternPerWeek: 'orders a week',
  usuallyTakes: 'Usually takes',
  typicalOrder: 'Typical order',
  nextExpected: 'Next expected',
  pattern_new: 'New',
  pattern_ok: 'On pattern',
  pattern_due: 'Due today',
  pattern_overdue: 'Overdue',
  pattern_quiet: 'Gone quiet',
  keyAccount: 'Key account (a column on the order day)',
  columnOrder: 'Column order',
  expectedToday: 'Regulars who usually order today, not in yet',
  // PACA (decision 57)
  pacaTitle: 'PACA and letterhead',
  pacaHint: 'The license number prints on every invoice. Late balances accrue interest and, with the clause on, collection costs and attorney fees: courts have awarded both under the PACA trust when the invoice terms say so. The letter asks for payment in 10 days; that is our choice, not a legal deadline.',
  pacaLicense: 'PACA license number',
  pacaLicenseShort: 'license',
  pacaInterest: 'Interest on late balances, % a year',
  pacaFees: 'Claim collection costs and attorney fees',
  pacaClausePreview: 'On the invoice',
  pacaTermsRule: 'PACA: terms of 30 days at most, and only by written agreement. Beyond 30 days the trust protection is lost.',
  termsDaysLabel: 'Terms (days, 30 max)',
  termsAgreedOn: 'Written terms signed on',
  pacaLetter: 'PACA demand letter',
  demandTo: 'To',
  demandBody1: 'The invoices below are past due. Under the Perishable Agricultural Commodities Act the produce, everything made from it and the money from its sale are held in trust for Morales Produce until paid in full. Pay the total by {payBy} to avoid a formal complaint to the USDA and the costs that follow.',
  demandBody2: 'This letter is also a written notice of intent to preserve trust benefits under 7 U.S.C. 499e(c)(3) for the invoices listed whose notice window is still open, in addition to the trust notice printed on each invoice under 7 U.S.C. 499e(c)(4).',
  nothingOpen: 'Nothing is open for this customer',
  noticeDaysLeft: '{n} days left to send the written notice',
  interest: 'interest',
  principal: 'Principal',
  lettersSent: 'Letters sent',
  recordLetterSent: 'Record it as sent today',
  letterRecorded: 'Recorded. The aging report shows the date.',
  letterDue: 'Letter due',
  letterSent: 'Letter sent',
  // signals (decision 55)
  qboImport: 'Import from QuickBooks',
  qboImportAll: 'Re-import everything',
  qboImportAllHint: 'Read every record from QuickBooks again, not only what changed. Use it if something looks missing; it takes a few minutes.',
  qboImporting: 'Importing… this can take a few minutes the first time',
  qboHistory: 'Copied from QuickBooks',
  qboHistoryHint: 'Everything in QuickBooks, copied into the app. Runs every night after 5 am; Import runs it now. Copying never changes anything in QuickBooks.',
  qboLastImport: 'Last import: {when}',
  qboImportFailed: 'The last import failed: {why}',
  qboNothingYet: 'Nothing imported yet.',
  qboTitle: 'QuickBooks',
  qboMatchTitle: 'QuickBooks matching',
  qboMatchHint: 'Which QuickBooks customer, vendor and product is which in the app. The import links exact names by itself; link the rest here, or make them in the app. Nothing is written to QuickBooks.',
  qboMatchTab: 'Matching',
  qboBalancesTab: 'Balances',
  qboMatchNotConnected: 'QuickBooks is not connected. Connect it and run Import first:',
  qboEntity_Customer: 'Customers',
  qboEntity_Vendor: 'Vendors',
  qboEntity_Item: 'Products',
  qboLinkedOf: '{n} of {of} linked',
  qboAppOnly: '{n} in the app only',
  qboMatchRun: 'Match by name now',
  qboMatchRan: 'Linked {c} customers, {v} vendors, {i} products.',
  qboMatchKind: 'Kind',
  qboAllLinked: 'Everything of this kind is linked.',
  qboCopyTitle: 'QuickBooks is the list',
  qboCopyHintCustomers: 'Makes the app\'s customers exactly QuickBooks\': each one linked under QuickBooks\' name, the missing ones made, the app\'s others put away (history kept, nothing deleted). Their email, phone and address come too where the app has none. One with an order to deliver, money between us, a key account or portal sign-ins stays, and is said. Preview first.',
  qboCopyHintProducts: 'Makes the app\'s products exactly QuickBooks\' (not the "- Count" and "- Weight" ones, which stay as they are): paired by the links made, the same name or the same words, renamed to QuickBooks\' name, the missing ones made, the app\'s others put away unless still in use (stock, an order, a purchase order, a standing order, register sales). Check the pairs in the preview: link a wrong one by hand below first.',
  qboCopyPreview: 'Preview',
  qboCopyApply: 'Apply',
  qboCopyConfirm: 'Make the app\'s list QuickBooks\' now, as in the preview?',
  qboCopyWould: 'Of {n} in QuickBooks, this is what Apply does:',
  qboCopyDone: 'Done: the app has QuickBooks\' list ({n} in QuickBooks).',
  qboCopy_linked: 'Linked (the same name or words)',
  qboCopy_renamed: 'Renamed to QuickBooks\' name',
  qboCopy_made: 'Made from QuickBooks',
  qboCopy_restored: 'Brought back (active in QuickBooks)',
  qboCopy_archived: 'Put away',
  qboCopy_kept: 'Kept, and why',
  qboCopy_filled: 'Copied from QuickBooks (only where the app had nothing)',
  qboCopyMirrorNote: 'Customers linked now bring their QuickBooks invoices in on the money mirror\'s next hourly run, and those take cases off the shelf. Open Administration → QuickBooks and press the mirror\'s Preview before that run.',
  qboCopyQuiet: 'Run it when nobody is invoicing: the preview holds the list for the seconds it takes.',
  qboWhy_same_name: 'same name',
  qboWhy_same_words: 'same words',
  qboWhy_inactive_qbo: 'inactive in QuickBooks',
  qboWhy_not_in_qbo: 'not in QuickBooks',
  qboWhy_per_unit_lb: 'sold by the pound out of this box',
  qboWhy_per_unit_ea: 'sold by the piece out of this box',
  qboWhy_per_unit_dz: 'sold by the dozen out of this box',
  qboWhy_per_unit_bn: 'sold by the bunch out of this box',
  qboHold_no_product: 'not linked: no one product in the app matches this name; link it by hand below',
  qboHold_box_unknown: 'not linked yet: set what one box holds on the product, then Apply again',
  qboHold_store: 'kept: a store of another company, billed through the app',
  qboHold_key_account: 'kept: a key account (its column on the order day)',
  qboHold_portal: 'kept: {n} portal sign-in(s)',
  qboHold_tier: 'kept: it has its own price tier',
  qboHold_orders: 'kept: on {n} open order(s)',
  qboHold_standing: 'kept: a standing order uses it',
  qboHold_owes: 'kept: money still between us',
  qboHold_on_hand: 'kept: {n} on hand',
  qboHold_purchase: 'kept: on {n} open purchase order line(s)',
  qboHold_register: 'kept: sold at the register lately',
  qboHold_qbo_deleted: 'kept: linked to a QuickBooks customer deleted there',
  qboCopyMaybe: 'maybe QuickBooks\' "{name}": link it on Matching, then Apply again',
  qboField_email: 'email',
  qboField_phone: 'phone',
  qboField_address: 'address',
  switchMatchTitle: 'Ready for the switch?',
  switchMatchReady: 'Ready: every QuickBooks customer with a balance or activity in the last year is linked.',
  switchMatchNotReady: 'Not ready: {c} QuickBooks customers with a balance or activity in the last year are not linked. The switch day waits for them (QuickBooks is the list, above, does it in one step).',
  switchMatchHint: 'Only what the switch needs: customers with a balance or a sale, payment or credit since {since}, and products sold since then. Link each to the app\'s; a customer can also be made in the app, and a fee marked "Not a product".',
  switchMatchCustomers: 'Customers to link',
  switchMatchProducts: 'Products not linked (not needed for the switch)',
  switchMatchVendors: 'Vendors (not needed for the switch)',
  switchMatchNew: 'New in QuickBooks',
  switchMatchCustomerDetail: '{n} sales, payments or credits · last {last}',
  switchMatchProductDetail: '{amount} sold in {n} lines · last {last}',
  switchMatchVendorDetail: '{n} bills or payments · last {last}',
  switchMatchVendorsHint: 'Bills stay in QuickBooks. Linked, a vendor\'s bills show by vendor in the app; this does not hold the switch back.',
  switchMatchNewHint: 'App customers and products not linked to QuickBooks. The ones marked first would be refused by QuickBooks on their first invoice after the switch: link them below now. The rest are made new in QuickBooks then; if QuickBooks has one under another name, link it below instead. Put away the practice customers.',
  switchMatchOwesOnly: 'owes, no sale or payment in the last year',
  switchMatchWhy_new: 'made new in QuickBooks',
  switchMatchWhy_inactive: 'QuickBooks has it, but inactive: make it active there, or link another',
  switchMatchWhy_taken: 'QuickBooks has the name, linked to another app record: link the right one',
  switchMatchWhy_other_kind: 'QuickBooks uses this name for a vendor or an employee: a customer of the same name is refused; link it to its QuickBooks customer, or rename it',
  switchMatchWhy_not_sellable: 'QuickBooks has the name as a bundle or group, not a product to sell: link it to its QuickBooks product',
  switchMatchNewCustomers: 'Customers',
  switchMatchNewProducts: 'Products',
  switchMatchNone: 'Nothing here.',
  switchMatchCapped: 'Showing the first {n}.',
  notAProduct: 'Not a product',
  notAProductConfirm: '"{name}" is not a product (a fee, freight, a deposit)? It is not asked about again, here or on the linking sheet.',
  qboUnlinkedCaption: 'Not linked yet: active ones first, then the biggest balances',
  qboInQuickBooksCol: 'In QuickBooks',
  qboBalanceCol: 'Owes in QuickBooks',
  qboAppRecordCol: 'In the app',
  qboPickApp: 'Type to pick the app record',
  qboInactive: 'inactive',
  qboLink: 'Link',
  qboMake: 'Make in the app',
  qboMakeHint: 'Makes the app record with the QuickBooks name and terms (a customer\'s at most 30 days), linked to it',
  qboBalancesHint: 'What each linked customer owes in QuickBooks beside what the app says (the app counts from {date}). The biggest differences first.',
  qboBalancesNoLedger: 'The app has no receivables start date yet, so only QuickBooks\' side shows. Set it on Ledger and opening balances.',
  qboOwedInQuickBooks: 'Owed in QuickBooks',
  qboOwedInApp: 'Owed in the app',
  qboDiffer: 'Customers that differ',
  qboBalancesCaption: 'Linked customers',
  qboAppCol: 'App',
  qboDiffCol: 'Difference',
  qboBalancesNone: 'No linked customer owes anything on either side.',
  qboUnlinkedOwing: 'Owed by customers not linked yet',
  qboUnlink: 'Unlink',
  qboUnlinkConfirm: 'Unlink {name}? It goes back to the Not linked list, and matching by name will not link that pair again.',
  qboOpenMatching: 'Matching and balances',
  standingHeld: 'Not drafted, on hold or over their credit limit: {names}. Take their order by hand if they should get one.',
  typedNotNumber: 'Not a number we can read: type it like 120 or 1,250.50',
  qboBalancesSource: 'What QuickBooks says',
  qboBalancesFromImport: 'Last import',
  qboBalancesFromReport: 'A/R aging report, now',
  qboReportTotal: 'QuickBooks\' A/R aging report, read {when}: {total} owed in all (linked and not).',
  qboReportOff: '{amount} of it is in none of the rows below: a QuickBooks customer the import has not copied yet, or a report row with no customer. Import, then read it again.',
  qboBalancesAsOf: 'QuickBooks as of the last import, {when}; the app is live, so anything entered in QuickBooks since shows as a difference until the next import.',
  qboCapped: 'Only the first {n} are shown; the rest come up as these are linked.',
  qboShowWhich: 'Show',
  qboNotLinked: 'Not linked',
  qboLinkedList: 'Linked',
  qboNoneLinked: 'Nothing of this kind is linked yet.',
  qboFindEither: 'Part of either name',
  qboHowCol: 'How',
  qboHow_key: 'by the QuickBooks id',
  qboHow_name: 'by name',
  qboHow_alias: 'by another name',
  qboHow_manual: 'by hand',
  qboHow_created: 'made from QuickBooks',
  qboHow_pushed: 'made in QuickBooks by the app',
  qboArchived: 'archived',
  qboOutsideLinks: 'Owed outside the links',
  qboFind: 'Find',
  qboFindPlaceholder: 'Part of the QuickBooks name',
  qboShowMore: 'Showing {n} of {of}: show more',
  qboHint: 'Connect the company once. The app reads QuickBooks (customers, items, invoices, payments, bills, the P&L) to import history and keep reports in step. It writes to QuickBooks only the invoices made in the app, once the owner turns that on below.',
  qboNotSetTitle: 'The app keys are not set',
  qboNotSet: 'QBO_CLIENT_ID and QBO_CLIENT_SECRET are not on the server yet. Add them under GitHub → Settings → Secrets and variables → Actions, then run Deploy → deploy.',
  qboConnectTitle: 'Connect QuickBooks',
  qboConnectHint: 'You go to Intuit, sign in, pick the company and approve. Intuit sends you back here.',
  qboEnv_production: 'Real books',
  qboEnv_sandbox: 'Intuit test company',
  qboEnvHint_production: 'Your company. Needs the Production keys from Intuit.',
  qboEnvHint_sandbox: 'The empty sandbox company the Development keys reach, to try the connection.',
  qboConnect: 'Connect to QuickBooks',
  qboRedirectHint: 'Intuit must list this address under the app’s Redirect URIs:',
  qboConnection: 'Connected',
  qboCompany: 'Company',
  qboBooks: 'Books',
  qboConnectedOn: 'Connected',
  qboGoodUntil: 'Good until',
  qboGoodUntilHint: 'Intuit ends a connection that is not used for 100 days; each use extends it.',
  qboCheck: 'Check connection',
  qboDisconnect: 'Disconnect',
  qboDisconnectConfirm: 'Disconnect QuickBooks? The app stops reading it until you connect again.',
  qboInQuickBooks: 'In QuickBooks',
  qboCheckHint: 'What the import will bring in. Nothing was copied or changed.',
  qboCount_customers: 'Customers',
  qboCount_vendors: 'Vendors',
  qboCount_items: 'Products and services',
  qboCount_invoices: 'Invoices',
  qboCount_payments: 'Payments',
  qboCount_bills: 'Bills',
  qboCount_accounts: 'Accounts',
  qboKind_Term: 'Terms',
  qboKind_Account: 'Accounts',
  qboKind_Customer: 'Customers',
  qboKind_Vendor: 'Vendors',
  qboKind_Item: 'Products and services',
  qboKind_Invoice: 'Invoices',
  qboKind_CreditMemo: 'Credit memos',
  qboKind_SalesReceipt: 'Sales receipts',
  qboKind_RefundReceipt: 'Refund receipts',
  qboKind_Payment: 'Payments',
  qboKind_Bill: 'Bills',
  qboKind_BillPayment: 'Bill payments',
  qboKind_VendorCredit: 'Vendor credits',
  qboKind_Purchase: 'Expenses and checks',
  qboKind_Deposit: 'Deposits',
  qboKind_Transfer: 'Transfers',
  qboKind_JournalEntry: 'Journal entries',
  qboReconnectNeeded: 'QuickBooks refused the connection: connect again to keep importing.',
  qboReconnect: 'Connect again',
  qboOldestInvoice: 'Oldest invoice: {date}',
  qboConnectedNow: 'QuickBooks is connected.',
  qboError_declined: 'The connection was not approved at Intuit.',
  qboError_intuit: 'Intuit returned an error. Try again.',
  qboError_state: 'That sign-in link expired or was not started here. Press Connect again.',
  qboError_signin: 'Sign in as the owner in this browser, then press Connect again.',
  qboError_exchange: 'Intuit did not accept the sign-in. Check that the redirect address is saved at Intuit, then try again.',
  qboError_production: 'Intuit refused the real books with these keys. Development keys only reach the test company: choose “Intuit test company”, or finish the Production keys at Intuit.',
  qboError_company: 'Connected at Intuit, but the company could not be read. Try again.',
  dismiss: 'Dismiss',
  privacyTitle: 'Privacy policy',
  termsTitle: 'Terms of use',
  legalUpdated: 'Morales Produce · updated October 2026',
  legalBack: 'Back to the app',
  privacy1: 'This app is the private operations system of Morales Produce, a wholesale produce company in Bernalillo, New Mexico. It is used by the company’s staff and by customers the company invites to order online.',
  privacy2: 'What it keeps: the business records needed to run the company — customers and their contacts, items, prices, orders, invoices, payments, vendors, bills, stock and deliveries — and the names, phone numbers and sign-in details of the people who use it.',
  privacy3: 'QuickBooks: when the owner connects QuickBooks Online, the app reads the company’s accounting records (customers, vendors, items, invoices, payments, bills, accounts and reports) to keep its own records and reports in step. The connection tokens are stored encrypted. The owner can disconnect at any time from the app or from QuickBooks, and the app then deletes the tokens.',
  privacy4: 'How it is used: only to run Morales Produce’s business. The data is not sold, rented or shared with anyone for advertising. Service providers that host the app (DigitalOcean), send texts (Twilio) or read documents on request (Anthropic) receive only what each task needs.',
  privacy5: 'Where it is kept: on servers in the United States, in a database reachable only by the app, with daily backups that are encrypted and tested.',
  privacy6: 'Questions or a request to see or remove your information: contact the Morales Produce office.',
  terms1: 'This app is provided by Morales Produce for its own staff and invited customers. Using it means agreeing to these terms.',
  terms2: 'Accounts are personal: keep your password and sign-in codes to yourself. The company may suspend an account that is misused.',
  terms3: 'Prices, orders and invoices shown in the app are the company’s business records; an order placed through the app is a request until the company confirms it.',
  terms4: 'The app connects to QuickBooks Online only with the owner’s approval and can be disconnected at any time.',
  terms5: 'The app is provided as is. Morales Produce works to keep it available and correct but is not liable for interruptions or for losses from its use beyond what the law requires. Questions: contact the Morales Produce office.',
  practiceTitle: 'Practice only.',
  practiceSales: 'Until the switch, QuickBooks is the real record: enter real invoices, payments and credits in QuickBooks. What is entered here is a TEST: it counts in no balance, report or stock, the customer never sees it, and the switch day deletes it.',
  testTag: 'TEST',
  testStamp: 'TEST: not a real invoice',
  practiceBills: 'Until the switch, enter real bills and bill payments in QuickBooks. Bills entered here are practice; their costs still count for prices.',
  salesTitle: 'Sales performance',
  pointsUnit: 'pts',
  otherCategories: 'Other',
  vsYearAgo: 'vs a year ago',
  kpiInvoices: 'Invoices',
  salesHint: 'The period against the one just before it and the same days a year ago: revenue, gross profit, invoices and customers, the trend, the mix and every customer and item. Revenue is what was invoiced, before credits; lines without an item count in the totals, not in By item.',
  noSalesInPeriod: 'No invoices in this period or the one before.',
  grossProfit: 'Gross profit',
  avgInvoice: 'Average invoice',
  activeCustomers: 'Active customers',
  vsPrior: 'vs prior period',
  findCustomer: 'Find a customer…',
  search: 'Search',
  priorPeriod: 'Prior period',
  changeCol: 'Change',
  changePctCol: 'Change %',
  qtySold: 'Qty sold',
  nRows: '{n} rows',
  uncategorized: 'No category',
  lastYear: 'Last year',
  thisPeriod: 'This period',
  weekOf: 'Week of {date}',
  nDaysInPeriod: '{n} days in the period',
  grainLabel: 'Group by',
  grain_day: 'Day',
  grain_week: 'Week',
  grain_month: 'Month',
  per_day: 'day',
  per_week: 'week',
  per_month: 'month',
  revenueBy: 'Revenue by {per}',
  gmBy: 'Gross margin % by {per}',
  salesMix: 'Sales mix by category',
  topCustomers: 'Top customers',
  topItems: 'Top items',
  partialNote: 'Lighter columns are partial periods',
  salesByItem: 'By item',
  salesByCustomer: 'By customer',
  arByAge: 'Receivables by age',
  salesLast12: 'Revenue, last 12 weeks',
  mixLast12: 'Sales mix, last 12 weeks',
  topCustomers12: 'Top customers, last 12 weeks',
  salesOpen: 'Open sales performance',
  age_current: 'Current',
  age_d1to30: '1–30 days',
  age_d31to60: '31–60 days',
  age_d61to90: '61–90 days',
  age_d90plus: 'Over 90 days',
  signalsTitle: 'Performance summary',
  revenueWeek: 'Sold this week',
  vsLastYear: 'Same week last year',
  ordersWeek: 'Invoices this week',
  dso: 'Days to get paid',
  costKnownFor: 'cost known for',
  errors24h: 'Errors in 24 hours',
  forecastScoreTitle: 'Forecast accuracy',
  forecastNotScored: 'Not scored yet: the scorer runs every morning once there are sales to compare.',
  forecastWeekdayAvg: 'Same weekday, 12-week average',
  forecastLastWeek: 'Plain last week',
  forecastScoreHint: 'Over the last {days} days: 100% means every day sold exactly what the forecast said; the error is weighted by what sold. The items below are the ones it missed most (+ over-forecast, − under).',
  forecastSaid: 'Forecast',
  soldWas: 'sold',
  jobsTitle: 'Scheduled jobs',
  jobsRunAt: 'every morning after {hour}:00',
  jobsHint: 'What the app does on its own clock. Each run is kept; a failed run shows why. Run now if a morning was missed.',
  job_calendar: 'Calendar of demand days',
  'job_qbo-pull': 'QuickBooks import',
  'job_daily-signals': 'Daily signals per item',
  'job_forecast-score': 'Forecast score',
  'job_line-costs': 'Costs on invoice lines',
  job_weather: 'Weather forecast',
  'job_clover-pull': 'Clover register sales',
  job_usda: 'USDA market prices',
  'job_gmail-pull': 'Vendor papers from Gmail',
  'job_prices-follow-cost': 'Auto prices follow cost',
  jobOk: 'ran',
  jobIdle: 'Not set up',
  jobLate: 'Late',
  jobFailed: 'failed',
  jobNeverRan: 'never ran',
  jobRows: 'rows',
  runNow: 'Run now',
  // the driver companion (decision 53)
  signature: 'Signature',
  signHere: 'Sign with a finger',
  signed: 'Signed',
  clearSignature: 'Clear',
  deliveryTitle: 'Delivery',
  deliveryHint: 'Where the truck goes and what the driver needs to know. Shows on the route.',
  addressLine1: 'Street',
  addressLine2: 'Suite, unit, gate',
  city: 'City',
  stateCode: 'State',
  zip: 'ZIP',
  windowStart: 'Window from',
  windowEnd: 'Window to',
  deliveryNotes: 'Driver instructions (back door, ask for Maria, cooler on the left)',
  nobodyWaiting: 'Everyone expected today has ordered',
  // the invoice as a document (one layout for the screen, the paper, the emailed link and the portal)
  shipTo: 'Ship to',
  descriptionCol: 'Description',
  invoiceNoLabel: 'Invoice no.',
  notesLabel: 'Notes',
  stampPaid: 'Paid',
  stampVoid: 'Void',
  letterheadTitle: 'Letterhead on invoices',
  letterheadHint: 'Your company name, address, phone and email as they print at the top of every invoice. Leave a line blank to leave it off.',
  companyNameLabel: 'Company name',
  addressLine1Label: 'Address',
  addressLine2Label: 'Address, line 2',
  stateLabel: 'State',
  saveLetterhead: 'Save letterhead',
  letterheadSaved: 'Saved. Every invoice prints with it.',
  pacaOffUntilLicense: 'Nothing about PACA prints on invoices (the trust notice, the interest and fee clause, the license) until the license number is filled in.',
  invoicesCapped: 'Showing the newest {n} invoices of these dates: pick a shorter range to see older ones.',
  letterheadMissing: 'Your address is not on invoices yet: add it in Settings, PACA and letterhead.',
} as const;

export type WebMessageKey = keyof typeof webEn;

const webEs: Record<WebMessageKey, string> = {
  greetingMorning: 'Buenos días',
  newInvoice: 'Crear factura',
  invoicesTitle: 'Facturas',
  saveNNumbers: 'Guardar {n} número(s)',
  orderDayUnsaved: 'Los números de las tiendas no están guardados. ¿Salir de todos modos?',
  lockedTyped: 'Los números escritos para {stores} no se pueden guardar aquí: el pedido ya avanzó. Cámbialo en la mesa de pedidos.',
  dropThem: 'Quitarlos',
  poWrittenShort: 'Pedidos hechos',
  storesCol: 'Tiendas',
  othersCol: 'Otros',
  nNotSaved: '{n} número(s) sin guardar',
  nBadNumbers: '{n} celda(s) en rojo no son números: corríjalas para guardar',
  allSaved: 'Todo guardado',
  onlyWithNumbers: 'Solo filas con números',
  saveFirst: 'Guarda primero los números de las tiendas',
  legendUnsaved: 'escrito, sin guardar',
  invoicesHint: 'Todas las facturas en las fechas que elijas. Haz clic en una fila y presiona Enter (o doble clic) para abrirla.',
  fromDate: 'Desde',
  toDate: 'Hasta',
  stateAll: 'Todas',
  stateOpen: 'Abierta',
  stateOverdue: 'Vencida',
  statePaid: 'Pagada',
  stateVoid: 'Anulada',
  colNo: 'Núm.',
  colStatus: 'Estado',
  colPaid: 'Pagado',
  colBalance: 'Saldo',
  findInvoice: 'Cliente o número de factura…',
  invoicesCountCol: 'Facturas',
  billed: 'Facturado',
  noInvoicesHere: 'Ninguna factura con estas fechas y filtros.',
  totalAllInvoices: 'Total de estas facturas',
  addInvoice: 'Agregar otra factura',
  createNInvoices: 'Crear {n} factura(s)',
  alreadyInvoicedOn: 'Este cliente ya tiene la factura {no} en esta fecha ({total}). Haz otra solo si es a propósito.',
  twoCardsSameDay: 'Hay dos facturas aquí para el mismo cliente y fecha: junta las líneas en una.',
  perDay: 'Por día',
  keepCol: 'Mínimo',
  suggestedCol: 'Sugerido',
  costCol: 'Costo',
  findItem: 'Buscar un artículo…',
  onlyToBuy: 'Solo artículos por comprar',
  xOfY: '{x} de {y} artículos',
  ordersByVendor: 'Pedidos por proveedor',
  linesCount: 'Líneas',
  unitCol: 'Unidad',
  tierCol: 'Nivel de precio',
  productService: 'Producto/servicio',
  pickProduct: 'Elige un producto',
  typeToSearch: 'Escribe para buscar',
  lineDescription: 'Descripción',
  rate: 'Precio',
  tierPrice: 'Precio del nivel',
  lastPaid: 'Último pagado',
  marginPct: 'Margen',
  dueOnReceipt: 'Al recibir',
  cameIn: 'Canal',
  invoiceLines: 'Líneas de la factura',
  pickCustomerFirst: 'Elige un cliente para empezar las líneas. Sus precios se llenan solos.',
  netDays: 'Neto {n}',
  qtyNeeded: 'Una línea tiene producto pero no cantidad: escribe las cajas (la celda roja).',
  changeCustomerClears: 'Cambiar el cliente borra las líneas. ¿Seguir?',
  clearAllLines: 'Borrar todas las líneas',
  pressAgainToClear: 'Presiona otra vez para borrar',
  addUsualItems: 'Agregar lo de siempre',
  removeLastLine: 'Quitar la última línea',
  messageOnInvoice: 'Mensaje en la factura',
  approveUnderFloor: 'Apruebo estos precios bajo el piso o bajo el costo',
  invoiceAnyway: 'Facturar de todos modos (pasando la retención o el límite de crédito)',
  saveAndNew: 'Guardar y nueva',
  saveAndClose: 'Guardar y cerrar',
  saveAndPrint: 'Guardar e imprimir',
  email: 'Correo',
  getsInvoicesLabel: 'Enviar facturas y estados de cuenta a este correo',
  getsInvoicesBadge: 'facturas',
  emailInvoice: 'Enviar la factura por correo',
  emailButton: 'Correo',
  emailCompose: 'Escribir el correo',
  emailPreviewTitle: 'Lo que recibe el cliente',
  emailSubject: 'Asunto',
  emailPayOff_square: 'Todavía sin botón de pago: pagar en línea (Square) está apagado hasta el cambio. Al encenderlo, el botón dice "Ver y pagar la factura".',
  emailPayOff_paid: 'Sin botón de pago: no se debe nada en esta factura.',
  emailPayOff_pending: 'Sin botón de pago: un pago de esta factura todavía se está procesando.',
  emailPayOff_test: 'Sin botón de pago: una factura de PRUEBA nunca se paga en línea.',
  emailNotePlaceholder: 'Una nota suya, si quiere (el correo ya dice que la factura está disponible)',
  emailStatement: 'Enviar el estado de cuenta por correo',
  emailNotSetUp: 'El correo aún no está configurado en el servidor.',
  emailSettingsTitle: 'Correo',
  lostSalesTitle: 'Ventas perdidas',
  lostSalesHint: 'Una venta que no se hizo y por qué. Regístrela cuando la oiga: lo que no teníamos, un precio que la perdió, un competidor. El reporte muestra qué tener más y dónde el precio pierde negocio.',
  lostSaleRecord: 'Registrar una venta perdida',
  lostSaleWhy: 'Por qué',
  lostReason_out_of_stock: 'No lo teníamos',
  lostReason_price: 'Precio',
  lostReason_quality: 'Calidad',
  lostReason_late: 'Tarde',
  lostReason_competitor: 'Se fue con la competencia',
  lostReason_not_carried: 'No lo manejamos',
  lostReason_other: 'Otro',
  lostSaleItem: 'Producto',
  lostSaleItemPlaceholder: 'Elija de la lista o escriba el nombre',
  lostSaleNotInList: 'No está en la lista: se guarda por su nombre',
  lostSaleCustomer: 'Cliente (si se sabe)',
  lostSaleCustomerPlaceholder: 'Elija un cliente o déjelo vacío',
  lostSaleUnknownCustomer: 'No es un cliente de la lista: elija uno o déjelo vacío',
  lostSaleQty: 'Cuántas',
  lostSaleDay: 'Cuándo',
  lostSalesUnvalued: '{n} no tienen cantidad o precio, así que no están en el valor.',
  lostSalesTruncated: 'Hay más registros de los que el reporte suma a la vez: los totales cubren los 5,000 más recientes. Elija un periodo más corto.',
  lostSaleQuoted: 'Precio que dimos',
  lostSaleTheirs: 'Precio que querían',
  lostSaleCompetitor: 'Competidor',
  lostSaleChannel: 'Cómo',
  lostChannel_phone: 'Teléfono',
  lostChannel_visit: 'Visita',
  lostChannel_text: 'Mensaje',
  lostChannel_whatsapp: 'WhatsApp',
  lostChannel_walkin: 'En persona',
  lostChannel_email: 'Correo',
  lostChannel_other: 'Otro',
  lostSaleNote: 'Nota',
  lostSaleBadNumber: 'Cantidades y precios son números (una cantidad mayor que 0).',
  lostSaleSave: 'Guardar venta perdida',
  lostSaleSaved: 'Guardado. Gracias: así aprendemos lo que nos falta.',
  lostSalesCount: 'Ventas perdidas',
  lostSalesValue: 'Lo que habría dejado',
  lostSalesNone: 'No hay ventas perdidas en este periodo.',
  lostSalesByItem: 'Los productos que más perdemos',
  lostSalesTimes: 'Veces',
  lostSalesEvery: 'Cada venta perdida',
  paymentsTitle: 'Pagos',
  paymentsHint: 'Los clientes pagan en línea la factura enviada por correo con Square (tarjeta o cuenta bancaria); el dinero se aplica a la factura cuando Square confirma que llegó.',
  squareServer: 'Square',
  squareConfigured: 'Square está configurado.',
  squareSandbox: 'Square está configurado con la cuenta de prueba de Square (sandbox): no se mueve dinero real.',
  squareNotSetUp: 'Square aún no está configurado en el servidor.',
  squareSetupHint: 'Claude le guía: una aplicación en el sitio de desarrolladores de Square, luego SQUARE_ACCESS_TOKEN, SQUARE_LOCATION_ID y SQUARE_WEBHOOK_SIGNATURE_KEY en los secretos de GitHub y Deploy → deploy.',
  squareWebhookUrl: 'Dirección del webhook (désela a Square)',
  squareWebhookHint: 'En el sitio de desarrolladores de Square: Webhooks → Add subscription, esta dirección, el evento payment.updated.',
  squarePayButton: 'El botón Pagar',
  squareLiveLabel: 'Encendido: las facturas por correo tienen un botón Pagar',
  squareLiveHint: 'Déjelo apagado hasta el cambio: hasta entonces las facturas reales salen de QuickBooks con su propio enlace de pago, y un pago aquí no llegaría a QuickBooks.',
  squareTestTitle: 'Pruébelo con $1',
  squareTestHint: 'Abre una página de Square de $1: páguela con su propia tarjeta, véala llegar abajo y luego reembólsela en Square. No se registra nada en los libros.',
  squareTestButton: 'Crear un enlace de prueba de $1',
  squareRecent: 'Últimos pagos de Square',
  squarePaidAt: 'Pagado',
  squareCustomer: 'Cliente',
  squareFee: 'Comisión de Square',
  squareNoPayments: 'Aún no hay pagos de Square.',
  payOnline: 'Pagar {amount} en línea',
  payOpening: 'Abriendo la página de pago…',
  payPending: 'Un pago de esta factura se está procesando (un pago bancario tarda unos días). Gracias.',
  payOnlineHint: 'Tarjeta o cuenta bancaria, en la página segura de Square.',
  paidThanksTitle: 'Gracias',
  paidThanksBody: 'Su pago fue recibido. Square le envía el recibo por correo, y lo aplicamos a su factura en cuanto se acredite (un pago bancario tarda unos días).',
  emailSettingsHint: 'Facturas y estados de cuenta por correo, con un enlace privado que el cliente abre sin iniciar sesión.',
  emailOffNote: 'El correo a clientes está apagado hasta encenderlo en Administración → Correo. Mientras tanto "Enviar prueba" va a una dirección escrita abajo, marcado PRUEBA: escriba la suya.',
  emailTestHint: 'Una prueba va solo a las direcciones escritas, y solo al personal (la dirección de un usuario o el dominio de la empresa), nunca a un cliente.',
  emailStatusUnknown: 'El servicio de correo no respondió: puede que se haya enviado. Revise antes de enviarlo otra vez',
  emailRevoke: 'Detener el enlace',
  emailRevoked: 'enlace detenido',
  moneyOnAccount: 'Pagos y créditos a cuenta',
  emailTo: 'Para',
  emailNoContacts: 'Ningún contacto de este cliente tiene correo: agréguelo en el cliente o escriba una dirección.',
  emailOther: 'Otras direcciones (coma entre ellas)',
  emailBadAddress: 'No es una dirección de correo: {list}',
  emailMessage: 'Una nota (opcional)',
  emailSendTest: 'Enviar prueba',
  emailSend: 'Enviar a {n}',
  emailSent: 'Enviado.',
  emailTestSent: 'Prueba enviada.',
  emailTestBadge: 'prueba',
  emailStatusSent: 'enviado',
  emailStatusFailed: 'no se envió',
  emailOpened: 'abierto {when} ({n}×)',
  emailNotOpened: 'aún no abierto',
  emailServer: 'El servidor',
  emailConfigured: 'El correo está configurado: sale desde {from}.',
  emailSetupHint: 'Claude le guía paso a paso: una cuenta de Postmark, los registros que le da agregados al dominio, luego POSTMARK_SERVER_TOKEN y EMAIL_FROM en los secretos de GitHub y Deploy → deploy.',
  emailToCustomers: 'Correo a clientes',
  emailLiveLabel: 'Encendido: las facturas y estados de cuenta pueden enviarse a clientes',
  emailLiveHint: 'Déjelo apagado mientras QuickBooks sea el registro oficial: las facturas hechas en la app son de práctica hasta el cambio, y un cliente no debe recibir una.',
  emailFromName: 'Nombre del remitente',
  emailReplyTo: 'Las respuestas van a (opcional)',
  asOf: 'Al {date}',
  unitPriceCol: 'Precio',
  amountCol: 'Importe',
  newProduct: 'Producto nuevo',
  packDescLabel: 'Empaque (como lo vende el proveedor)',
  homeVendor: 'Se compra a',
  noHomeVendor: '— sin proveedor aún —',
  homeVendorHint: 'Su pestaña en el día de pedido, y el proveedor cuyo costo le pone precio',
  packUnitLabel: 'Unidad de empaque',
  newProductHint: 'El SKU se hace con el nombre; los precios salen del motor cuando llega un costo (factura del proveedor o recepción).',
  addProduct: 'Agregar producto',
  newProductNameNeeded: 'Escriba el nombre del producto',
  workloadTitle: 'Carga de entregas',
  workloadHint: 'Los próximos días: paradas, pedidos, cajas y líneas para surtir, apartadas y esperadas, y los choferes que lleva cada día. Planee los camiones y la tienda con la misma gente.',
  wlNextDays: 'Próximos {n} días',
  wlBooked: 'Apartado',
  wlBookedHint: 'Paradas con pedido ya hecho (borradores incluidos)',
  wlUsual: 'Lo usual',
  wlUsualHint: 'El promedio de este día de la semana en las últimas semanas',
  wlBusy: 'Día fuerte',
  wlBusyHint: 'Un día fuerte para este día: 4 de cada 5 semanas quedan en o debajo',
  wlStops: 'Paradas',
  wlStopsHint: 'Paradas esperadas: lo mayor entre apartado y usual',
  wlOrders: 'Pedidos',
  wlCases: 'Cajas',
  wlLines: 'Líneas',
  wlLinesHint: 'Líneas de pedido para surtir',
  wlDrivers: 'Choferes',
  wlDriversHint: 'Choferes para las paradas esperadas, y para un día fuerte cuando lleva más',
  wlNotes: 'Notas',
  wlNotInYet: 'Aún no piden',
  wlBusiest: 'Día más fuerte',
  wlStopsWord: 'paradas',
  wlDriversWord: 'chofer(es) en un día fuerte',
  wlMostDrivers: 'Más choferes necesarios',
  wlAtStops: 'a {n} paradas por chofer',
  wlStopsAhead: 'Paradas por venir',
  wlOverDays: 'en {n} días de entrega',
  wlAccuracy: 'Pronóstico a 2 paradas',
  wlAccuracyHint: 'En las últimas 4 semanas: qué tan seguido el día usual quedó a 2 paradas o menos de lo que pasó',
  wlOffBy: 'desviado {n} paradas en promedio',
  wlNotEnough: 'aún sin historial suficiente',
  wlNothing: 'No hay entregas apartadas ni usuales en estos días.',
  wlStopsByDay: 'Paradas por día',
  wlByWeekday: 'Paradas usuales por día de la semana',
  wlStopsPerDriver: 'Paradas por chofer',
  wlFootnote: 'Usual = el promedio del mismo día de la semana en las últimas {weeks} semanas. Una parada es un cliente entregado ese día. Mostrador y traspasos no son entregas; la tienda (Clover) no se cuenta. Aún no piden = clientes fijos que piden ese día al menos la mitad de las semanas.',
  chooseCustomer: 'Elige un cliente',
  backdated: 'No es hoy',
  setToToday: 'hoy',
  unsavedLeave: 'Esta factura no está guardada. ¿Salir de todos modos?',
  rateHint: 'En gris va el precio del cliente. Escribe un precio para cambiarlo en esta factura; se pone verde. Borrar regresa el precio del cliente. Un precio bajo cero (-16) es un crédito: elige el motivo debajo de las líneas.',
  creditLineTag: 'Crédito · {reason}',
  unitLbSetBox: 'lb (poner el peso de la caja)',
  unitEaSetBox: 'pieza (poner cuántas trae la caja)',
  boxTooBig: '¿Una caja de más de 2,000 lb o piezas? No se guardó nada: elige lb otra vez y escribe lo que trae una caja.',
  boxHoldsLbAsk: '¿Cuántas lb trae una caja de {name}? (se guarda en el producto: la existencia y las ventas por libra cuentan con esto)',
  boxHoldsEaAsk: '¿Cuántas piezas trae una caja de {name}? (se guarda en el producto: la existencia y las ventas por pieza cuentan con esto)',
  creditLinesTitle: 'Créditos en esta factura',
  creditLinesHint: 'Una línea con precio bajo cero es un crédito: no sale ninguna caja del almacén y no se surte. Di el motivo de cada uno.',
  creditWhy: '¿Por qué?',
  creditReasonNeeded: 'Elige el motivo de cada crédito antes de guardar.',
  creditMoreThanInvoice: 'Los créditos son más que la factura: acredita el resto con el botón Crédito del estado de cuenta.',
  greetingAfternoon: 'Buenas tardes',
  greetingEvening: 'Buenas noches',
  ordersToday: 'Pedidos de hoy',
  loading: 'Cargando…',
  loadingPrices: 'Cargando precios…',
  pricesFailed: 'Los precios de este cliente no cargaron; todavía no se puede elegir un producto.',
  retry: 'Reintentar',
  noResults: 'No se encontró nada',
  back: 'Atrás',
  more: 'Más',
  less: 'Menos',
  prevDay: 'Día anterior',
  nextDay: 'Día siguiente',
  pack: 'Empaque',
  retailUnits: 'Unidades',
  category: 'Categoría',
  vendor: 'Proveedor',
  shelfLife: 'Vida útil',
  shelf_delicate: 'Delicado',
  shelf_medium: 'Media',
  shelf_hardy: 'Resistente',
  shelf_shelf_stable: 'No perecedero',
  keepOnHand: 'Mantener en existencia',
  sellsSplit: 'Se vende por mitad',
  yes: 'Sí',
  no: 'No',
  inactive: 'Inactivo',
  noPrice: 'sin precio',
  tier: 'Lista de precios',
  tier_mezquite: 'Mezquite',
  tier_standard: 'Estándar',
  contacts: 'Contactos',
  preferred: 'Preferido',
  call: 'Llamar',
  recentInvoices: 'Facturas recientes',
  invoice: 'Factura',
  terms: 'Plazo',
  days: 'días',
  deliveryDays: 'Días de entrega',
  lang_es: 'Español',
  lang_en: 'Inglés',
  notes: 'Notas',
  order: 'Pedido',
  lines: 'líneas',
  noOrders: 'No hay pedidos para este día',
  channel: 'Canal',
  channel_phone: 'Llamada',
  channel_sms: 'Texto',
  channel_whatsapp: 'WhatsApp',
  channel_walkin: 'En persona',
  channel_web: 'Web',
  channel_standing: 'Fijo',
  channel_import: 'Importado',
  channel_transfer: 'Traspaso',
  status_draft: 'Borrador',
  status_confirmed: 'Confirmado',
  status_picked: 'Surtido',
  status_out_for_delivery: 'En camino',
  status_delivered: 'Entregado',
  status_invoiced: 'Facturado',
  status_cancelled: 'Cancelado',
  change: 'Cambiar',
  remove: 'Quitar',
  pickCustomer: 'Elige un cliente',
  addItems: 'Agrega al menos un producto',
  sending: 'Enviando…',
  codeSent: 'Enviamos un código a',
  testCode: 'Código de prueba',
  phoneInvalid: 'Escribe un número de 10 dígitos',
  codeInvalid: 'Código incorrecto o vencido',
  otherPhone: 'Usar otro número',
  cooler: 'Cuarto frío',
  countTitle: 'Conteo de inventario',
  countOlder: '{n} contados el {day} sin guardar. Vuélvalos a poner solo si nada entró ni salió desde entonces:',
  countOlderKeep: 'Volver a ponerlos',
  countOlderDrop: 'Descartar',
  countKept: '{n} contados, sin guardar todavía: se quedan en este aparato hasta que guarde.',
  countHint: 'Escribe cuántos hay en la cámara, en la unidad de cada producto (cajas salvo que diga LB o EA), con decimales para una abierta: 2.5 es dos y media. Deja en blanco lo que no cuentes.',
  coolerCount: 'Conteo',
  salesLast4: 'Ventas, últimas 4 semanas (línea: las 4 semanas anteriores)',
  mixLast4: 'Mezcla de ventas, últimas 4 semanas',
  topCustomers4: 'Mejores clientes, últimas 4 semanas',
  fourWeeksBefore: '4 semanas antes',
  thisWeekSoFar: 'Esta semana hasta hoy',
  vsFourWeekAvg: 'Semana promedio, últimas 4 semanas',
  thisMonthSoFar: 'Este mes hasta hoy',
  last4Weeks: 'Últimas 4 semanas',
  vsSameDaysLastWeek: 'Mismos días la semana pasada',
  vsSameDaysLastMonth: 'Mismos días el mes pasado',
  vsFourWeeksBefore: 'Las 4 semanas anteriores',
  customerTrends: 'Tendencias de clientes',
  customerTrendsHint: 'Las últimas 4 semanas contra las 4 anteriores: cuentas que cambiaron una cuarta parte o más.',
  orderingLess: 'Piden menos',
  pickingUp: 'Piden más',
  noMoves: 'Sin cambios grandes.',
  stoppedOrdering: 'Dejó de pedir',
  newCustomerTrend: 'Nuevo',
  wx_sun: 'Soleado',
  wx_partly: 'Parcialmente nublado',
  wx_cloud: 'Nublado',
  wx_rain: 'Lluvia',
  wx_storm: 'Tormentas',
  wx_snow: 'Nieve',
  wx_wind: 'Ventoso',
  suggestMatches: 'Sugerir coincidencias',
  suggestMatchesHint: 'La app propone un producto para cada nombre: primero las mismas palabras (sin unidades ni conteos), luego Claude para el resto. Las seguras van marcadas; revisa las demás y vincula. Los nombres vinculados se vinculan solos la próxima vez.',
  suggestAiFailed: 'Claude no pudo sugerir parte de la lista; abajo están las coincidencias por palabras. Intenta de nuevo en un minuto.',
  matchingProgress: 'Buscando {done} de {total}…',
  linkingProgress: 'Vinculando {done} de {total}…',
  linkedN: '{n} vinculados',
  linkChecked: 'Vincular marcados ({n})',
  checkAll: 'Marcar todos',
  checkNone: 'Desmarcar todos',
  nameOnDocs: 'Nombre',
  matchHow: 'Coincidencia',
  matchByWords: 'Mismas palabras',
  matchClaudeSure: 'Claude: segura',
  matchClaudeLikely: 'Claude: probable, revísala',
  link: 'Vincular',
  stop: 'Detener',
  priceListFoot: 'Entrega disponible todos los días',
  countUnitCol: 'Unidad',
  countUnitHint: 'Lo que cuenta como uno: CS una caja, LB una libra, EA una pieza',
  countAgain: 'Cuenta estos de nuevo',
  outOfStock: 'Agotado',
  onHandFlagHint: 'Lo que hay en la cámara: rojo si no hay, dorado si la factura vende más de lo que hay',
  coolerCountHint: 'Cuántos hay en la cámara, en la unidad del producto; decimales para una abierta: 2.5 es dos y media',
  onHand: 'En existencia',
  lastCount: 'Último conteo',
  neverCounted: 'nunca contado',
  casesOnHand: 'Cajas en existencia',
  toSave: 'por guardar',
  countSaved: 'Conteo guardado',
  adjusted: 'ajustados',
  nothingToSave: 'Escribe al menos un conteo',
  sealed: 'Cerradas',
  openUnit: 'Abierta',
  leftInOpen: 'Lo que queda en la abierta',
  addOpen: '+ abierta',
  noOpen: 'Sin unidad abierta',
  flaggedDrift: 'Revisar',
  identity: 'Identidad',
  buying: 'Compra',
  selling: 'Venta',
  stockBlock: 'Inventario',
  sku: 'SKU',
  family: 'Familia',
  variety: 'Variedad',
  nameEs: 'Nombre en español',
  status: 'Estado',
  status_active: 'Activo',
  status_seasonal: 'De temporada',
  status_dormant: 'Inactivo',
  stockUnit: 'Unidad de inventario',
  content: 'Contenido',
  sellUnits: 'Se vende por',
  whole: 'entero',
  half: 'mitad',
  loose: 'suelto',
  sensitivity: 'Sensibilidad de precio',
  sens_H: 'Alta (piso 10%)',
  sens_M: 'Media (piso 14%)',
  sens_L: 'Baja (piso 20%)',
  vendorCodes: 'Códigos de proveedor',
  rules: 'Reglas por cliente',
  noRules: 'Sin reglas por cliente',
  aliases: 'Otros nombres',
  addAlias: 'Agregar nombre',
  edit: 'Editar',
  sealedOpen: 'Cerradas · abierta',
  lastDrift: 'Diferencia en el último conteo',
  moved28: 'Movimiento, últimas 4 semanas',
  inbox: 'Nombres desconocidos',
  inboxHint: 'Nombres en facturas, la caja y listas de proveedor que aún no son un producto. Toca uno y elige el producto.',
  inboxEmpty: 'Nada pendiente. Todos los nombres están asignados.',
  assignTo: 'Qué producto es',
  assigned: 'Asignado',
  name: 'Nombre',
  nameHint: 'Lo que ve el cliente en facturas y lista de precios. Sin pesos ni tamaños aquí.',
  internalNote: 'Descripción interna',
  internalNoteHint: 'Solo nosotros la vemos. Nunca en una factura.',
  fixed: 'fijo',
  skuFormat: 'El SKU es FAM-VAR-PACK, p. ej. ONI-YEL-50LB',
  noVendorCodes: 'Aún sin códigos de proveedor',
  noAliases: 'Aún sin otros nombres',
  noCount: 'Nunca contado',
  countedAt: 'Contado',
  openInbox: 'Bandeja de nombres desconocidos',
  trendsTitle: 'Cómo están pidiendo los clientes',
  forecastTitle: 'Pronóstico',
  usdaBlock: 'Precios del mercado (USDA)',
  usdaHint: 'USDA Market News, cada noche: el mercado terminal de Los Ángeles (lo que pagan los mayoristas allá) y el punto de embarque de Phoenix (México por Nogales), para el producto básico de este producto, junto a nuestro costo.',
  usdaNotSetUp: 'USDA Market News aún no está configurado en el servidor (la clave gratuita del USDA).',
  usdaNotLinked: 'Aún no está ligado a un producto del USDA.',
  usdaSuggested: 'El USDA reporta:',
  usdaLink: 'Ligar al USDA',
  usdaChange: 'Cambiar',
  usdaUnlink: 'Desligar del USDA',
  usdaSearch: 'Buscar productos del USDA',
  usdaCommodity: 'Producto del USDA',
  usdaVariety: 'Variedad',
  usdaPackage: 'Empaque',
  usdaAny: 'cualquiera',
  usdaNoPrices: 'Sin precios del USDA en los últimos 30 días.',
  usdaOurCost: 'Nuestro costo',
  usdaOurCostHint: 'por caja, hoy',
  usdaWeeks: 'Semana por semana',
  usdaWeekOf: 'Semana del',
  usdaShowRows: 'Todas las filas del USDA',
  usdaHideRows: 'Ocultar las filas',
  usdaMarket: 'Mercado',
  usdaSize: 'Tamaño',
  usdaOrigin: 'Origen',
  usdaRange: 'Mín. – máx.',
  usdaMostly: 'Mayormente',
  usdaDate: 'Día',
  usdaCol: 'USDA',
  forecastHint: 'Quién probablemente pedirá en los próximos días, de las últimas 12 semanas de facturas: la probabilidad de que pida ese día y lo que suele sumar su pedido. Un ✓ ya está. Elija un cliente para ver lo que suele llevar.',
  forecastSpan: 'Qué tan adelante',
  forecastNext7: 'Próximos 7 días',
  forecastNext14: 'Próximos 14 días',
  forecastView: 'Mostrar',
  forecastByCustomer: 'Por cliente',
  forecastByProduct: 'Por producto',
  forecastByDay: 'Por día',
  forecastPickDay: 'Elija un día',
  forecastLikelyN: '{n} probables',
  forecastDayHint: '{n} por llamar, {in} ya pidieron. Los más probables primero; toque el número para llamar y luego Marcar llamado (queda en su historial).',
  forecastChance: 'Probabilidad · usual',
  forecastUsualDays: 'Días usuales',
  forecastLastOrder: 'Último pedido',
  forecastCall: 'Llamar',
  forecastCalled: 'Marcar llamado',
  forecastCalledToday: 'llamado hoy',
  forecastNoPhone: 'sin número',
  forecastIn: 'pidió',
  forecastDaysAgo: 'hace {n} días',
  forecastCalledBody: 'Se le llamó por un pedido para el {day} (pronóstico).',
  forecastExpected: 'Esperado',
  forecastBooked: 'Ya está',
  forecastCellTitle: '{pct}% de probabilidad; su pedido suele sumar {usual}',
  forecastUsualOrder: 'Pedido usual:',
  forecastNothing: 'No se espera a nadie en estos días.',
  forecastHowWell: 'Qué tan bien acertó el pronóstico',
  forecastHowWellHint: 'El mismo pronóstico corrido en cada uno de los últimos {days} días como si fuera esa mañana, contra lo facturado.',
  forecastRight: 'Acierto cuando lo esperaba',
  forecastRightOf: 'llegaron {hits} de {expected} pedidos esperados',
  forecastCaught: 'Pedidos que vio venir',
  forecastCaughtOf: '{hits} de {ordered} pedidos',
  forecastProductsOff: 'Productos desviados',
  forecastLowerBetter: 'cajas por producto por día, menos es mejor',
  forecastDollars: 'Dólares esperados',
  forecastDollarsActual: 'facturado: {actual}',
  pasteTitle: 'Pegar una llamada o un texto',
  pasteHint: 'Copie la transcripción de una llamada (el Plaud, la grabadora del teléfono) o el texto de un cliente, péguelo aquí y Claude abre la factura llena para que la revise y la cree. Las palabras quedan en el historial del cliente.',
  pasteKind: 'Qué es',
  pasteCall: 'Una llamada',
  pasteText: 'Un texto',
  pasteFromClipboard: 'Pegar del portapapeles',
  pasteClipboardNo: 'El teléfono no entregó el portapapeles: mantenga presionado en el cuadro y elija Pegar.',
  pasteBoxCall: 'La llamada',
  pasteBoxText: 'El texto',
  pasteRead: 'Leerlo',
  pasteSaved: 'Guardado en el historial de',
  pasteNotSaved: 'No se guardó: Claude no supo de qué cliente es.',
  trendsHint: 'De las facturas: las últimas 4 semanas contra el promedio de 4 semanas de las 8 anteriores. Sin pedidos: ninguna factura en el doble de su intervalo usual. Elija un cliente para agregar una nota, un cumpleaños o lo que le gusta.',
  trend_more: 'Pide más',
  trend_steady: 'Estable',
  trend_less: 'Pide menos',
  trend_quiet: 'Sin pedidos',
  trend_new: 'Nuevo',
  colTrend: 'Tendencia',
  colPrior4: 'Prom. 4 semanas antes',
  colUsualDays: 'Días usuales',
  trendEvery: 'cada {n} días',
  trendEveryDay: 'todos los días',
  openAccount: 'Abrir la cuenta',
  noTrends: 'Sin facturas en el último año.',
  tasksDue: 'Tareas que vencen',
  birthdaysNear: 'Cumpleaños en las próximas dos semanas',
  quietCustomers: 'Callados',
  quietHint: 'Sin pedido por más del doble de su ritmo normal.',
  prospectsStale: 'Prospectos por tocar',
  prospectsHint: 'Sin visita, llamada ni nota en dos semanas.',
  inDays: 'en {n} días',
  today: 'hoy',
  tomorrow: 'mañana',
  daysAgo: 'hace {n} días',
  lastOrder: 'Último pedido',
  usualGap: 'normalmente cada {n} días',
  neverTouched: 'Nunca contactado',
  stage: 'Etapa',
  stage_prospect: 'Prospecto',
  stage_trial: 'Prueba',
  stage_customer: 'Cliente',
  stage_lost: 'Perdido',
  allStages: 'Todos',
  source: 'Origen del cliente',
  activities: 'Historial',
  logActivity: 'Registrar',
  kind: 'Qué',
  kind_visit: 'Visita',
  kind_call: 'Llamada',
  kind_text: 'Mensaje',
  kind_note: 'Nota',
  kind_offer: 'Oferta',
  kind_task: 'Tarea',
  whatHappened: 'Qué pasó',
  dueOn: 'Recordar el',
  markDone: 'Hecho',
  undo: 'Deshacer',
  doneLabel: 'hecho',
  addContact: 'Agregar contacto',
  editContact: 'Editar',
  contactName: 'Nombre',
  contactRole: 'Puesto',
  phone: 'Teléfono',
  whatsappLabel: 'Usa WhatsApp',
  smsConsentLabel: 'Aceptó mensajes',
  consentNote: 'Forma de consentimiento',
  birthday: 'Cumpleaños',
  likes: 'Preferencias',
  cancel: 'Cancelar',
  exceptionsTitle: 'Reporte de excepciones',
  exceptionsHint: 'Qué fue contra las reglas y cuánto costó. El piso sale del costo al vender; los precios de nivel son los de hoy.',
  range_d7: 'Últimos 7 días',
  pricingExceptions: 'Vendido bajo el piso o bajo el nivel',
  leftOnTable: 'Dejado en la mesa',
  belowCostLines: 'Líneas bajo costo',
  underFloorBadge: 'Bajo el piso',
  underTierBadge: 'Bajo el nivel',
  belowCostBadge: 'Bajo costo',
  soldAt: 'Vendido a',
  floor: 'Piso',
  creditsByReason: 'Créditos por motivo',
  shrinkByCause: 'Merma por causa',
  voidedInvoices: 'Facturas anuladas',
  cancelledOrders: 'Pedidos cancelados',
  unpricedLinesLabel: 'Líneas sin precio',
  noCostOnShrink: 'sin costo registrado',
  shrinkCostPartial: 'Al menos esto: algunos de estos bultos no tienen costo registrado',
  oauthTitle: 'Conectar una app',
  oauthAsk: '{app} quiere usar Morales Produce como tú',
  oauthHint: 'Verá y hará lo que tú puedes, con tu nombre. Cada lectura y borrador queda registrado como tuyo. El dueño puede desconectarla cuando quiera desde la pantalla del conector de Claude.',
  oauthBad: 'Este enlace no es una solicitud de conexión válida.',
  allow: 'Permitir',
  deny: 'Rechazar',
  connectedApp: 'App de Claude',
  expires: 'vence',
  readFromPhoto: 'Leer de una foto o PDF',
  reading: 'Leyendo…',
  readingLong: 'Una lista de precios larga tarda unos minutos en leerse. Deje esta página abierta.',
  readDone: 'Leído. Revisa cada línea y guarda; el archivo se adjunta con el documento.',
  readNotes: 'El lector dice',
  readDuplicate: 'Este mismo archivo ya se leyó como el documento',
  vendorUnmatched: 'El membrete dice {name}; elige el proveedor.',
  portalCutoffHint: 'Pida antes de las {time} para el día siguiente. Después, el primer día disponible es el siguiente.',
  portalCancel: 'Cancelar este pedido',
  portalCancelAsk: '¿Cancelar este pedido? No se entregará nada de él.',
  portalCallToChange: 'Para cambiar este pedido ahora, llámenos.',
  portalInvoices: 'Facturas',
  portalSettingsTitle: 'Portal de clientes',
  portalSettingsHint: 'Los clientes piden (y cancelan un pedido) en el portal hasta esta hora, hora de Denver, el día antes de la entrega. Después, el primer día disponible es el siguiente. Para hasta la medianoche, ponga 11:59 p. m.',
  portalCutoffLabel: 'Hora límite de pedidos (el día antes de la entrega)',
  stalePricesTitle: 'Precios rezagados',
  stalePricesHint: 'Precios publicados cuyo costo cambió desde que se fijaron, o que quedan bajo el piso con el costo de hoy. Primero bajo el piso, luego lo que el nivel compró en 30 días. Los precios de media caja no se revisan.',
  stalePricesThreshold: 'El costo cambió al menos',
  noStalePrices: 'Todos los precios publicados siguen de acuerdo con su costo.',
  openPriceManagement: 'Abrir gestión de precios',
  priceSinceCol: 'Precio desde',
  costThenCol: 'Costo entonces',
  costNowCol: 'Costo hoy',
  costChangeCol: 'Cambio',
  gmNowCol: '% MB hoy',
  floorCol: 'Piso',
  sold30Col: 'Vendido al nivel 30 d',
  suggestionsTitle: 'Qué más podría comprar',
  suggestionsHint: 'Productos que al menos una cuarta parte de los clientes del mismo nivel de precio compraron en los últimos 90 días, y este no.',
  suggestionPeers: '{n} de {of} clientes similares lo compran',
  lastBoughtOn: 'última compra',
  neverBought: 'nunca lo ha comprado',
  fillRateCol: 'Surtido',
  fillRateHint: 'Cajas entregadas de las cajas pedidas en sus facturas (líneas por empaque).',
  shrinkCol: 'Merma',
  netAfterDeliveryCol: 'Tras entrega',
  netAfterShrinkCol: 'Tras merma',
  costPerStop: 'Costo por parada',
  complianceTitle: 'Cumplimiento y flotilla',
  complianceHint: 'Temperaturas para el inspector, lo que vence y cuándo, y los camiones con su combustible y servicio.',
  renewalsTitle: 'Licencias, permisos y renovaciones',
  noRenewals: 'Nada agregado aún: la licencia PACA, la licencia de negocio, permisos, seguros, registros.',
  renewalKindCol: 'Tipo',
  renewalHolderCol: 'De',
  renewalHolderHint: 'La empresa, un camión, una persona',
  expiresCol: 'Vence',
  renewalDaysLeft: '{n} días',
  renewalExpired: 'Vencida',
  renewBtn: 'Renovar o cambiar',
  archive: 'Archivar',
  renewalArchiveAsk: '¿Archivarla? Ya no se necesita (queda en el registro).',
  addRenewal: 'Agregar una renovación',
  remindDaysLabel: 'Recordar días antes',
  renewal_license: 'Licencia',
  renewal_permit: 'Permiso',
  renewal_insurance: 'Seguro',
  renewal_registration: 'Registro',
  renewal_inspection: 'Inspección',
  renewal_certification: 'Certificación',
  renewal_other: 'Otro',
  temperaturesTitle: 'Registro de temperaturas',
  temperatureLimits: 'Límites: cámara fría {cmin}–{cmax} °F, andén {dmin}–{dock} °F, camión {tmin}–{truck} °F.',
  renewalToday: 'Hoy',
  renewalOneDay: '1 día',
  takenBack: 'Anulada',
  takeBack: 'Anular',
  takeBackWhy: '¿Por qué está mal este registro? (queda en el registro, marcado)',
  enteredLate: 'Registrada tarde',
  changeLimits: 'Cambiar los límites',
  limitsHint: 'Las lecturas ya tomadas conservan los límites con que se tomaron. Los productos sensibles al frío (tomate, plátano, pepino, chile) se conservan mejor a 45–55 °F: anote su cuarto en la nota hasta que cada cuarto tenga sus límites.',
  tempPlace: 'Dónde',
  temp_cooler: 'Cámara fría',
  temp_dock: 'Andén (camión recibido)',
  temp_truck: 'Camión',
  tempF: 'Temperatura °F',
  loadCondition: 'Estado de la carga',
  condition_good: 'Bueno',
  condition_fair: 'Regular',
  condition_poor: 'Malo',
  vehicle: 'Vehículo',
  logReading: 'Registrar la lectura',
  tempOutOfRange: 'Fuera de rango',
  noReadings: 'Sin lecturas en los últimos 7 días.',
  fleetTitle: 'Camiones',
  noVehicles: 'Aún no hay camiones.',
  renewalsDueBadge: '{n} renovación pendiente',
  odometer: 'Odómetro',
  lastService: 'último servicio',
  fuel30: 'combustible, 30 días',
  vehicleName: 'Nombre del camión',
  plate: 'Placa',
  addVehicle: 'Agregar un camión',
  logKind: 'Qué registrar',
  vlog_fuel: 'Combustible',
  vlog_service: 'Servicio',
  vlog_odometer: 'Odómetro',
  gallons: 'Galones',
  serviceWhat: 'Qué se hizo',
  weatherTitle: 'Clima en el almacén (máxima / mínima · probabilidad de lluvia)',
  qboKind_PaymentMethod: 'Métodos de pago',
  qboMirrorTitle: 'Dinero de QuickBooks',
  qboMirrorOffHint: 'Apagado: la app muestra sus facturas de práctica. Actívelo abajo y las facturas y pagos de QuickBooks pasan a ser los de la app, al día después de cada importación nocturna, así saldos, antigüedad y estados de cuenta cuadran con QuickBooks. Esto es lo que traería hoy.',
  qboMirrorTurnedOnAt: 'Activado {when}. El dinero se registra solo en QuickBooks; la app lo copia cada noche.',
  qboMirrorRun: 'Traer el dinero de QuickBooks ahora',
  qboWriteTitle: 'Las facturas hechas en la app van a QuickBooks',
  qboWriteHint: 'La app es donde se hacen las facturas. Cada una se copia a QuickBooks (con su cliente y productos) en un minuto, como la copia para el contador. Los pagos y créditos se siguen capturando en QuickBooks, y los pagos de QuickBooks regresan a las facturas de la app.',
  qboWriteOn: 'Encendido: las facturas hechas en la app son reales y se copian a QuickBooks.',
  qboWriteFrom: 'Encendido desde el {date}: hasta entonces, las facturas hechas en la app son de práctica.',
  qboWritePaused: 'En pausa: las facturas hechas en la app siguen siendo reales y esperan aquí hasta que lo reanude.',
  qboWriteTurnedOn: 'encendido {when} por {who}',
  qboWriteOtherServer: 'Esta copia de la app no es la que se encendió: nunca escribe en QuickBooks.',
  qboWriteReconnect: 'Hay que conectar QuickBooks de nuevo: no se envía nada hasta entonces (no se pierde nada).',
  qboWriteCountWaiting: 'Esperando',
  qboWriteCountSent: 'Copiadas',
  qboWriteCountFailed: 'Por revisar',
  qboWriteLastSent: 'Última copia {when}',
  qboWriteRetry: 'Intentar de nuevo',
  qboWriteSendNow: 'Enviar ahora',
  qboWriteSentNow: 'Copiadas {sent}, rechazadas {failed}, esperando {waiting}.',
  qboWritePause: 'Pausar',
  qboWriteResume: 'Reanudar',
  pnlTitle: 'Pérdidas y ganancias (QuickBooks)',
  pnlHint: 'El estado de pérdidas y ganancias de QuickBooks, leído de QuickBooks al abrirlo: lo que ve el contador. Las facturas de proveedores, los gastos y la nómina se registran en QuickBooks; las ventas son las facturas que tiene QuickBooks (las de la app van allá). Haga clic en una sección para ver sus cuentas.',
  pnlThisMonth: 'Este mes',
  pnlLastMonth: 'Mes pasado',
  pnlYearToDate: 'Año hasta hoy',
  pnlLastYear: 'Año pasado',
  pnlColumns: 'Columnas',
  pnlOneTotal: 'Un total',
  pnlByMonth: 'Por mes',
  pnlAccount: 'Cuenta',
  pnlIncome: 'Ingresos',
  pnlCogs: 'Costo de lo vendido',
  pnlGrossProfit: 'Utilidad bruta',
  pnlExpenses: 'Gastos',
  pnlNetOperating: 'Utilidad de operación',
  pnlOtherIncome: 'Otros ingresos',
  pnlOtherExpenses: 'Otros gastos',
  pnlNetOther: 'Otros ingresos netos',
  pnlNetIncome: 'Utilidad neta',
  pnlGrossPct: 'Margen bruto',
  pnlEmpty: 'QuickBooks no tiene nada en este periodo.',
  pnlReadAt: '{period} · leído de QuickBooks {when} · {basis}.',
  pnlReading: 'Leyendo el nuevo periodo de QuickBooks…',
  pnlTotal: 'Total',
  pnlAccrual: 'base devengada',
  pnlCash: 'base de efectivo',
  switchReadyTitle: 'Listo para el día',
  switchAgingAsk: 'Cada cliente contra el reporte de antigüedad de cuentas por cobrar de QuickBooks: léalo ahora, justo antes del cambio.',
  switchAgingButton: 'Revisar contra QuickBooks ahora',
  switchAgingReading: 'Leyendo QuickBooks…',
  switchAgingResult: '{n} de {of} clientes enlazados deben distinto de lo que dice el reporte de antigüedad de QuickBooks (total {total}). Corríjalos en QuickBooks antes del cambio.',
  switchAgingOpen: 'Verlos en Emparejar con QuickBooks → Saldos',
  switchCounted: 'Inventario contado por última vez {when} ({n} productos en los dos días antes). Cuente la noche antes del cambio.',
  switchNotCounted: 'Todavía no hay conteo de inventario: cuente la noche antes del cambio.',
  switchSquareNotSet: 'Square todavía no está configurado en el servidor.',
  switchSquareNotTested: 'Square está configurado, pero su pago de prueba de $1 no ha llegado.',
  switchSquareReady: 'Square está configurado y probado: active el botón Pagar después del cambio.',
  switchEmailNotSet: 'El correo todavía no está configurado en el servidor.',
  switchEmailReady: 'El correo está configurado: active el correo a clientes después del cambio.',
  switchTurnOnSquare: 'Active ahora el botón Pagar para clientes:',
  switchTurnOnEmail: 'Active ahora el correo a clientes:',
  switchTitle: 'Pasar los libros a la app',
  switchHint: 'El día del cambio (1 de diciembre): desde entonces la app lleva las facturas, pagos y créditos, y los escribe todos en QuickBooks para el contador. Las cuentas por pagar, los pagos a proveedores y el banco siguen en QuickBooks. Hágalo cuando nadie esté registrando nada en QuickBooks.',
  switchPractice: 'Borra la práctica: {n} facturas ({total}), {p} pagos y {c} créditos. Las facturas reales se quedan.',
  switchLedger: 'El libro de cuentas por cobrar empieza con el primer registro de QuickBooks: {from} → {to}.',
  switchNumbers: 'El siguiente número de factura: {n} (después de todos los de QuickBooks).',
  switchAfter: 'Primero se lee QuickBooks por última vez. Después del cambio, el dinero se registra en la app y va a QuickBooks en un minuto; las facturas propias de QuickBooks reciben pagos y créditos aquí, y se corrigen con un crédito. No hay vuelta atrás salvo el respaldo.',
  switchBlocked: 'Todavía no:',
  switchReady: 'Todo cuadra: se puede hacer.',
  switchBackup: 'Corrí Deploy → backup-now justo antes de esto',
  switchType: 'Escriba SWITCH para confirmar',
  switchButton: 'Pasar los libros a la app',
  switchRunning: 'Cambiando… se lee QuickBooks por última vez. Esta tarjeta dice cuando termine.',
  switchStopped: 'No se cambió, nada se modificó: {why}',
  switchDone: 'Los libros pasaron a la app el {when}. El dinero se registra aquí y va a QuickBooks.',
  switchEnteredThere: 'Registrado en QuickBooks mismo desde el cambio (la app nunca lo trae): {i} facturas, {p} pagos, {c} créditos (números {docs}). Bórrelos en QuickBooks y regístrelos en la app.',
  switchOrdersStay: '{n} pedidos que facturó una factura de práctica quedan facturados: QuickBooks los facturó.',
  practiceCardTitle: 'Práctica',
  practiceStatement: 'Facturas y pagos de práctica',
  paidAndCredited: 'Pagado y acreditado',
  statementView: 'Qué dinero',
  statementReal: 'Real (QuickBooks)',
  statementPractice: 'Práctica',
  statementRealHint: 'Hasta el cambio, los pagos y créditos reales se capturan en QuickBooks (la app los copia cada hora). Para practicar pagos y créditos aquí, abra Práctica.',
  practiceCardHint: 'Lo que se capturó en la app mientras QuickBooks lleva los libros: no cuenta en nada y nunca se envía a QuickBooks. Bórrelo al terminar una práctica; los registros reales nunca se tocan. Se cuentan renglones: un cheque en dos facturas son dos pagos, y una reversión o un deshacer es uno más; las facturas anuladas se cuentan pero no van en el total.',
  practiceInvoices: 'Facturas de práctica',
  practicePayments: 'Pagos de práctica',
  practiceCredits: 'Créditos de práctica',
  practiceOrdersBack: '{n} pedidos reales facturados con una factura de práctica vuelven a quedar por facturar (los cambios hechos con la factura de práctica, líneas y cantidades, se quedan en el pedido).',
  practiceType: 'Escriba DELETE PRACTICE',
  practiceDelete: 'Borrar la práctica',
  practiceDeleted: 'Se borraron {n} facturas, {p} pagos y {c} créditos de práctica.',
  qboWriteDoneThere: 'Hecho en QuickBooks',
  qboWriteDoneThereConfirm: 'Usted lo hizo a mano en QuickBooks, así que la app no lo enviará. ¿Marcarlo como hecho?',
  qboWriteKindPayment: 'Pago',
  qboWriteKindCredit: 'Crédito',
  qboWriteTurnOff: 'Apagar',
  qboWriteTurnOffConfirm: 'Las facturas hechas en la app vuelven a ser de práctica. Lo que ya se copió se queda en QuickBooks; lo que espera se envía si lo vuelve a encender. ¿Apagar?',
  qboWriteStepNumbers: 'En QuickBooks: ⚙ Configuración → Cuenta y configuración → Ventas → Contenido del formulario de ventas → Números de transacción personalizados → Activado (para que QuickBooks guarde los números de factura de la app).',
  qboWriteStepCredits: 'En QuickBooks: Cuenta y configuración → Avanzado → Automatización → Aplicar créditos automáticamente → Desactivado.',
  qboWriteStepSales: 'QuickBooks tiene una cuenta de ingresos llamada Sales: un producto que la app crea allí se registra en ella.',
  qboWriteStepMoney: 'Los pagos y créditos se quedan en QuickBooks hasta el cambio; regresan a las facturas de la app cada hora.',
  qboWriteFromLabel: 'Reales desde',
  qboWriteTurnOn: 'Encender',
  qboWriteConfirm: 'Desde el {date}, las facturas hechas en la app son reales y se copian a QuickBooks. Desde entonces haga las facturas solo en la app, nunca en QuickBooks (los números chocarían). ¿Encender?',
  qboWriteOff: 'Apagado: el dueño lo enciende aquí.',
  qboWriteOffBand: 'APAGADO: las facturas hechas en la app son de práctica. No se envía nada a QuickBooks.',
  qboWriteSent: 'Copiada a QuickBooks como #{n}',
  qboWriteWaiting: 'Esperando copiarse a QuickBooks…',
  qboWriteFailed: 'QuickBooks rechazó esta factura (vea Administración → QuickBooks):',
  qboRealTitle: 'Factura real.',
  qboRealHint: 'Las facturas hechas aquí cuentan y se copian a QuickBooks. Los pagos y créditos se siguen capturando en QuickBooks.',
  qboMirrorRunning: 'Trayendo el dinero de QuickBooks… esta tarjeta muestra la corrida cuando termine.',
  qboTurnOnTitle: 'Activarlo',
  qboTurnOnHint: 'Desde entonces facturas, pagos y créditos se registran solo en QuickBooks y la app los rechaza. Al activarlo se deshace el dinero de práctica capturado en la app (nada se borra; cada reverso queda):',
  qboTurnOnInvoices: '{n} facturas hechas en la app anuladas ({total})',
  qboTurnOnSheets: '{n} facturas de la importación de Sheets también anuladas; QuickBooks las trae de vuelta como suyas',
  qboTurnOnSheetsStay: '{n} de ellas quedan anuladas: QuickBooks no las tiene para un cliente enlazado, o se registró un faltante en ellas. Enlace primero a los clientes para traer más.',
  qboMirrorRunError: 'La última corrida no terminó: {why}. Presione Traer el dinero de QuickBooks ahora, o se intenta de nuevo esta noche.',
  qboTurnOnPayments: '{n} pagos capturados en la app revertidos ({total})',
  qboTurnOnCredits: '{n} créditos revertidos ({total})',
  qboTurnOnOpening: 'Saldos iniciales de {n} clientes en cero',
  qboTurnOnLedger: 'El inicio del libro pasa de {from} a {to} (el primer registro de QuickBooks)',
  qboTurnOnAfter: 'Luego entran las facturas y pagos de QuickBooks. Las facturas de QuickBooks hechas desde ahora descuentan sus cajas del inventario. El efectivo cobrado en la puerta se guarda para el conteo; la oficina registra el pago en QuickBooks.',
  qboTurnOnBackup: 'Hoy corrí Deploy → What to do: backup-now y terminó en verde',
  qboTurnOnType: 'Escriba TURN ON',
  qboTurnOnButton: 'Activar el dinero de QuickBooks',
  qboTurnOnStarted: 'Activado. Están entrando las facturas y pagos de QuickBooks; esta tarjeta muestra la corrida cuando termine.',
  stopCashUndo: 'Deshacer el efectivo',
  stopCashUndoConfirm: '¿Deshacer el dinero guardado en esta parada? El chofer registra el monto correcto en la siguiente entrega.',
  qboMirrorOnHint: 'Encendido: las facturas y pagos de QuickBooks son los de la app, al día después de cada importación. Cámbielos en QuickBooks.',
  qboMirrorInvoicesNew: 'Facturas por traer o actualizar',
  qboMirrorAdopted: 'De ellas, de la importación de Sheets (se toman)',
  qboMirrorInvoicesHeld: 'Facturas ya al día',
  qboMirrorPaymentRows: 'Líneas de pago por escribir',
  qboMirrorSkipped: 'En espera: {n} clientes sin enlazar',
  qboMirrorSkippedHint: 'Las facturas de clientes de QuickBooks no enlazados con los de la app esperan hasta enlazarlos.',
  qboMirrorProblems: '{n} cosas por revisar',
  qboMirrorLast: 'Última corrida {when}',
  qboMirrorAllMatch: 'Cada factura debe en la app lo mismo que en QuickBooks.',
  qboMirrorDiffer: '{n} facturas deben distinto que en QuickBooks',
  qboMirrorApp: 'app',
  portalGreeting: 'Hola',
  portalNewOrder: 'Hacer un pedido',
  portalSameAgain: 'Lo mismo de la última vez',
  portalMyOrders: 'Mis pedidos',
  portalStatement: 'Mi cuenta',
  portalLastOrder: 'Último pedido',
  portalNextDelivery: 'Próxima entrega',
  portalDeliverOn: 'Entregar el',
  portalAsk: 'preguntar',
  portalLastTime: 'la última vez',
  portalNotes: 'Notas para el chofer o la oficina',
  portalPlace: 'Enviar el pedido',
  portalPlaced: 'Pedido recibido. Lo confirmamos en breve.',
  portalEmpty: 'Pon una cantidad junto a un producto',
  portalOrderNo: 'Pedido',
  portalOpenInvoices: 'Facturas abiertas',
  portalAccess: 'Puede pedir en línea',
  portalAccessHint: 'Entra con este teléfono y ve solo sus precios, pedidos y cuenta.',
  portalOrders: 'Pedidos',
  signedInAs: 'Entró como',
  textsTitle: 'Mensajes',
  textsHint: 'Los mensajes al número del negocio llegan aquí. Responde, haz un borrador de pedido con uno, o márcalo como atendido.',
  textsOff: 'Los mensajes aún no están configurados (el número del negocio). Ver el manual.',
  textsInbox: 'Bandeja',
  textsEmpty: 'No hay mensajes pendientes',
  makeDraft: 'Hacer borrador de pedido',
  markHandled: 'Atendido',
  reply: 'Responder',
  sendText: 'Enviar mensaje',
  textConfirmation: 'Enviar confirmación por mensaje',
  textSent: 'Mensaje enviado',
  textsThread: 'Mensajes',
  blastTitle: 'Mensaje a un grupo',
  blastHint: 'Cada persona recibe su propio mensaje en su idioma. Variables: {name} {customer} {item} {price} {lastQty}. Solo se incluyen contactos que aceptaron mensajes.',
  blastItem: 'Sobre qué producto',
  blastEveryone: 'Todos los que aceptaron mensajes',
  blastDays: 'Lo compraron en los últimos',
  blastAudience: 'Destinatarios',
  blastEn: 'Mensaje en inglés',
  blastEs: 'Mensaje en español',
  blastPreview: 'Vista previa',
  blastSend: 'Enviar a {n} personas',
  blastSent: 'Enviado a {n}. Omitidos: {k}.',
  unknownNumber: 'Número desconocido',
  textsWaiting: 'mensajes pendientes',
  routesTitle: 'Despacho',
  routesHint: 'Las entregas del día en orden de parada. La oficina la arma con los pedidos confirmados y pone al chofer; el chofer va tocando parada por parada.',
  buildRoute: 'Armar con los pedidos confirmados',
  addRoute: 'Agregar una ruta',
  deliveriesOnPhone: '{n} entregas guardadas en el teléfono: se mandan cuando haya señal.',
  sendNow: 'Mandar ahora',
  deliveryNotSent: 'no se mandó:',
  deliveryNoRoom: 'El teléfono no tiene espacio para guardar esta entrega. Busque señal y toque Entregado otra vez, o anótela.',
  savedOnPhone: 'Guardada en el teléfono',
  newRouteName: 'Nombre de la nueva ruta',
  moveToRoute: 'Mover a',
  loadSheet: 'Hoja de carga',
  printInvoices: 'Imprimir las facturas',
  loadOrderHint: 'Cargue en este orden: la última parada sube primero al camión.',
  itemTotals: 'Totales por producto',
  notInvoicedYet: 'Sin factura todavía',
  usualRoute: 'Ruta de siempre',
  usualRouteHint: 'Armar pone a este cliente en esta ruta (Ruta 1 si está vacío).',
  codBadge: 'Contra entrega',
  owesNow: 'Debe',
  pastDueShort: 'vencido',
  whyShort: 'Por qué faltó',
  short_damaged: 'Dañado',
  short_refused: 'Rechazado',
  short_not_loaded: 'No se cargó',
  short_out_of_stock: 'Sin existencia',
  short_wrong_item: 'Producto equivocado',
  short_other: 'Otro',
  noRoute: 'No hay ruta para este día',
  driver: 'Chofer',
  truck: 'Camión',
  startRoute: 'Salir con el camión',
  routeOut: 'En camino',
  routeDone: 'Ruta terminada',
  notDelivered: 'No entregado',
  notDeliveredWhy: 'Por qué no se entregó',
  nd_closed: 'Cerrado',
  nd_refused: 'Lo rechazaron',
  nd_nobody: 'No había nadie',
  nd_not_reached: 'No se llegó',
  nd_cancelled: 'Cancelado: regréselo',
  newCustomer: 'Cliente nuevo',
  customerShortName: 'Nombre corto',
  customerTypeLabel: 'Tipo',
  callAheadLabel: 'Llamar antes de entregar',
  editDetails: 'Editar datos',
  restoreBtn: 'Restaurar',
  archivedFilter: 'Archivados',
  archiveCustomerConfirm: '¿Archivar a {name}? Sale de las listas y de las opciones; sus facturas e historial se quedan. Puede restaurarlo en Archivados.',
  customerArchived: 'Archivado: no aparece en las listas ni en las opciones. Su historial se queda.',
  vendorsTitle: 'Proveedores',
  vendorsHint: 'A quién le compramos: cómo contactarlos, sus plazos y los días que entregan.',
  newVendor: 'Proveedor nuevo',
  vendorKind: 'Clase',
  vendorKind_wholesaler: 'Mayorista',
  vendorKind_grower: 'Productor',
  vendorKind_retail: 'Minorista',
  vendorKind_other: 'Otro',
  vendorTermsDays: 'Plazo (días)',
  archiveVendorConfirm: '¿Archivar a {name}? Sale de las opciones; sus precios, papeles e historial se quedan.',
  receivePaymentTitle: 'Recibir pago',
  receivePaymentHint: 'Escoja el cliente que pagó; el pago va primero a sus facturas más viejas, como en su estado de cuenta.',
  pickCustomerWhoPaid: 'Cliente que pagó',
  openStatement: 'Abrir su estado de cuenta',
  undoCredit: 'Deshacer',
  undoCreditWhy: '¿Por qué deshacer este crédito?',
  creditUndone: 'Deshecho',
  creditUndoes: 'Deshace un crédito',
  notForRole: 'Esto no es para su puesto. Pida al dueño o a la oficina si lo necesita.',
  confirmSure: '¿Está seguro?',
  cancelTransferConfirm: '¿Cancelar esta transferencia? No se envía nada.',
  tempTab: 'Temperaturas',
  updateReady: 'Hay una versión nueva de la app.',
  updateReload: 'Recargar',
  deliveryUnsaved: 'Hay una entrega a medio llenar en esta pantalla. ¿Salir sin guardarla?',
  nd_other: 'Otro',
  notDeliveredSave: 'Guardar: no entregado',
  notDeliveredHint: 'El pedido regresa para entregarse otro día: la oficina escoge el día en esta parada.',
  anotherDay: 'Entregarlo el',
  anotherDayBtn: 'Pasar a ese día',
  finishWaitPhone: 'Primero envíe las entregas guardadas en el teléfono',
  finishRoute: 'De regreso: terminar la ruta',
  finishRouteConfirm: '¿Terminar la ruta? Las paradas no entregadas quedan marcadas como «no se llegó» y sus pedidos regresan para entregarse otro día.',
  collectFill: 'Cobrar {amount}',
  stops: 'paradas',
  moveUp: 'Subir',
  moveDown: 'Bajar',
  arrived: 'En la puerta',
  delivered: 'Entregado',
  deliverBtn: 'Entregado',
  receivedBy: 'Recibió',
  doorNote: 'Faltantes, cambios, lo que se dijo en la puerta',
  qtyDelivered: 'Bajó del camión',
  etaText: 'Avisar llegada',
  etaMinutes: 'minutos',
  photoPod: 'Foto en la puerta',
  photoDone: 'Foto guardada',
  navigate: 'Navegar',
  noAddress: 'Sin dirección registrada',
  window: 'Horario',
  freight: 'Flete',
  fees: 'Cargos (combustible, tarimas, broker)',
  allocate: 'Repartir por',
  allocate_packs: 'bultos',
  allocate_weight: 'peso',
  landed: 'puesto en bodega',
  agingTitle: 'Antigüedad de cuentas por cobrar',
  agingHint: 'Facturas abiertas por días de atraso, por cliente, menos los pagos y créditos no ligados a una factura (Sin aplicar), con el plazo, el límite de crédito y el estado de cobranza. Haz clic en un cliente para ver su estado de cuenta.',
  bucket_current: 'Al día',
  bucket_d1to30: '1–30',
  bucket_d31to60: '31–60',
  bucket_d61to90: '61–90',
  bucket_d90plus: '90+',
  creditLimit: 'Límite de crédito',
  limitOwnerOnly: 'Solo el dueño pone el límite de crédito. Usted puede poner al cliente en espera o quitarle la espera.',
  noLimit: 'sin límite',
  onHold: 'Retenido',
  holdReason: 'Por qué está retenido',
  creditTitle: 'Crédito',
  creditHint: 'Pasado el límite o retenido, no se toma pedido desde ninguna pantalla; el dueño puede tomarlo de todos modos.',
  oldest: 'más viejo',
  daysLateShort: 'días de atraso',
  taxRate: 'Tasa (%)',
  subtotal: 'Subtotal',
  tax: 'Impuesto',
  connectorTitle: 'Conector de Claude',
  connectorHint: 'Claude habla con el negocio por las mismas puertas que la app, como un usuario propio. Crea un token para ese usuario y pégalo en la configuración del conector, nunca en un chat.',
  tokenOnce: 'Tu nuevo token',
  tokenOnceHint: 'Se muestra una sola vez. Cópialo ahora; no se puede volver a mostrar.',
  copyToken: 'Copiar el token',
  copied: 'Copiado',
  done: 'Listo',
  makeToken: 'Crear un token',
  tokenFor: 'Para qué usuario',
  tokenName: 'Nombre de este token',
  tokens: 'Tokens',
  lastUsed: 'último uso',
  revoked: 'Revocado',
  revoke: 'Revocar',
  howToConnect: 'Cómo conectar Claude Code',
  howToConnectHint: 'En una computadora con Claude Code, ejecuta esto una vez con el token en lugar de <token>. Los conectores de Claude.ai requieren inicio de sesión por OAuth, que viene después.',
  money: 'Dinero',
  invoicedToday: 'Facturado hoy',
  invoicedWeek: 'Facturado, 7 días',
  paidWeek: 'Cobrado, 7 días',
  openBalance: 'Cuentas por cobrar',
  overdue: 'vencido',
  marginWeek: 'Margen, 7 días',
  slowPayers: 'Cuentas vencidas',
  stockAtRisk: 'Inventario en riesgo',
  waiting: 'Requiere atención',
  waitingHint: 'Trabajo detenido hasta que alguien actúe. Haga clic en una línea para resolverla.',
  unknownNamesCount: 'nombres en ventas o en líneas de proveedor sin producto asignado',
  unmappedDocLines: 'líneas de proveedor sin producto',
  openPos: 'pedidos de compra abiertos',
  tomorrowOrders: 'mañana',
  unpricedLinesCount: 'líneas sin precio',
  buyTitle: 'Planeación de compras',
  buyHint: 'Pedidos en libros, más el ritmo diario reciente, más el mínimo en mano, menos existencia. Cambia cualquier número y escribe el pedido por proveedor.',
  buyFor: 'Comprar para',
  buy: 'Comprar',
  ordered: 'pedido',
  day: 'día',
  nothingToBuy: 'Nada que comprar para este periodo',
  noVendor: 'Sin proveedor principal',
  writePo: 'Escribir el pedido',
  poWritten: 'Pedido escrito',
  vendorDocs: 'Documentos de proveedores',
  navOrderDay: 'Día de pedido',
  priceSection_fruit: 'Frutas',
  priceSection_veg: 'Verduras',
  priceSection_dry: 'Abarrotes',
  priceSection_other: 'Otros',
  vsOfferCol: 'Oferta',
  offersTodayTitle: 'Ofertas de hoy',
  offeredCol: 'Ofrecida',
  offeredHint: 'Una oferta vale hasta el día siguiente y luego se quita sola',
  offeredToday: 'hoy, vale hasta mañana',
  offeredYesterday: 'ayer, se quita hoy en la noche',
  alsoOrdered: '{n} pedidas ahí',
  vsAddItem: 'Agregar un producto a este pedido',
  vsOfferLinesHint: 'Los productos ofrecidos están en blanco: escriba cuántos quiere de este proveedor. La pestaña del proveedor principal modela solo el resto (un pedido ya guardado ahí conserva su número: bájelo).',
  vsOverModel: 'Pedido aquí por encima de lo que falta comprar (otro proveedor tiene el resto): {items}',
  vsLowerToModel: 'Bajar al modelo',
  vsOfferHint: 'La oferta de este proveedor por el producto, o el precio de otro proveedor esta semana',
  offeredAt: 'ofrece {price}',
  offersTitle: 'Ofertas de proveedores',
  offersHint: 'Lo que un proveedor mandó por mensaje hoy: elija cada producto y escriba el precio por caja. En el día de pedido sale una línea en su pestaña a ese precio, en blanco para que usted la llene, y se queda en la pestaña del proveedor principal. Cada línea vale hasta mañana y luego se quita sola; hacer su pedido la usa antes. Cada oferta queda en la base de datos.',
  herPrice: 'Precio por caja',
  pickVendor: 'Elija un proveedor',
  saveOffer: 'Guardar oferta',
  clearOffer: 'Borrar oferta',
  clearOfferConfirm: '¿Borrar esta oferta? Queda en el historial.',
  offerTyped: 'Capturada {when} por {who}',
  offerNone: 'Sin oferta hoy.',
  offerUsed: 'usada por un pedido {when}: se quita 36 horas después',
  offerTwice: 'Un producto está dos veces en la oferta: deje una línea.',
  offerSaved: 'Oferta guardada: las pestañas de proveedores ya la muestran.',
  vsCostHint: 'El precio del proveedor por caja: va en el pedido y al recibir es el costo que se registra. Corríjalo si el precio cambió.',
  openEarlier: 'La carga del {date} tiene pedidos hechos sin recibir.',
  openEarlierGo: 'Abrirla',
  callAheadCol: 'Otro',
  moreOthersCol: 'Más otros',
  moreOthersHint: 'Pedidos por teléfono de clientes además de las dos columnas Otro',
  callAheadPick: '+ Cliente',
  noVendorWarn: 'Sin proveedor para pedir esto (ponga un proveedor principal en el producto): {items}',
  countMe: 'contar',
  countMeHint: 'Bajo cero: falta una entrada o un conteo, así que ORDER no lo cuenta como existencia. Cuente este producto (Conteo de inventario).',
  noPriceWarn: 'En el pedido de una tienda sin precio para ella, así que la factura será rechazada: {items}. Publique un precio en',
  columnOnLoads: 'Su columna aparece el',
  loadOrderTitle: 'Pedido del {load}',
  loadWeek: 'Qué carga',
  prevLoad: 'Anterior',
  nextLoad: 'Próxima carga',
  laterLoad: 'Siguiente',
  vendorTabs: 'Plan y proveedores',
  planTab: 'Plan',
  orderColHint: 'Lo que el modelo pide para esta carga: se pide en las pestañas de proveedores',
  vsReceived: 'recibido',
  vsPlacedNote: 'Pedido hecho. Copie las líneas al correo del proveedor.',
  vsReceivedNote: 'Recibido: el inventario está en existencia y lo que no llegó está en faltantes.',
  vsCode: 'Código',
  vsModel: 'Modelo dice',
  vsModelHint: 'El PEDIDO del día de pedido para este proveedor: base + seguridad + mínimo + las tiendas + pedidos por teléfono − existencia',
  vsOrder: 'Cajas a pedir',
  vsReceivedCol: 'Recibido',
  vsReceivedHint: 'En blanco = llegó todo, 0 = nada',
  vsShort: 'Faltante',
  vshortTitle: 'Faltantes del proveedor',
  vshortHint: 'Lo que un proveedor no va a traer para una carga: cambie las facturas de estas tiendas al revisarlas (Facturar las cuentas marca las líneas) y avíseles en la mañana: cada tienda tiene una tarea en Seguimiento con el texto para mandar.',
  vshortComing: '{vendor}: llegan {coming} de {ordered}',
  vshortFrom_invoice: 'según su factura',
  vshortFrom_typed: 'lo que dijeron (escrito)',
  vshortFrom_dock: 'contado al recibir',
  vshortOnHand: 'en existencia {n}',
  vshortLoad: 'carga {day}',
  vshortNoStores: 'Ningún pedido de tienda lo lleva.',
  vshortOrderNo: 'pedido #{n}',
  vshortInvoiceNo: 'factura {n}',
  vshortToDo: 'tarea en Seguimiento',
  vshortTold: 'tarea hecha',
  vshortCopy: 'Copiar texto',
  vshortCopied: 'Copiado',
  vshortHandled: 'Atendido',
  vshortReopen: 'Abrir otra vez',
  vshortUndoTyped: 'Quitar lo escrito',
  vshortSaidTitle: 'El proveedor dijo (llamada o mensaje):',
  vshortSaidItem: 'Producto del pedido',
  vshortSaidQty: 'Llegan',
  vshortOnCard: 'Al proveedor le faltó:',
  vshortSaid_invoice: 'factura',
  vshortSaid_typed: 'dicho',
  vshortSaid_dock: 'recibido',
  unitCostCol: 'Costo unitario',
  lineTotalCol: 'Total de línea',
  vsCopied: 'Copiado: péguelo en el correo.',
  vsCopyPrompt: 'Copie estas líneas al correo:',
  vsStatusReceived: 'Recibido',
  vsStatusPlaced: 'Pedido {when} por {who}',
  vsStatusDraft: 'Guardado, sin pedir todavía',
  vsStatusNone: 'Sin pedir todavía: las cajas son lo que pide el modelo',
  vsAllItems: 'Todos los productos del proveedor',
  vsCopy: 'Copiar para correo',
  vsSave: 'Guardar',
  editOrder: 'Cambiar pedido',
  orderNotEditable: 'Este pedido ya se surtió o va en camino: cámbielo en su factura (o en la puerta).',
  orderDatePast: 'La fecha de entrega ya pasó: elija hoy o después.',
  notTheirDeliveryDay: 'No es uno de sus días de entrega ({days}).',
  needsOwnerOk: 'requiere al dueño',
  approveFloorSave: 'Aprobar como dueño y guardar',
  overrideHoldSave: 'Tomarlo de todos modos (dueño) y guardar',
  changeOrder: 'Cambiar',
  docCostFarAsk: 'Estos costos están a más de 25% de lo que cuestan ahora:\n{list}',
  docTotalOffAsk: 'Las líneas suman {lines}, el total escrito es {total}.',
  docSaveAnyway: 'Un costo guardado entra de inmediato a Precios de proveedores y al día de pedido y no se puede deshacer. ¿Guardarlo de todos modos?',
  voidOrderAsk: 'El pedido #{no} no se ha entregado. Después de anular:',
  deleteOrderAsk: 'El pedido #{no} no se ha entregado. Después de borrar la factura:',
  voidOrderCancel: 'Cancelar el pedido: no sale nada (se quita de la lista de surtido y de la ruta)',
  voidOrderKeep: 'Conservar el pedido: sí sale y se factura de nuevo',
  cashShortBy: 'faltan {amount}',
  cashOverBy: 'sobran {amount}',
  routeCashShortBank: '{route} cobró {collected} en efectivo y se entregaron {counted}: faltan {short}. El efectivo abajo es lo que pagaron los clientes; lleve {counted} al banco y registre los {short} como faltante de caja en QuickBooks.',
  paysOpening: '{amount} de esto paga el saldo inicial (se deben {owed} de antes del inicio del libro).',
  shrinkOverLot: 'El lote {lot} tiene {have}. ¿Dar de baja {n}? Salen los {have} del lote y el resto sale del inventario sin lote.',
  lineKeptAsIs: 'se queda como está',
  orderChangedMeanwhile: 'Alguien cambió este pedido desde que lo abrió. Cárguelo de nuevo antes de guardar.',
  reloadOrder: 'Cargar de nuevo',
  ordersEveryDay: 'Todos los días: los pedidos que coinciden con lo escrito (la entrega más reciente primero).',
  vsPlace: 'Hacer pedido',
  buyFromPriceFar: '{price} es más del doble o menos de la mitad de lo que cuesta ahora ({cost}). Un precio que usted da mueve los precios automáticos de inmediato. ¿Es correcto?',
  soldByUnit: 'vendido por {unit}',
  qboSoldAs: 'Se vende por',
  qboSoldAsHint: 'Qué es una cantidad de QuickBooks: la caja del producto, o libras o piezas de ella ("Jalapeno - weight" es lb). El producto necesita lo que trae una caja.',
  qboSoldAsBox: 'caja',
  qboSoldAsLb: 'lb',
  qboSoldAsEa: 'pieza',
  qboSoldAsDz: 'docena',
  qboSoldAsBn: 'manojo',
  qboSoldAsConfirm: '¿Cambiar cómo se vende {name}? Sus facturas se leen otra vez en la nueva unidad y el inventario que tomaron se toma otra vez.',
  unitLb: 'lb',
  unitEa: 'pieza',
  unitBn: 'manojo',
  unitDz: 'docena',
  pricedOnSave: 'precio al guardar',
  activityTitle: 'Actividad',
  activityHint: 'Quién hizo qué: cada pedido, factura, orden de compra, conteo, precio y cambio, con la persona que lo hizo. Los cambios automáticos (la copia de QuickBooks, los trabajos nocturnos) solo si se marcan.',
  activityPerson: 'Persona',
  activityPersonNone: 'Esta persona (sin cambios en estos días)',
  activityEveryone: 'Todos',
  activityArea: 'Área',
  activityAllAreas: 'Todas las áreas',
  activitySystem: 'Incluir cambios automáticos',
  activityAutomatic: 'Automático',
  activityNone: 'Nada hecho en este periodo.',
  activityOlder: 'Ver anteriores',
  activityWhen: 'Cuándo',
  activityWhat: 'Qué',
  activityWhich: 'Cuál',
  actTest: 'PRUEBA',
  act_created: 'creó',
  act_changed: 'cambió',
  act_deleted: 'borró',
  act_placed: 'hizo',
  act_received: 'recibió',
  act_voided: 'anuló',
  act_cancelled: 'canceló',
  act_confirmed: 'confirmó',
  act_picked: 'surtió',
  act_delivered: 'entregó',
  act_invoiced: 'facturó',
  act_closed: 'cerró',
  act_published: 'publicó',
  act_counted: 'contó',
  actArea_sales: 'Ventas',
  actArea_buying: 'Compras',
  actArea_stock: 'Inventario',
  actArea_prices: 'Precios',
  actArea_customers: 'Clientes',
  actArea_products: 'Productos',
  actArea_delivery: 'Entregas',
  actArea_admin: 'Administración',
  actArea_other: 'Otro',
  actThing_orders: 'Pedido',
  actThing_order_lines: 'Línea de pedido',
  actThing_invoices: 'Factura',
  actThing_invoice_adjustments: 'Línea de factura',
  actThing_payments: 'Pago',
  actThing_credits: 'Crédito',
  actThing_purchase_orders: 'Orden de compra',
  actThing_purchase_order_lines: 'Línea de orden de compra',
  actThing_receipts: 'Recepción',
  actThing_inventory_counts: 'Conteo',
  actThing_inventory_movements: 'Movimiento de inventario',
  actThing_price_lists: 'Lista de precios',
  actThing_items: 'Producto',
  actThing_customers: 'Cliente',
  actThing_vendors: 'Proveedor',
  actThing_vendor_offers: 'Oferta de proveedor',
  actThing_vendor_items: 'Código de proveedor',
  actThing_qbo_links: 'Vínculo de QuickBooks',
  actThing_users: 'Usuario',
  actThing_lost_sales: 'Venta perdida',
  actThing_customer_activities: 'Nota del cliente',
  actThing_routes: 'Ruta',
  actThing_cost_observations: 'Costo',
  priceTierLabel: 'Nivel de precio',
  priceTierConfirm: '¿Pasar a {name} a {tier}? Sus pedidos sin facturar toman los nuevos precios.',
  productCategory: 'Categoría',
  productCategoryNone: 'Sin categoría',
  vsCostFarAsk: 'Estos costos están a más de 25% del costo de la línea. Un costo que usted da mueve los precios automáticos y los pedidos abiertos de inmediato. ¿Es correcto?\n\n{list}',
  vsPlaceConfirm: '¿Hacer el pedido de {n} cajas con {vendor}?',
  costFrom: 'de {vendor}',
  vsReopen: 'Reabrir',
  vsReceive: 'Recibir',
  vsReceiveConfirm: '¿Recibir lo que llegó de {vendor}? RECIBIDO en blanco cuenta como todo, 0 como nada; lo que no llegó va a faltantes.',
  vsReceiveHint: 'Cuando llegue la confirmación o el camión, escriba lo que llegó en RECIBIDO (en blanco = todo, 0 = nada) y luego Recibir.',
  vsOrderHint: 'PEDIDO viene con lo que pide el modelo. Cambie el número que no le parezca y luego Hacer pedido.',
  vsNothing: 'Nada que pedir a este proveedor para esta carga. Marque "Todos los productos del proveedor" para agregar uno.',
  anyDay: 'Cualquier día',
  placedCol: 'Pedido',
  loadCol: 'Carga',
  orderedCol: 'Pedido',
  byCol: 'Por',
  orderHistoryTitle: 'Historial de pedidos',
  orderHistoryHint: 'Cada pedido hecho a un proveedor, línea por línea: pedido, recibido, faltante, costo. Haga clic en una línea para abrir su pestaña.',
  shortsTitle: 'Faltantes',
  editInvoice: 'Editar',
  editInvoiceTitle: 'Editar factura {no}',
  editInvoiceClosed: 'Aquí solo se edita una factura emitida hecha en la app, y una de un mes anterior solo el dueño. Una de QuickBooks se cambia en QuickBooks; una anulada no se cambia.',
  editInvoiceChanged: 'Alguien cambió esta factura después de que la abrió. Guardar ahora sería rechazado.',
  editInvoiceReopen: 'Abrirla de nuevo (lo que escribió aquí se pierde)',
  adjKind_short: 'Faltante',
  adjKind_edit: 'Cambiada',
  adjKind_add: 'Agregada',
  adjKind_remove: 'Quitada',
  shortsHint: 'Lo que los proveedores no mandaron, por carga, proveedor y producto.',
  allVendors: 'Todos los proveedores',
  ordersPlaced: 'Pedidos hechos',
  orderedValue: 'Valor pedido',
  casesShort: 'Cajas faltantes',
  shortValue: 'Valor faltante',
  vendorPricesTitle: 'Precios de proveedores',
  papersTitle: 'Papeles',
  gmailTitle: "Gmail",
  gmailTrusted: "Direcciones de proveedores de confianza",
  gmailTrustedHint: "Los papeles de estos dominios de correo entran solos al historial cuando Google confirma que el correo de verdad vino de ahí (para Mata's: mataproduce.com). Cualquier otro papel del correo espera a que toques \"Es real\".",
  gmailTrustedSave: "Guardar",
  gmailTrustedNone: "Ninguna aún: cada papel del correo espera un toque.",
  paperItsReal: "Es real",
  gmailNotSetUp: "Gmail no está configurado en este servidor (GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET).",
  gmailNotConnected: "Conecta tu correo de negocio una vez: cada hora la app lee los correos con la etiqueta \"Vendor papers\" y deja aquí cada PDF o foto. Solo lee; nunca envía, mueve ni borra un correo.",
  gmailConnect: "Conectar Gmail",
  gmailConnected: "Leyendo {email}, etiqueta \"{label}\"",
  gmailLastRead: "Última lectura {when}: {what}",
  gmailNeverRead: "Aún no se ha leído: la primera lectura es dentro de la hora, o toca Leer ahora.",
  gmailReadNow: "Leer ahora",
  gmailDisconnect: "Desconectar",
  gmailBack_connected: "Gmail está conectado.",
  gmailBack_declined: "Gmail no se conectó: el acceso fue rechazado en Google.",
  gmailBack_google: "Google no pudo conectar el correo. Intenta de nuevo.",
  gmailBack_state: "La conexión expiró o vino de otra ventana. Toca Conectar Gmail de nuevo.",
  gmailBack_signin: "Entra como dueño y toca Conectar Gmail de nuevo.",
  gmailBack_exchange: "Google no entregó el acceso. Toca Conectar Gmail de nuevo (permite leer el correo).",
  papersHint: 'Facturas y listas de precios de proveedores en bloque. Suéltalas aquí; el lector las lee una por una, las más nuevas primero. La que cuadra entra sola al historial de precios y compras y nunca es una cuenta por pagar; la que no cuadra espera aquí con el motivo.',
  papersDrop: 'Suelta aquí PDFs o fotos, o toca para elegir (varios a la vez)',
  papersUploading: 'Enviando {n} de {total}…',
  papersUploaded: '{added} agregados, {dupes} ya estaban, {failed} sin enviar',
  paperWaiting: 'En espera',
  paperReading: 'Leyendo',
  paperPosted: 'En historial',
  paperHeld: 'Revisar',
  paperSkipped: 'Omitido',
  paperFailed: 'No leído',
  papersAll: 'Todos',
  callAheadAdd: "Agregar una columna",
  callAheadRemove: "— quitar la columna —",
  buyFromTitle: "{item}: comprarlo para esta carga con",
  buyFromHint: "Elige el proveedor con quien esta carga lo compra: las cajas van a la orden de ese proveedor para la carga, y la otra pestaña las cuenta como compradas.",
  buyFromPick: "elegir proveedor",
  buyFromCases: "cajas",
  buyFromPrice: 'Precio ($)',
  buyFromBadPrice: 'Escriba el precio como número, por ejemplo 18',
  vsNoCostAsk: 'Sin costo en {n} línea(s) ({items}): el costo no seguirá este pedido para ellas. ¿Hacer el pedido de todos modos?',
  buyFromAlways: "Comprarlo con este proveedor de ahora en adelante",
  buyFromGo: "Ponerlo en esa orden",
  buyFromSaid: "Se agregaron {n} {item} a la orden de {vendor} para esta carga.",
  buyFromBadQty: "Escribe las cajas como número.",
  callAheadKeep: "Mantener esta columna cada semana",
  callAheadUnkeep: "— dejar de mantenerla (sigue mientras tenga orden) —",
  buyFromOnOrders: "En órdenes de esta carga:",
  buyFromPlaced: "enviada",
  buyFromTakeOff: "Quitar estas cajas de la orden de {vendor} (tiene {n})",
  buyFromHomePlaced: "La orden de {vendor} para esta carga ya se envió con {n}: estas cajas se compran además de esas. Para moverlas, primero reabre esa orden en su pestaña.",
  heldTitle: "Por qué hay que revisarlos",
  papersRecheck: "Revisar todos otra vez (gratis)",
  papersRecheckHint: "Revisa otra vez cada papel por revisar con lo que ya se leyó, sin costo: después de nombrar o agregar un proveedor, o agregar una unidad.",
  papersRecheckSaid: "{posted} entraron al historial · {held} todavía por revisar",
  heldKeep: "{n} de los que el lector solo dudó (su propia nota, un precio lejos del último, o un total con flete o cargos)",
  heldVendor: "“{name}”: no se reconoció el proveedor ({n})",
  heldVendorHint: "Elige quién es una vez: estos entran ahora, y los papeles futuros con este nombre se reconocen.",
  heldVendorSave: "Es este proveedor",
  heldUnit: "{n} con una unidad que la app no conoce",
  heldUnitHint: "Unidades: {units}. Mándaselas a Claude; cuando se agreguen, toca Revisar todos otra vez.",
  heldSender: "{n} de una dirección no confirmada como del proveedor",
  heldSenderHint: "Abre cada uno; si de verdad es del proveedor, toca Es real en su línea.",
  heldLine: "{n} con una línea que no cuadra (cantidad × precio)",
  heldLineHint: "El lector leyó mal un número. Leerlos otra vez casi siempre lo arregla (unos centavos cada uno).",
  heldLineAgain: "Leer estos {n} otra vez",
  heldOther: "{n} por otras razones",
  heldOtherHint: "La razón está en cada línea abajo. Abre el papel desde su nombre de archivo para decidir.",
  vendorCodesTitle: "Códigos de proveedor",
  vendorCodesHint: "Cada código que un proveedor imprime en sus facturas y listas de precios, y el producto al que está enlazado. Los precios de un código siguen su enlace: enlaza uno y sus precios pasados llegan al historial de costo del producto con sus fechas; cambia uno equivocado y se mueven. Oculta un código que no es un producto (flete, un cargo).",
  navVendorCodes: "Códigos de proveedor",
  codesFind: "Busca un código, una descripción o un producto",
  codesUnlinked: "Sin enlazar",
  codesLinked: "Enlazados",
  codesHidden: "Ocultos",
  codesNone: "No hay códigos aquí.",
  vendorCodeCol: "Código",
  vendorDescCol: "Descripción del proveedor",
  productCol: "Producto",
  lastSeenCol: "Última vez",
  papersCol: "Papeles",
  codeNotLinked: "Sin enlazar",
  codeLink: "Enlazar",
  codeChange: "Cambiar",
  codeUnlink: "Desenlazar",
  codeNewProduct: "Producto nuevo",
  codeHide: "Ocultar",
  codeShow: "Mostrar",
  codeLinkTo: "Enlazar a un producto:",
  codeLinkedSaid: "{code} → {product}. {n} precios pasados agregados a su historial de costo.",
  codeUnlinkedSaid: "{code} desenlazado: está otra vez en Nombres desconocidos.",
  codeMovedSaid: "{code} → {product}: sus precios se movieron con él.",
  codeUnlinkAsk: "¿Desenlazar {code} de {product}? Sus precios dejan el producto y el código vuelve a Nombres desconocidos.",
  codeHiddenSaid: "{code} oculto: no es un producto.",
  codeShownSaid: "{code} se muestra otra vez.",
  codeAdd: "Agregar un código",
  codeAddHint: "El código como el proveedor lo imprime en la factura.",
  codeLinkedHere: "Enlazado a este producto: sus precios cuentan aquí.",
  codeNewFrom: "Producto nuevo del código de proveedor {code}: se enlaza al guardar.",
  paperKeep: 'Conservar',
  papersKeepAll: 'Conservar los {n} de esta página',
  papersKeepHint: 'Conservar es tu palabra por un papel del que el lector solo tuvo una duda (su propia nota, un precio lejos del último, o un total distinto cuando cada línea cuadra sola: flete, cargos, un descuento). Sus precios entran al historial; las demás revisiones siguen. Los códigos que el lector dijo cortados no se usan: esas líneas se igualan por las palabras del artículo, y las demás van a Nombres desconocidos para enlazar.',
  papersKept: '{posted} conservados en el historial · {held} todavía por revisar',
  papersShowing: '{from}–{to} de {total}',
  papersNewer: 'Más nuevos',
  papersOlder: 'Más viejos',
  papersPause: 'Pausar lectura',
  papersResume: 'Reanudar lectura',
  papersPaused: 'La lectura está en pausa: no se lee nada hasta que el dueño la reanude.',
  papersNoReader: 'El lector no está configurado en este servidor (ANTHROPIC_API_KEY): los papeles esperan hasta que lo esté.',
  papersStopped: 'La lectura se detuvo: {why}',
  oldMailTitle: 'Correo viejo (Google Takeout)',
  oldMailHint: 'Suelta el archivo .mbox de tu descarga de Google Takeout (abre el .zip; está en Takeout › Mail). Los papeles adjuntos a correos de los últimos dos años entran y se leen como los del correo: los de un proveedor de confianza confirmado entran solos al historial. Spam y Papelera se saltan; cada papel leído cuesta unos centavos. Deja esta página abierta hasta que diga Listo. Si se detiene, suelta el mismo archivo otra vez: lo que ya entró se salta.',
  oldMailPick: 'Elige el archivo .mbox',
  oldMailRunning: 'Leyendo {read} de {total} MB · {mails} correos · {sent} papeles enviados',
  oldMailTally: '{sent} papeles enviados · {known} ya estaban · {old} correos de hace más de dos años · {junk} en Spam o Papelera · {refused} no aceptados · {failed} no enviados',
  oldMailNoTrusted: 'No hay un dominio de proveedor de confianza guardado en la tarjeta de Gmail: cada papel del correo viejo esperaría a "Es real". Guarda primero ahí el dominio del proveedor.',
  oldMailDone: 'Listo.',
  oldMailStopped: 'Detenido. Suelta el mismo archivo otra vez para seguir.',
  oldMailStop: 'Detener',
  oldMailNotMbox: 'Ese no es un archivo de correo de Google Takeout (.mbox). Abre el .zip de Takeout y toma el archivo dentro de Takeout › Mail.',
  papersCost: 'Cada papel leído cuesta unos centavos de crédito de Anthropic (una lista larga hasta unos 30 centavos).',
  paperReadAgain: 'Leer de nuevo',
  papersRetryFailed: 'Leer de nuevo todos los no leídos',
  docHistory: 'Historial',
  paperOpenDoc: 'Abrir',
  paperFileCol: 'Archivo',
  paperReceivedCol: 'Recibido',
  paperStatusCol: 'Estado',
  paperWhyCol: 'Motivo',
  papersNone: 'Aún no hay papeles. Suelta los primeros arriba.',
  vendorPricesHint: 'Cada precio que nos dio un proveedor: ofertas por mensaje, listas de precios, facturas y lo que llegó en el camión. El precio del proveedor, sin flete.',
  latestByItem: 'Último por producto',
  allPrices: 'Todos los precios',
  changeHint: 'La lista de precios más reciente del proveedor contra la anterior: sube, baja o igual',
  priceSame: 'igual',
  wasPrice: 'lista anterior',
  notOnLatestList: 'No está en la lista del {date}: no viene, o su código no está enlazado a este producto',
  latestListLine: '{vendor}: lista de precios más reciente {date}',
  staleCount: '{n} producto(s) no vienen en ella (fechas en ámbar)',
  allFromLatest: 'todos los productos son de ella',
  unlinkedOnList: '{n} líneas de ella no están enlazadas a un producto',
  spreadCol: 'Diferencia',
  spreadHint: 'Qué tan lejos están los últimos precios de los proveedores, sobre el más barato',
  sourceCol: 'De',
  nItems: '{n} productos',
  nPrices: '{n} precios',
  pricesTruncated: 'Solo se muestran los 5,000 precios más recientes: elija un periodo más corto o un proveedor.',
  priceSrc_offer: 'Oferta por mensaje',
  priceSrc_list: 'Lista de precios',
  priceSrc_invoice: 'Factura del proveedor',
  priceSrc_receipt: 'Camión (recibo)',
  priceSrc_manual: 'Capturado a mano',
  priceSrc_import: 'Importado',
  vendorBills: 'Facturas de proveedores',
  vendorBillsHint: 'Lo que los proveedores nos facturan o acreditan: facturas, notas de crédito, estados de cuenta y otros documentos. Una factura con total y vencimiento es una cuenta por pagar.',
  vendorPriceLists: 'Listas de precios de proveedores',
  vendorPriceListsHint: 'Los precios que nos cotizan los proveedores. Cada línea con producto conocido se vuelve un costo en el libro.',
  enterVendorBill: 'Capturar una factura de proveedor',
  enterVendorPriceList: 'Capturar una lista de precios',
  dateCol: 'Fecha',
  costsRecordedCol: 'Costos registrados',
  unmappedCol: 'Sin producto',
  totalCol: 'Total',
  openCol: 'Pendiente',
  photoCol: 'Foto',
  billDueCol: 'Vence',
  docNoCol: 'Número',
  vendorDocsHint: 'Cada precio que nos da un proveedor, guardado: captura las líneas, adjunta la foto, y el costo se actualiza.',
  enterVendorDoc: 'Capturar un documento de proveedor',
  docType: 'Tipo',
  doc_invoice: 'Factura',
  doc_price_list: 'Lista de precios',
  doc_credit_memo: 'Nota de crédito',
  doc_statement: 'Estado de cuenta',
  doc_other: 'Otro',
  docNumber: 'Número impreso',
  docDate: 'Fecha',
  costsRecorded: 'costos registrados',
  unmappedLines: 'aún no es producto',
  unmappedLine: 'aún no es producto',
  descriptionAsPrinted: 'Descripción como está impresa',
  vendorCode: 'Código del proveedor',
  whichItem: '¿Qué producto?',
  photo: 'Foto o PDF',
  photoAttached: 'foto adjunta',
  noPhoto: 'Aún sin foto.',
  attachPhoto: 'Tomar foto / adjuntar',
  intelTitle: 'Inteligencia de precio',
  intelLink: 'Análisis de precio',
  phLink: 'Historial de precios',
  phTitle: 'Historial de precios',
  phHint: 'Lo que pagamos, lo que fijamos y lo que cobramos, en cualquier periodo, antes de créditos. Cada precio se guarda para siempre. Los márgenes cuentan las líneas con costo conocido (Costo conocido).',
  phSrc_order_cancelled: 'Pedido hecho (cancelado)',
  phSrc_order_replaced: 'Pedido hecho (se volvió a hacer)',
  phSrc_order_reopened: 'Pedido hecho (reabierto, sin hacer aún)',
  phCharged: 'Lo que cobramos, por semana',
  phCustomers: 'Lo que pagó cada cliente',
  phPaid: 'Lo que pagamos',
  phSet: 'Lo que fijamos',
  phNone: 'Nada en este periodo',
  phWeek: 'Semana del',
  phPacks: 'Empaques',
  phAvgPrice: 'Precio prom.',
  phAvgCost: 'Costo prom.',
  phLastPrice: 'Último precio',
  phDate: 'Fecha',
  phVendor: 'Proveedor',
  phSource: 'De',
  phVendorPrice: 'Precio del proveedor',
  phFreight: 'Flete',
  phFor: 'Para',
  phPrice: 'Precio',
  phCostBasis: 'Costo entonces',
  phNote: 'Nota',
  phList: 'Lista de precios',
  phRule: 'Precio del cliente',
  phMarginRule: 'margen',
  phUntil: 'hasta',
  phEnded: 'terminó',
  phSrc_receipt: 'Muelle',
  phSrc_invoice: 'Factura',
  phSrc_list: 'Lista de precios',
  phSrc_manual: 'Escrito',
  phSrc_import: 'Importado',
  phSrc_order: 'Pedido hecho',
  thisWeek: 'Esta semana',
  sold7: 'Vendido, últimos 7 días',
  sold28: 'Vendido, últimas 4 semanas',
  marginNowBefore: 'Margen, últimas 4 semanas · las 4 anteriores',
  was: 'antes',
  costWeeks: 'Costo semanal, últimas 12 semanas',
  soldFor: 'Vendido a',
  soldWeeks: 'Precio de venta semanal, últimas 12 semanas',
  whoPaidWhat: 'Último precio por cliente',
  competitors: 'Precios de la competencia',
  noCompetitorPrices: 'Nada registrado aún. Cada precio que veas u oigas va aquí.',
  seenPrice: 'Registrar un precio que viste',
  competitor: 'Competidor',
  seenWhere: 'Dónde lo viste',
  cps_customer_invoice: 'Factura del cliente',
  cps_quote: 'Cotización que le dieron',
  cps_price_list: 'Lista de precios',
  cps_shelf: 'Anaquel de tienda',
  cps_word_of_mouth: 'De boca en boca',
  cps_other: 'Otro',
  recordSeenPrice: 'Registrarlo',
  standingOrder: 'Pedido fijo',
  standingHint: 'Lo que piden cada semana. La app lo prepara para cada día de entrega; la oficina lo confirma con un toque.',
  retireStanding: 'Retirar este pedido fijo',
  standingSummary: 'Registrado',
  packs: 'cajas',
  standingDue: 'Pedidos fijos para',
  draftStanding: 'Crear borradores',
  drafted: 'preparados',
  confirmOrder: 'Confirmar',
  cancelOrder: 'Cancelar pedido',
  cancelWhy: '¿Por qué se cancela?',
  markDelivered: 'Entregado',
  markOut: 'En camino',
  profitTitle: 'Rentabilidad',
  profitHint: 'Venta, costo y margen de las facturas emitidas. El costo es el del momento de la venta, o el vigente ese día.',
  period: 'Periodo',
  range_d30: 'Últimos 30 días',
  range_d90: 'Últimos 90 días',
  range_d365: 'Último año',
  groupBy: 'Por',
  revenue: 'Venta',
  invoicesCount: 'facturas',
  withCost: 'con costo',
  costCoverage: 'Costo conocido para',
  costCoverageHint: 'El resto aún no tiene costo registrado, así que el margen es parcial.',
  reports: 'Reportes',
  count: 'Contar',
  receive: 'Recibir',
  pick: 'Surtir',
  lots: 'Lotes',
  receiveTitle: 'Recepción',
  receiveHint: 'Lo que bajó del camión, por caja. Cada línea es un lote.',
  vendorTicket: 'Ticket / factura del proveedor',
  addItem: 'Agregar producto',
  unitCost: 'Costo por caja',
  docLines: 'Líneas del documento',
  costNow: 'Costo actual',
  costNowHint: 'El costo del producto en la app hoy (el más nuevo de cualquier proveedor), para compararlo con este documento',
  costChange: 'Cambio',
  linesNotLinked: '{n} sin producto',
  grade: 'Calidad',
  grade_good: 'Buena',
  grade_fair: 'Regular',
  grade_reject: 'Rechazo',
  origin: 'Origen',
  vendorLot: 'Lote del proveedor',
  received: 'Recibido',
  receivedPacks: 'cajas recibidas',
  lot: 'Lote',
  lotCode: 'Lote',
  ageDays: 'días',
  oldestFirst: 'Del más viejo al más nuevo: surte desde arriba.',
  noLots: 'Sin lotes con existencia',
  recordShrink: 'Registrar una pérdida',
  shrinkReason: 'Por qué',
  shrink_age: 'Vejez',
  shrink_damaged: 'Dañado',
  shrink_temperature: 'Temperatura',
  shrink_over_ordered: 'Se pidió de más',
  shrink_transfer: 'Perdido en traspaso',
  shrink_short_dated: 'Llegó con poca vida',
  shrink_sample: 'Muestra / regalado',
  shrink_other: 'Otro',
  shrinkSaved: 'Pérdida registrada',
  pickTitle: 'Lista de surtido',
  prepTab: 'Tarjeta de preparación',
  prepTitle: 'Tarjeta de preparación',
  prepHint: 'Las facturas con fecha de estos días (las de QuickBooks y las de la app) como las arma el equipo de surtido. Marque las que va a imprimir y oprima Imprimir tarjeta: una hoja carta doblada en cuatro, TOTALES primero y luego cada pedido.',
  prepFrom: 'Desde',
  prepTo: 'Hasta',
  prepYesterday: 'Ayer',
  prepToday: 'Hoy',
  prepTomorrow: 'Mañana',
  prepAll: 'Todas',
  prepNone: 'Ninguna',
  prepNotPrinted: 'Sin imprimir',
  prepCount: '{n} de {total} marcadas · {qty} cantidad total',
  prepCustomer: 'Cliente',
  prepInvoiceNo: '#',
  prepStatus: 'Estado',
  prepNew: 'nueva',
  prepPrintedAt: 'tarjeta {when}',
  prepChanged: 'CAMBIÓ desde que se imprimió',
  prepTest: 'PRUEBA',
  prepNoLines: 'sin productos',
  prepEmpty: 'No hay facturas con fecha de estos días.',
  prepPrint: 'Imprimir tarjeta',
  prepPrintHint: 'Hoja carta, impresora normal. Doble en las líneas punteadas. Si dice a dos caras, marque "Imprimir en ambas caras" (voltear por el lado largo).',
  prepFitted: '{orders} pedidos en {panels} paneles, letra de {min}–{max} pt',
  prepOneSide: 'UNA cara',
  prepTwoSided: 'imprimir A DOS CARAS (voltear por el lado largo)',
  prepSheets: '{n} hojas',
  prepSpilled: 'un pedido era muy largo para un panel y continúa',
  prepMarked: 'Enviada a la impresora y marcada como impresa.',
  prepMarkFailed: 'Se imprimió, pero no se pudo marcar como impresa:',
  prepTotals: 'TOTALES',
  prepCont: '(sigue)',
  prepTickets: 'Imprimir boletas (Zebra)',
  prepPullZebra: 'TOTALES (Zebra)',
  prepZebraHint: 'Una etiqueta de 4 × 6 por pedido, con una casilla en cada línea: entréguelas a quien surte los pedidos. En la ventana de impresión elija la Zebra, Márgenes: Ninguno, Escala: Predeterminada (100%).',
  prepTicketAt: 'boleta {when}',
  prepLabels: '{n} etiquetas',
  prepLabel1: '1 etiqueta',
  prepSent: 'Enviado a la impresora.',
  prepPulled: 'Surtió',
  prepChecked: 'Revisó',
  prepLinesQty: '{n} líneas · {q} cant.',
  prepLineQty: '1 línea · {q} cant.',
  prepOrdersN: '{n} pedidos',
  prepOrders1: '1 pedido',
  prepItemsN: '{n} productos · {q}',
  prepItems1: '1 producto · {q}',
  pickHint: 'Pedidos confirmados del día. Toma del lote más viejo. Toca Surtido cuando el pedido esté en la tarima.',
  nothingToPick: 'Nada que surtir este día',
  totals: 'Totales del día',
  short: 'faltan',
  pickedBtn: 'Surtido',
  pickedAlready: 'Surtido',
  deliveries: 'Entregas',
  fromPo: 'De un pedido de compra',
  vendor_required: 'Elige el proveedor',
  makeInvoice: 'Hacer la factura',
  invoiced: 'Facturado',
  invoiceDate: 'Fecha de factura',
  dueDate: 'Vence',
  paid: 'Pagado',
  overpaidOnAccount: 'Pagó {amount} más de lo que suma: queda como dinero a cuenta del cliente',
  onAccountAmount: '{amount} a cuenta',
  balanceDue: 'Saldo pendiente',
  print: 'Imprimir',
  voidInvoice: 'Anular esta factura',
  deleteInvoice: 'Borrar esta factura',
  deleteReason: '¿Por qué se borra?',
  deletedTag: 'Borrada',
  deleteVoidHint: 'Ya está anulada (sus cajas volvieron entonces). Borrarla la quita de toda lista, reporte y estado de cuenta, y de QuickBooks. Quién la borró, cuándo y por qué quedan en Administración → Actividad.',
  deleteInvoiceHint: 'Desaparece de toda lista, reporte y estado de cuenta, y de QuickBooks. Sus cajas vuelven al inventario. Quién la borró, cuándo y por qué quedan en Administración → Actividad.',
  voidReason: '¿Por qué se anula?',
  voidKeepCredits: 'Dejar los créditos de esta factura en la cuenta del cliente como dinero por aplicar',
  voidCreditLinesAsked: 'Esta factura tiene líneas de crédito (un producto a precio bajo cero). Al anularla se quitan también y no regresan al facturar el pedido otra vez: vuelva a dar el crédito en la próxima factura.',
  voidDropCreditLines: 'Daré el crédito otra vez en la próxima factura',
  voidCreditsAsked: 'Esta factura tiene créditos. Al anularla quedan en la cuenta del cliente: marque la casilla y anule de nuevo.',
  voided: 'Anulada',
  recordPayment: 'Registrar un pago',
  amount: 'Monto',
  method: 'Forma',
  method_cash: 'Efectivo',
  method_check: 'Cheque',
  method_card: 'Tarjeta',
  method_ach: 'Transferencia',
  method_zelle: 'Zelle',
  method_qbo_payments: 'QuickBooks Payments',
  method_other: 'Otro',
  method_applied: 'Aplicado de la cuenta',
  method_credit_memo: 'Nota de crédito (QuickBooks)',
  paymentInvoices: 'Facturas que paga este pago',
  paymentCol: 'Pago',
  appliedTotal: 'Aplicado',
  leftOnAccount: 'Queda a cuenta',
  moreThanReceived: 'Las facturas suman más que lo recibido',
  moreThanOwes: 'Eso es más de lo que debe la factura',
  applyOnAccount: 'Aplicar dinero a cuenta',
  applyOnAccountHint: '{amount} está a cuenta y en ninguna factura. Paga primero las facturas más antiguas.',
  appliedSaved: 'Aplicado a facturas',
  reverseGroup: 'Revierte el pago completo ({n} líneas)',
  reference: 'No. de cheque / referencia',
  applyTo: 'Aplicar a',
  onAccount: 'A cuenta (sin factura)',
  paymentSaved: 'Pago registrado',
  reverse: 'Revertir',
  reverseWhy: '¿Por qué se revierte?',
  giveCredit: 'Dar un crédito',
  creditReason: 'Motivo',
  reason_quality: 'Calidad',
  reason_short: 'Faltante',
  reason_price: 'Precio',
  reason_returned: 'Devolución',
  reason_goodwill: 'Cortesía',
  reason_other: 'Otro',
  creditSaved: 'Crédito aplicado',
  statement: 'Estado de cuenta',
  openInvoices: 'Facturas abiertas',
  daysLate: 'días de atraso',
  onTime: 'aún no vence',
  payments: 'Pagos',
  credits: 'Créditos',
  ledgerNotStarted: 'Los saldos empiezan cuando el dueño fija el inicio del libro (Finanzas, Libro y saldos iniciales); lo que cada cliente debía ese día se captura ahí como su saldo inicial.',
  startLedger: 'Contar saldos desde',
  billTo: 'Facturar a',
  thankYou: 'Gracias por su preferencia',
  unpricedLines: 'Cada línea necesita precio antes de facturar',
  source_invoice: 'Factura',
  source_clover: 'Caja',
  source_vendor: 'Proveedor',
  untilDate: 'hasta',
  margin: 'margen',
  // sites and transfers
  site: 'Sitio',
  sites: 'sitios',
  sitesTitle: 'Sitios y empresas',
  sitesHint: 'Cada lugar donde hay mercancía: la bodega, las tiendas, los camiones. Una tienda de otra empresa compra a la bodega por traspasos.',
  addSite: 'Agregar sitio',
  siteCode: 'Código',
  kind_hub: 'Bodega',
  kind_store: 'Tienda',
  kind_truck: 'Camión',
  company: 'Empresa',
  companies: 'Empresas',
  companyCode: 'Código',
  homeCompany: 'Nuestra empresa',
  addCompany: 'Agregar empresa',
  address: 'Dirección',
  storeOfOtherCompanyHint: 'Una tienda de otra empresa recibe una ficha de cliente: sus traspasos se cobran con esa ficha y llegan como pedidos entregados, listos para facturar.',
  billsAsCustomer: 'Se cobra como cliente',
  closeSite: 'Cerrar',
  reopenSite: 'Reabrir',
  transfers: 'Traspasos',
  transfersHint: 'Mercancía que se mueve entre sitios. Al enviarlo sale de los lotes de aquí; al recibirlo se vuelve lotes allá.',
  oneSiteOnly: 'Solo existe la bodega. El dueño agrega tiendas y camiones en Sitios y empresas.',
  newTransfer: 'Nuevo traspaso',
  noTransfers: 'Aún no hay traspasos',
  fromSite: 'De',
  toSite: 'A',
  status_sent: 'Enviado',
  sendTransfer: 'Enviar',
  receiveAll: 'Llegó completo',
  receiveShort: 'Llegó incompleto',
  confirmReceived: 'Confirmar lo que llegó',
  qtyReceived: 'Recibido',
  oldestLotFirst: 'El lote más viejo primero',
  intercompanyOrder: 'Pedido',
  saveDraft: 'Guardar borrador',
  // sign-in with a password, users
  loginLabel: 'Teléfono, correo o usuario',
  username: 'Usuario',
  usernameRule: 'Una letra primero; de 2 a 30 letras, números, puntos o guiones',
  password: 'Contraseña',
  loginNeeded: 'Escribe tu teléfono, correo o usuario y tu contraseña',
  phoneOrEmail: 'Teléfono o correo',
  usernameOptional: 'Usuario (opcional)',
  wrongLogin: 'Teléfono, correo o contraseña incorrectos',
  signInWithCode: 'Entrar con un código por texto',
  signInWithPassword: 'Entrar con contraseña',
  usersTitle: 'Usuarios',
  usersHint: 'Las personas que entran al sistema. Agrega a alguien con una primera contraseña y dísela en persona; la app le pide elegir la suya la primera vez.',
  addPerson: 'Agregar persona',
  role: 'Rol',
  userRole_owner: 'Dueño',
  userRole_office: 'Oficina',
  userRole_warehouse: 'Bodega',
  userRole_driver: 'Chofer',
  userRole_sales: 'Ventas',
  userRole_readonly: 'Solo lectura',
  homeSite: 'Sitio de inicio',
  anySite: 'La bodega',
  firstPassword: 'Primera contraseña',
  setPassword: 'Poner contraseña',
  resetPassword: 'Restablecer contraseña',
  newPassword: 'Nueva contraseña',
  currentPassword: 'Contraseña actual',
  changePassword: 'Cambiar contraseña',
  passwordChanged: 'Contraseña cambiada. Úsala la próxima vez que entres.',
  passwordRule: 'Mínimo 8 caracteres.',
  passwordsDiffer: 'Las dos contraseñas no son iguales',
  repeatPassword: 'Repite la nueva contraseña',
  mustChangeHint: 'Tu contraseña fue restablecida: elige una nueva ahora.',
  noPasswordNudge: 'Pon una contraseña para entrar sin código por texto.',
  lastSeen: 'Última vez',
  never: 'nunca',
  switchOff: 'Desactivar',
  switchOn: 'Activar',
  noPasswordYet: 'sin contraseña',
  saved: 'Guardado',
  switchedOff: 'Inactivo',
  // the prices screen and the price list
  pricesTitle: 'Gestión de precios',
  pricesHint: 'Cada artículo activo con su costo, el precio más bajo de la competencia y una columna por nivel. Escribe sobre el precio de un nivel; la columna Motor es lo que pondrían el costo y el margen del nivel. Publicar escribe una lista de precios con fecha.',
  soldAvgCol: 'Vendido prom. 28 d',
  competitorLowCol: 'Competencia mín.',
  engineCol: 'Motor',
  gmCol: 'MB %',
  changesCol: 'Cambios',
  onlyChanged: 'Solo filas cambiadas',
  publishN: 'Publicar {n} precio(s)',
  publishConfirm: '¿Publicar {n} precio(s)? Las facturas los cobran desde hoy ({floor} bajo el piso).',
  pricesUnsaved: 'Hay precios escritos sin publicar. ¿Salir de todos modos?',
  itemsCol: 'Artículos',
  packsSold: '{n} bultos vendidos',
  typedHidden: '{n} precio(s) escrito(s) ocultos por la búsqueda',
  vsPrior30: 'vs 30 d anteriores',
  customersHint: 'Cada cuenta con su lista de precios y etapa, ventas y margen bruto de los últimos 30 días, cómo están pidiendo, ventas de 90 días, la última factura, el saldo abierto y lo vencido. Los totales comparan estos 30 días con los 30 anteriores.',
  sales30Col: 'Ventas 30 d',
  trendCol: 'Tendencia',
  customersTabAccounts: 'Cuentas',
  customersTabTrends: 'Cómo están pidiendo',
  customersTabFollowups: 'Seguimiento',
  trendHint: 'Cómo están pidiendo: las últimas 4 semanas contra el promedio de 4 semanas de las 8 anteriores (la misma tendencia que la pestaña Cómo están pidiendo)',
  itemsHint: 'Cada producto con su SKU, proveedor, costo vigente, empaque, caja y de dónde viene este mes. Los precios están en Gestión de precios.',
  costDateCol: 'Fecha del costo',
  noCostCol: 'sin costo',
  showInactive: 'Mostrar inactivos',
  countGridHint: 'Escribe cuántos hay en la cámara en Conteo, en la unidad del producto, con decimales para una abierta (2.5 es dos y media). Guardar registra el conteo; deja en blanco lo que no cuentes.',
  countedCol: 'Contado',
  varianceCol: 'Diferencia',
  openLeftCol: 'Abierta, queda',
  saveCountN: 'Guardar {n} conteo(s)',
  typedCol: 'Escritos',
  netVariance: 'Diferencia neta',
  countUnsaved: 'Hay conteos escritos sin guardar. ¿Salir de todos modos?',
  countInvalid: 'Corrige primero los conteos en rojo: un conteo es un número como 3 o 2.5.',
  cannotCount: 'Por corregir',
  unappliedCol: 'Sin aplicar',
  paymentsAndCredits: 'Pagos y créditos',
  unappliedHint: 'Pagos a cuenta y créditos no ligados a una factura; bajan el saldo',
  collectionCol: 'Cobranza',
  reportTotal: 'Total',
  overdueTotal: 'Vencido',
  billNoCol: 'Factura no.',
  billDateCol: 'Fecha',
  notDue: 'No vence aún',
  dueToday: 'Vence hoy',
  daysLateN: '{n} d de atraso',
  vendorsCol: 'Proveedores',
  payHint: 'Selecciona una factura y presiona Enter (o doble clic) para pagarla.',
  payBillTitle: 'Pagar {vendor} {no}',
  close: 'Cerrar',
  claudeTitle: 'Claude',
  claudeWorking: 'Claude está en eso…',
  claudeTalk: 'Hablar con Claude',
  claudeStop: 'Dejar de escuchar',
  claudeListening: 'Escuchando… toca al terminar',
  claudeFilled: 'Llenado por Claude: revisa cada línea y luego guarda.',
  claudeYou: 'Tú:',
  boxCol: 'Caja',
  boxHint: 'La caja como se compra: cuánto pesa llena y cuántas piezas trae',
  countShort: 'pzs',
  originNow: 'Viene de ahora',
  originNowHint: 'De dónde se surte este mes (se pone en el producto, por temporada)',
  boxBlock: 'La caja',
  boxWeight: 'Caja llena pesa (lb)',
  boxCount: 'Piezas por caja',
  sourcingBlock: 'De dónde viene',
  sourcingHint: 'Por temporada: la región de donde viene y los meses. La lista muestra la de este mes.',
  sourcingNone: 'Sin poner todavía.',
  addSource: '+ Agregar una región',
  regionLabel: 'Región',
  regionPlaceholder: 'Michoacán, México',
  monthsLabel: 'Meses',
  sourceVendor: 'Normalmente de',
  anyVendor: 'Cualquier proveedor',
  sourceNote: 'Nota',
  vendorsPriced: 'Proveedores que le dieron precio',
  vendorsNone: 'Todavía no hay precio de proveedor.',
  pricesCount: '{n} precios',
  priceOne: '1 precio',
  receiveQtyBad: 'Cada línea necesita una cantidad mayor que 0: escríbela o quita la línea',
  receiveCostBad: 'Un costo no es un número: corrígelo o déjalo en blanco',
  consentNotePlaceholder: 'lo pidió en el mostrador el lunes',
  newRoutePlaceholder: 'Ruta 2',
  boxWeightBad: 'Escribe las libras como número, por ejemplo 25 o 40.5',
  boxCountBad: 'Escribe un número entero de piezas, por ejemplo 48',
  sourceHalf: 'Ponle los meses a esta región (o quita la fila)',
  pricesListLink: 'Abrir la lista de precios',
  navPriceLists: 'Listas de precios',
  navPinned: 'Fijadas',
  navPin: 'Fijar arriba',
  navUnpin: 'Quitar de arriba',
  seasonBlock: 'Temporadas',
  seasonHint: 'De nuestras facturas, ventas de caja y precios de proveedores, por mes del año (promedio por año).',
  seasonSold: 'Vendido al año',
  seasonRetail: 'Caja (unidades)',
  seasonPrice: 'Precio prom.',
  seasonCost: 'Costo prom.',
  seasonNoData: 'Todavía sin ventas.',
  pricesLiveOnPm: 'Los precios están en Gestión de precios.',
  claudeListeningShort: 'Escucha',
  claudeTapWhenDone: 'Toca Claude cuando termines.',
  claudeTapToSend: 'Toca para enviar',
  claudePausedHint: 'El teléfono apagó el micrófono. Toca Claude para enviar lo que escuchó.',
  claudeSayIt: 'Dilo ahora…',
  claudeNoSpeech: 'No se escuchó nada. Toca Claude y habla.',
  claudeMicBlocked: 'El micrófono está bloqueado: permítelo para este sitio en el navegador (el candado junto a la dirección) y toca Claude otra vez.',
  confirmReverse: '¿Revertir el pago de {amount} a {vendor}? Una reversión no se deshace; el pago se tendría que capturar de nuevo.',
  shareCol: 'Participación',
  grossMarginCol: 'Margen bruto',
  costKnownCol: 'Costo conocido',
  registerCol: 'Mostrador',
  registerMarginCol: 'Margen de mostrador',
  registerTitle: 'Mostrador (Clover)',
  registerHint: 'Lo que vendió el mostrador en el periodo, al costo de la caja de ese día por la parte de una caja que es cada venta; el margen es sobre las ventas con costo. Se muestra junto a las facturas, nunca sumado a ellas.',
  gapNoConvert: 'Se vende en una unidad que el contenido de la caja no convierte (libras de una caja contada en piezas, o al revés): revise la unidad de sus ventas o lo que trae una caja.',
  gapsTitle: 'Por qué algunas ventas no tienen margen',
  gapsHint: 'Arregle esto y el margen cubre más de sus ventas. Lo más grande primero.',
  gapNoProduct: 'en ventas de facturas no tienen producto (líneas: {n}).',
  gapRegisterNoProduct: 'en ventas de mostrador no tienen producto (líneas: {n}).',
  gapLinkMatching: 'Vincular productos de QuickBooks',
  gapLinkInbox: 'Nombrar nombres desconocidos',
  linkingTitle: 'Hoja de vínculos',
  linkingHint: 'Todo lo que aún no está vinculado a un producto (códigos de proveedor, productos de QuickBooks, nombres en ventas) en un archivo de Excel. Llénelo en Excel a su ritmo y súbalo aquí.',
  linkingStep1: '1. Descargue la hoja',
  linkingStep1Hint: 'Una pestaña para cada uno: Códigos de proveedor, QuickBooks, Nombres en ventas, Contenido de la caja (lo que trae una caja, para ventas por libra o pieza). Donde las palabras coinciden, la columna Producto ya trae una sugerencia, marcada "guess: check it".',
  linkingDownload: 'Descargar la hoja de vínculos',
  linkingMaking: 'Preparando la hoja…',
  linkingStep2: '2. Llene la columna Producto',
  linkingStep2Hint: 'Elija el producto de la lista en la columna amarilla, o elija "ignore" para lo que no es un producto (flete, un cargo, un depósito). Deje una fila en blanco para saltarla. Revise las sugerencias. No tiene que terminar: suba lo que tenga y descargue de nuevo después.',
  linkingStep3: '3. Súbala y revise',
  linkingStep3Hint: 'Al subirla nada cambia: primero ve lo que haría cada fila y luego oprime Aplicar.',
  linkingUpload: 'Subir la hoja llena',
  linkingReading: 'Leyendo la hoja…',
  linkingPreviewTitle: 'Lo que haría',
  linkingNothingFilled: 'Ninguna fila tiene un producto.',
  linkingRowCol: 'Fila',
  linkingNameCol: 'Nombre',
  linkingProductCol: 'Producto',
  linkingDoesCol: 'Hace',
  linkingWhyCol: 'Por qué',
  linkKindCode: 'Código de proveedor',
  linkKindQbo: 'Producto de QuickBooks',
  linkKindName: 'Nombre en ventas',
  linkKindBox: 'Contenido de la caja',
  linkActSet: 'Fijar la caja',
  linkingIgnoredTitle: 'Marcados como no producto ({n})',
  linkingIgnoredHint: 'Productos de QuickBooks y nombres en ventas que dijo que no son productos. Preguntar otra vez lo trae de vuelta en la siguiente hoja. Los códigos de proveedor ocultos vuelven en Códigos de proveedor → Ocultos.',
  linkingAskAgain: 'Preguntar otra vez',
  linkActLink: 'Vincular',
  linkActGuess: 'Sugerencia sin revisar',
  linkActIgnore: 'No es producto',
  linkActDone: 'Ya hecho',
  linkActProblem: 'Hay que corregir',
  linkingApply: 'Aplicar {n} filas',
  linkingApplying: 'Aplicando {n} de {total}…',
  linkingGuessesChecked: 'Vincular también las {n} sugerencias que dejó la app: las revisé',
  linkingNotTried: 'Sin intentar: suba la misma hoja otra vez para terminar',
  linkingProblemsNote: 'Las filas que hay que corregir se dejan fuera. Corríjalas en la hoja y súbala otra vez: lo ya hecho se salta.',
  linkingDoneTitle: 'Aplicado',
  linkingDoneSummary: '{ok} hechas, {failed} sin hacer.',
  linkingFailedTitle: 'Sin hacer',
  linkingAfter: 'Los códigos vinculados traen sus precios pasados al historial de costos, y los productos de QuickBooks y nombres vinculados traen sus ventas a los márgenes. Véalo en',
  linkingOneAtATime: 'Uno por uno:',
  gapLinkSheet: 'Vincularlos todos en Excel',
  cloverTitle: 'Caja registradora (Clover)',
  cloverHint: 'Qué categorías de Clover guarda la app. Aquí solo importan frutas, verduras y artículos de mayoreo: una venta de caja en una categoría que se deja fuera (un refresco, un snack con código de barras) se quita y no se vuelve a traer. Un artículo ya borrado en Clover siempre se guarda.',
  cloverNotConnected: 'Clover no está conectado (CLOVER_API_TOKEN y CLOVER_MERCHANT_ID, ver el manual).',
  cloverAll: 'Hoy entran todas las categorías.',
  cloverFiltered: '{n} categorías guardadas.',
  cloverLastPull: 'última lectura',
  cloverKeep: 'Guardar',
  cloverCategory: 'Categoría de Clover',
  cloverItems: 'Artículos',
  cloverSold90: 'Vendido, 90 días',
  cloverNoCategory: 'Artículos sin categoría',
  cloverNew: 'nueva en Clover: se guarda hasta que elija',
  cloverKeptSold: 'Guardado: {kept} de {all} vendidos en caja en los últimos 90 días.',
  cloverSave: 'Guardar solo estas',
  cloverKeepAll: 'Guardar todo otra vez',
  cloverAsk: 'Las ventas de caja fuera de las categorías marcadas se quitan de la app ahora y no se vuelven a traer. ¿Seguir?',
  cloverSaved: 'Guardado: se quitaron {n} ventas de caja fuera de ellas.',
  cloverSavedAgain: 'Guardado: se quitaron {n} ventas de caja; lo que regresa se lee otra vez en las próximas lecturas de cada hora.',
  cloverOwnerOnly: 'Solo el dueño cambia esto.',
  gapFillBoxes: 'Llenar lo que trae cada caja en Excel (Hoja de vínculos → Contenido de la caja)',
  gapWhy: 'Por qué no tiene costo',
  gapNoPrice: 'No hay precio registrado para los días que se vendió: vincule su código de proveedor (Productos → Códigos de proveedor) o registre un precio.',
  gapNoContent: 'Se vende por libra o por pieza, pero el contenido de la caja está vacío: abra el producto y llene lo que trae una caja.',
  linesCol: 'Líneas',
  rankCol: '#',
  reasonCol: 'Motivo',
  countCol: 'Veces',
  qtyCol: 'Cant.',
  priceCol: 'Precio',
  flagCol: 'Alerta',
  packsCol: 'Bultos',
  salesCol: 'Ventas',
  ordersHint: 'Cada pedido de la fecha de entrega con su estado, canal y valor. Enter abre el pedido; las flechas mueven un día.',
  unpricedCol: 'Sin precio',
  ordersCol: 'Pedidos',
  orderValue: 'Valor de pedidos',
  createdCol: 'Capturado',
  findOrder: 'Buscar cliente o no. de pedido…',
  todayStrip: 'Hoy',
  likelihoodCol: 'Probabilidad',
  usualItemsCol: 'Artículos usuales',
  typicalCol: 'Pedido típico',
  shelfLifeCol: 'Vida de anaquel',
  ageCol: 'Edad (días)',
  lotCol: 'Lote',
  pastDueCustomers: 'Vencido ({n} clientes)',
  unpricedLinesCol: 'Líneas sin precio',
  pastDueCustomer: 'Vencido ({n} cliente)',
  ledgerTitle: 'Libro y saldos iniciales',
  ledgerHint: 'La app lleva sus propias cuentas por cobrar desde el inicio del libro. Escribe lo que cada cliente debía ese día (de QuickBooks: Reportes, resumen de antigüedad de cuentas por cobrar, al día anterior al inicio). Un saldo bajo cero es dinero que pagaron por adelantado. Los pagos después del inicio liquidan primero el saldo inicial. Cada cambio se guarda: una corrección es una entrada nueva, nunca una edición.',
  ledgerStart: 'Inicio del libro',
  setStart: 'Fijar el inicio',
  openingCol: 'Saldo inicial',
  balanceTodayCol: 'Saldo hoy',
  dueDateCol: 'Vencimiento más viejo',
  noteCol: 'Nota',
  entriesCol: 'Entradas',
  saveOpeningN: 'Guardar {n} saldo(s) inicial(es)',
  withOpening: 'Con saldo inicial',
  openingTotal: 'Total inicial',
  openingUnsaved: 'Hay saldos iniciales escritos sin guardar. ¿Salir de todos modos?',
  openingSaved: 'Saldos iniciales guardados',
  notADate: 'no es una fecha (AAAA-MM-DD, en o antes del inicio)',
  moveStartConfirm: '¿Mover el inicio del libro al {date}? Las facturas y pagos anteriores dejan de contar en los saldos.',
  openingLine: 'Saldo inicial al {date}',
  sales90Col: 'Ventas 90 d',
  gm30Col: 'MB % 30 d',
  lastInvoiceCol: 'Última factura',
  pastDueCol: 'Vencido',
  daysLateCol: 'Días de atraso',
  customersCol: 'Clientes',
  activeOnly: 'Solo activos',
  invalidPrices: 'no es un precio',
  useEngineAll: 'Usar los precios del motor en todo',
  published: 'Publicado',
  pricesPublished: 'precios publicados',
  nothingChanged: 'Nada cambió',
  lastPublished: 'Última publicación',
  costBefore: 'antes',
  costWas: 'antes {n}',
  priceAuto: 'auto',
  priceHand: 'a mano',
  priceHeld: 'en espera: {n}',
  followsCol: 'Sigue',
  followsHint: 'Auto: el precio sigue al costo solo (el precio del motor, cada hora y al hacer un pedido). A mano: su precio, se queda hasta que lo cambie',
  priceAutoShort: 'sigue al costo',
  priceHandShort: 'mi precio, se queda',
  fillHeldNote: '{n} precios cuyo costo se movió más que el límite se dejaron como están: revíselos (marcados en espera) y publíquelos usted.',
  priceByAuto: 'auto (siguió al costo)',
  phBy: 'Quién',
  costsPricesTitle: 'Costos y precios',
  costsPricesHint: 'Los precios de un proveedor (primero Mata\'s) frente a USDA y los otros proveedores, nuestro costo, nuestros precios y los márgenes, en el periodo. Haga clic en un producto para ver sus semanas.',
  cpVendor: 'Proveedor',
  cpWeeksTitle: 'Ventas y utilidad bruta por semana',
  cpOpenHint: 'Haga clic en un producto para graficar el precio del proveedor frente a USDA por semana.',
  cpVendorMove: '{vendor}: cambio de precio en el periodo',
  cpNProducts: '{n} productos',
  cpVsUsda: '{vendor} frente a USDA',
  cpNoUsda: 'Ningún producto ligado a USDA todavía (la página del producto, USDA)',
  cpUnder: 'Bajo costo · bajo el piso',
  cpUnderSub: 'precios de nivel de lo vendido',
  cpModes: 'Precios auto · a mano',
  cpChanges: '{n} cambios de precio en el periodo',
  cpItemTitle: '{item}: {vendor} frente a USDA',
  cpUsdaMid: 'USDA (medio)',
  cpOurPrice: 'Nuestro precio',
  cpItemTable: 'Las semanas',
  cpWeek: 'Semana del',
  cpSold: 'Vendido',
  cpWas: 'era {price}',
  cpChange: 'Cambio',
  cpBestOther: 'Otro más barato',
  cpVsUsdaCol: 'vs USDA',
  priceChangesTitle: 'Cambios de precio',
  priceChangesHint: 'Cada precio de nivel que cambió en el periodo, de todos los productos, lo más reciente primero: el precio antes, el de ahora, el costo con que se fijó y quién lo fijó (auto cuando siguió al costo solo).',
  priceChangesNone: 'Ningún precio cambió en este periodo.',
  priceChangesTruncated: 'Se muestran los primeros 5,000 cambios: elija un periodo más corto para ver el resto.',
  priceBeforeCol: 'Antes',
  priceNowCol: 'Ahora',
  costFromCol: 'Costo de',
  costDaysOld: 'hace {n} días',
  costSrcList: 'lista de precios',
  costSrcOrder: 'pedido',
  costSrcDock: 'andén',
  costSrcBill: 'factura',
  costSrcTyped: 'escrito',
  costSrcOther: 'otro',
  underFloor: 'bajo el piso',
  belowCost: 'bajo el costo',
  approveFloorPrices: 'Publicar de todos modos (bajo el piso)',
  priceList: 'Lista de precios',
  priceListHint: 'Elige un nivel o un cliente; luego imprime, descarga la hoja o copia como texto.',
  forTier: 'Nivel',
  forCustomer: 'Cliente',
  download: 'Descargar',
  copyText: 'Copiar como texto',
  pricesFrom: 'Precios desde',
  perPack: 'por caja',
  // payables and cash on the route
  payablesTitle: 'Cuentas por pagar',
  payablesHint: 'Cada factura de proveedor con saldo, la más vencida primero. Paga desde el renglón; un error se revierte, nunca se edita.',
  openBills: 'Por pagar',
  dueThisWeek: 'Vence esta semana',
  noBills: 'Sin facturas abiertas',
  recentPayments: 'Pagos recientes',
  reversed: 'revertido',
  pay: 'Pagar',
  collected: 'Cobrado',
  checkNo: 'No. de cheque',
  cashOnRoute: 'Efectivo en la ruta',
  checks: 'Cheques',
  turnedIn: 'Entregado en oficina',
  turnInCash: 'Efectivo entregado',
  countedAmount: 'Efectivo contado',
  notTurnedIn: 'Aún no se entrega',
  // passkeys and the system screen
  signInWithPasskey: 'Usar cara o huella',
  passkeyUnknown: 'Este teléfono aún no está registrado. Entra de otra forma y agrégalo en Cambiar contraseña.',
  passkeysTitle: 'Entrar con cara o huella',
  passkeysHint: 'Agrega este teléfono una vez; desde entonces la pantalla de entrada abre con su propio bloqueo, sin código ni teclear.',
  noPasskeys: 'Ningún teléfono agregado',
  added: 'Agregado',
  forget: 'Olvidar',
  addThisPhone: 'Agregar este teléfono',
  passkeysUnsupported: 'Este navegador no puede entrar con cara o huella. Usa el teléfono mismo.',
  systemTitle: 'Sistema',
  systemHint: 'La app sobre sí misma: versión, base de datos, errores, quién entra, qué está encendido. Cuando un teléfono muestre un error, mira aquí primero.',
  appVersion: 'Versión',
  devBuild: 'versión de desarrollo',
  startedAt: 'Activa desde',
  database: 'Base de datos',
  migrations: 'migraciones',
  dbDown: 'no responde',
  people: 'Personas',
  withPassword: 'con contraseña',
  withPasskey: 'con teléfono agregado',
  seenThisWeek: 'entraron esta semana',
  switches: 'Encendido',
  storage: 'Archivos',
  readerShort: 'Lector',
  textingShort: 'Textos',
  switchedOn: 'sí',
  switchedOffState: 'no',
  errorsTitle: 'Errores',
  noErrors: 'Sin errores registrados',
  server: 'servidor',
  browser: 'teléfono',
  // traceability
  scan: 'Escanear',
  scanHint: 'Apunta la cámara a la etiqueta del lote, o escribe el código, para ver de dónde vino y quién lo recibió.',
  camera: 'Cámara',
  cameraOff: 'La cámara no pudo iniciar',
  noCamera: 'Este navegador no puede escanear. Escribe el código abajo.',
  open: 'Abrir',
  wentTo: 'Fue a',
  notSoldYet: 'Aún no vendido',
  cameFrom: 'Vino de',
  labels: 'Etiquetas',
  labelsHint: 'Una etiqueta por lote con existencia en este sitio: el código, un QR, el artículo, la fecha, el proveedor. Imprime y pega en la tarima o la primera caja.',
  traceExport: 'Exportar rastreo',
  dateFrom: 'Desde',
  dateTo: 'Hasta',
  traceExportHint: 'Cada línea de factura con su lote, para las fechas (y un lote). La hoja que pide un retiro o un auditor.',
  // the office layout
  navSell: 'Ventas',
  navBuyStock: 'Compras',
  navCustomers: 'Clientes',
  navWarehouse: 'Operaciones',
  navProducts: 'Productos',
  navReports: 'Reportes',
  navSettings: 'Administración',
  navDashboard: 'Panel',
  navSalesOrders: 'Órdenes de venta',
  navReceivable: 'Cuentas por cobrar',
  endOfDayTitle: 'Cierre del día',
  worklistsTitle: 'Pendientes',
  draftsToConfirm: 'Borradores por confirmar',
  noDrafts: 'No hay borradores esperando',
  confirmAll: 'Confirmar todos',
  confirmAllAsk: '¿Confirmar los {n} borradores? Cada uno se revisa como si se confirmara uno por uno: un cliente en espera o sobre el límite queda en borrador.',
  staleDrafts: 'borradores para un día ya pasado: abra cada uno para cambiar el día o cancelarlo',
  confirmedCount: 'confirmados',
  collectionsTitle: 'Cobranza',
  collectionsHint: 'Vencido después del dinero a cuenta, el mayor primero. Llame y anote lo que prometieron: se marca cumplido o incumplido solo.',
  nothingPastDue: 'Nadie tiene saldo vencido',
  lastCall: 'Última llamada',
  logCall: 'anotar llamada',
  promiseCol: 'Promesa de pago',
  addPromise: '+ Promesa',
  promiseAmount: 'Monto prometido',
  promiseBy: 'Prometido para',
  promise_open: 'prometido',
  promise_kept: 'cumplido',
  promise_broken: 'incumplido',
  complaintsTitle: 'Quejas',
  complaintsHint: 'Anotadas en el cliente como queja; las abiertas primero, las resueltas por 30 días.',
  noComplaints: 'No hay quejas abiertas',
  markResolved: 'Resuelta',
  resolved: 'resuelta',
  noCostTitle: 'Vendiendo sin costo',
  noCostHint: 'Productos vendidos en los últimos 60 días sin costo registrado: no se sabe su margen. Agregue el costo o ligue el código del proveedor.',
  linesSold: 'líneas',
  kind_complaint: 'Queja',
  vendorClaimsTitle: 'Reclamos a proveedores',
  trucksNoBillTitle: 'Camiones sin factura',
  trucksNoBillHint: 'Una factura se revisa contra lo que trajeron sus camiones. El número de factura escrito en el andén la junta; si no, escoja aquí la factura (una factura semanal puede cubrir varios camiones).',
  itsBill: 'Su factura',
  noBillNear: 'Todavía no hay factura de ese proveedor cerca de su día',
  linkBillConfirm: '¿Este camión vino en la factura {bill}?',
  billsAndTrucks: 'Facturas y los camiones que cubren',
  matchLinked: 'escogida',
  matchNumber: 'por número',
  unlink: 'Quitar',
  receiptLines: 'líneas',
  billNumber: 'Factura no.',
  dismissClaim: 'Descartar',
  noBillYet: 'Sin factura todavía',
  claimCreditedConfirm: '¿Marcarlo acreditado? El proveedor dio el crédito (o está en su nota de crédito). Esto se guarda.',
  vendorClaimsHint: 'Lo que los proveedores nos deben: en cada factura, cajas cobradas que su camión no trajo (rechazadas o faltantes) y un precio arriba del pedido. Antes de que llegue la factura, los rechazos y faltantes del camión se muestran como lo que hay que vigilar. Márquelo acreditado cuando el proveedor lo acredite, o descártelo con un motivo.',
  noVendorClaims: 'No hay reclamos abiertos',
  claimWhy: 'Motivo',
  date: 'Fecha',
  qty: 'Cant.',
  claim_reject: 'Rechazado en el andén',
  claim_short: 'Faltó contra el pedido',
  claim_billed: 'Cobrado, no llegó',
  claim_price: 'Cobrado arriba del pedido',
  claimCredited: 'Acreditado',
  dismissWhy: 'Por qué se descarta',
  printClaim: 'Imprimir el reclamo',
  claimLetterTo: 'Reclamo a',
  claimLetterBody: 'Por favor acredite nuestra cuenta por lo siguiente. Cada línea muestra el día, el motivo, el producto y el monto.',
  monthEndTitle: 'Cierre de mes',
  booksClosedTitle: 'Libros cerrados',
  booksClosedHint: 'Cuando un mes ya se reportó, ciérrelo: ninguna factura, anulación, faltante, pago, reversión, documento de proveedor, recepción ni conteo con fecha de ese día o antes puede cambiar. El dueño cierra y reabre.',
  booksClosedThrough: 'Los libros están cerrados hasta el',
  booksOpen: 'Todavía no hay meses cerrados.',
  closeThrough: 'Cerrar hasta',
  closeBooks: 'Cerrar los libros',
  closeBooksConfirm: '¿Cerrar los libros hasta el {date}? Nada con esa fecha o antes cambiará hasta que los reabra.',
  reopenBooks: 'Reabrir',
  reopenBooksConfirm: '¿Reabrir los libros? Los meses cerrados podrán cambiar otra vez.',
  inventoryValueTitle: 'Valor del inventario',
  asOfDay: 'Al final del',
  inventoryValueHint: 'Lo que valía la existencia al final del día: cada lote a su costo puesto en bodega (su costo de compra, o el costo registrado ese día).',
  product: 'Producto',
  value: 'Valor',
  noCostShort: 'sin costo',
  downloadCsv: 'Hoja de cálculo (CSV)',
  statementsTitle: 'Estados de cuenta',
  statementsHint: 'Cada cliente con saldo, un estado de cuenta por página, para imprimir o guardar en PDF.',
  printStatements: 'Imprimir todos los estados de cuenta',
  onAccountLine: 'Pagos y créditos a cuenta',
  forTheBank: 'Para el banco',
  nothingToDeposit: 'No hay efectivo ni cheques por depositar',
  inDeposit: 'En el depósito',
  bankAccount: 'Cuenta de banco',
  makeDeposit: 'Hacer el depósito e imprimir la ficha',
  depositsMade: 'Depósitos',
  deposit: 'Depósito',
  depositSlip: 'Ficha de depósito',
  receivedTotal: 'Recibido en total',
  variance: 'Sobra / falta',
  stopsNotDelivered: 'paradas sin entregar',
  deliveredNotInvoiced: 'pedidos entregados sin factura',
  dayClosed: 'Día cerrado',
  cashCounted: 'Efectivo contado',
  closeDay: 'Cerrar el día',
  changedSinceClose: 'Cambió después del cierre',
  navFinance: 'Finanzas',
  greetingLine: '{greeting}, {name}',
  sortBy: 'Ordenar',
  sortedAsc: 'orden ascendente',
  sortedDesc: 'orden descendente',
  orderNo: 'Pedido',
  // the order day and customer patterns
  orderDayTitle: 'Planeación de pedidos',
  orderDayHint: 'Qué comprar para una carga: una semana normal de nuestros clientes, un margen de seguridad, el piso, cada cuenta clave como columna, los demás pedidos anticipados, lo que hay. Escribe lo que pidió cada cuenta en su columna (ese es su pedido), escribe sobre la columna de pedir y luego genera las órdenes de compra y las facturas.',
  invoiceAccounts: 'Facturar las cuentas',
  nothingToInvoice: 'Nada que facturar para esta carga',
  noLines: 'sin líneas',
  orderDayToCheck: 'Las facturas del día de pedido, una por pedido: revise cantidades y precios, cambie lo que llamó una tienda, luego Crear facturas. Nada se factura hasta entonces.',
  orderDaySkipped: 'No incluidas',
  fromOrderNo: 'Pedido #{no}',
  backToOrderDay: 'Volver al día de pedido',
  orderLockedNote: 'ya preparado: se factura tal como está; cambie la factura después si algo cambió',
  keyMoreHint: 'Incluye {n} más de las tiendas: otro pedido suyo en esta carga, o su pedido fijo de un día sin pedido todavía',
  preview: 'Vista previa',
  takeAnyway: 'Dueño: tomarlo aunque esté en espera o pase el límite',
  alreadyMade: 'Esas facturas ya se hicieron antes de que se perdiera la respuesta. Revisa Facturas antes de intentar de nuevo.',
  invoicesWritten: 'Facturas',
  accountSaved: 'Pedido guardado',
  load: 'Carga',
  lands: 'Llega',
  sells: 'vende',
  itemCol: 'Artículo',
  invShortsTitle: 'Faltantes',
  invShortsHint: 'Escriba lo que cada renglón entregó de verdad. Los totales, el inventario y el pedido se ajustan; cada cambio queda abajo.',
  shortsRecord: 'Registrar faltantes',
  shortsOnInvoice: 'En la factura',
  shortsDelivered: 'Entregado',
  shortsBad: 'Lo entregado es un número de 0 hasta lo que dice la factura (lo que sea más va en una factura nueva).',
  shortsNote: 'Por qué (opcional)',
  shortsNotePlaceholder: 'p. ej. faltaron 2 cajas de Mata\'s, el chofer regresó 1',
  shortsNewTotal: 'Cambian {n} renglón(es); el nuevo total es {total}.',
  shortsSave: 'Guardar faltantes',
  shortsConfirmCredits: 'Son otras cajas, no las que ya tienen crédito',
  shortLine: 'pedido {ordered}, faltan {short}',
  base: 'Base',
  baseHint: 'Una ventana normal de venta de nuestros clientes (sin las cuentas clave), últimas 8 semanas',
  safety: 'Seguridad',
  safetyHint: '1.65 × la variación entre ventanas',
  keep: 'Piso',
  accountsTotal: 'Cuentas',
  othersCallAhead: 'Otros',
  orderCol: 'Pedir',
  orderIn: 'pedido recibido',
  expected: 'se espera',
  noOrderYet: 'sin pedido aún',
  legendOrder: 'un pedido real',
  legendStanding: 'el pedido fijo',
  legendPattern: 'lo que predice el patrón',
  writePurchaseOrders: 'Generar las órdenes de compra',
  patternTitle: 'Patrón',
  patternOrdersOn: 'Pide los',
  patternNoDays: 'Aún sin días habituales',
  patternPerWeek: 'pedidos por semana',
  usuallyTakes: 'Suele llevar',
  typicalOrder: 'Pedido típico',
  nextExpected: 'Próximo esperado',
  pattern_new: 'Nuevo',
  pattern_ok: 'En patrón',
  pattern_due: 'Toca hoy',
  pattern_overdue: 'Atrasado',
  pattern_quiet: 'Callado',
  keyAccount: 'Cuenta clave (una columna en el día de pedido)',
  columnOrder: 'Orden de columna',
  expectedToday: 'Clientes que suelen pedir hoy y aún no piden',
  pacaTitle: 'PACA y membrete',
  pacaHint: 'El número de licencia se imprime en cada factura. Los saldos atrasados generan interés y, con la cláusula activa, costos de cobranza y honorarios de abogado: los tribunales los han concedido bajo el fideicomiso PACA cuando la factura lo dice. La carta pide el pago en 10 días; es nuestra decisión, no un plazo legal.',
  pacaLicense: 'Número de licencia PACA',
  pacaLicenseShort: 'licencia',
  pacaInterest: 'Interés sobre saldos atrasados, % anual',
  pacaFees: 'Reclamar costos de cobranza y honorarios de abogado',
  pacaClausePreview: 'En la factura',
  pacaTermsRule: 'PACA: plazo de 30 días como máximo, y solo por acuerdo escrito. Más de 30 días pierde la protección del fideicomiso.',
  termsDaysLabel: 'Plazo (días, 30 máx.)',
  termsAgreedOn: 'Plazo firmado el',
  pacaLetter: 'Carta de cobro PACA',
  demandTo: 'Para',
  demandBody1: 'Las facturas de abajo están vencidas. Bajo la Perishable Agricultural Commodities Act, la fruta y verdura, todo lo hecho con ella y el dinero de su venta se mantienen en fideicomiso a favor de Morales Produce hasta el pago total. Pague el total antes del {payBy} para evitar una queja formal ante el USDA y los costos que siguen.',
  demandBody2: 'Esta carta es también un aviso por escrito de la intención de preservar los beneficios del fideicomiso bajo 7 U.S.C. 499e(c)(3) para las facturas listadas cuyo plazo de aviso sigue abierto, además del aviso de fideicomiso impreso en cada factura bajo 7 U.S.C. 499e(c)(4).',
  nothingOpen: 'Este cliente no tiene nada pendiente',
  noticeDaysLeft: 'quedan {n} días para enviar el aviso escrito',
  interest: 'interés',
  principal: 'Capital',
  lettersSent: 'Cartas enviadas',
  recordLetterSent: 'Registrar como enviada hoy',
  letterRecorded: 'Registrada. El reporte de antigüedad muestra la fecha.',
  letterDue: 'Carta pendiente',
  letterSent: 'Carta enviada',
  qboImport: 'Importar de QuickBooks',
  qboImportAll: 'Reimportar todo',
  qboImportAllHint: 'Leer otra vez todos los registros de QuickBooks, no solo lo que cambió. Úselo si algo parece faltar; tarda unos minutos.',
  qboImporting: 'Importando… la primera vez puede tardar unos minutos',
  qboHistory: 'Copiado de QuickBooks',
  qboHistoryHint: 'Todo lo de QuickBooks, copiado a la app. Corre cada noche después de las 5 am; Importar lo corre ahora. Copiar nunca cambia nada en QuickBooks.',
  qboLastImport: 'Última importación: {when}',
  qboImportFailed: 'La última importación falló: {why}',
  qboNothingYet: 'Nada importado todavía.',
  qboTitle: 'QuickBooks',
  qboMatchTitle: 'Emparejar con QuickBooks',
  qboMatchHint: 'Qué cliente, proveedor y producto de QuickBooks es cuál en la app. La importación enlaza los nombres exactos sola; enlace el resto aquí o créelos en la app. No se escribe nada en QuickBooks.',
  qboMatchTab: 'Emparejar',
  qboBalancesTab: 'Saldos',
  qboMatchNotConnected: 'QuickBooks no está conectado. Conéctelo y corra Importar primero:',
  qboEntity_Customer: 'Clientes',
  qboEntity_Vendor: 'Proveedores',
  qboEntity_Item: 'Productos',
  qboLinkedOf: '{n} de {of} enlazados',
  qboAppOnly: '{n} solo en la app',
  qboMatchRun: 'Emparejar por nombre ahora',
  qboMatchRan: 'Se enlazaron {c} clientes, {v} proveedores, {i} productos.',
  qboMatchKind: 'Tipo',
  qboAllLinked: 'Todo de este tipo está enlazado.',
  qboCopyTitle: 'QuickBooks es la lista',
  qboCopyHintCustomers: 'Deja los clientes de la app exactamente como en QuickBooks: cada uno enlazado con el nombre de QuickBooks, los que faltan creados, los demás de la app archivados (la historia se queda, nada se borra). Su correo, teléfono y dirección también llegan donde la app no tiene nada. Uno con un pedido por entregar, dinero pendiente, cuenta clave o acceso al portal se queda, y se dice. Primero la vista previa.',
  qboCopyHintProducts: 'Deja los productos de la app exactamente como en QuickBooks (no los "- Count" ni "- Weight", que se quedan como están): emparejados por los enlaces hechos, el mismo nombre o las mismas palabras, con el nombre de QuickBooks, los que faltan creados, los demás de la app archivados salvo si siguen en uso (existencia, un pedido, una orden de compra, un pedido fijo, ventas en la caja). Revisa las parejas en la vista previa: enlaza a mano abajo una que esté mal.',
  qboCopyPreview: 'Vista previa',
  qboCopyApply: 'Aplicar',
  qboCopyConfirm: '¿Dejar ahora la lista de la app como la de QuickBooks, como en la vista previa?',
  qboCopyWould: 'De {n} en QuickBooks, esto hace Aplicar:',
  qboCopyDone: 'Listo: la app tiene la lista de QuickBooks ({n} en QuickBooks).',
  qboCopy_linked: 'Enlazados (mismo nombre o palabras)',
  qboCopy_renamed: 'Con el nombre de QuickBooks',
  qboCopy_made: 'Creados desde QuickBooks',
  qboCopy_restored: 'De vuelta (activos en QuickBooks)',
  qboCopy_archived: 'Archivados',
  qboCopy_kept: 'Se quedan, y por qué',
  qboCopy_filled: 'Copiado de QuickBooks (solo donde la app no tenía nada)',
  qboCopyMirrorNote: 'Los clientes enlazados ahora traen sus facturas de QuickBooks en la próxima hora del espejo de dinero, y esas descuentan cajas del inventario. Abre Administración → QuickBooks y oprime la Vista previa del espejo antes de esa hora.',
  qboCopyQuiet: 'Hazlo cuando nadie esté facturando: la vista previa detiene la lista los segundos que tarda.',
  qboWhy_same_name: 'mismo nombre',
  qboWhy_same_words: 'mismas palabras',
  qboWhy_inactive_qbo: 'inactivo en QuickBooks',
  qboWhy_not_in_qbo: 'no está en QuickBooks',
  qboWhy_per_unit_lb: 'se vende por libra de esta caja',
  qboWhy_per_unit_ea: 'se vende por pieza de esta caja',
  qboWhy_per_unit_dz: 'se vende por docena de esta caja',
  qboWhy_per_unit_bn: 'se vende por manojo de esta caja',
  qboHold_no_product: 'sin enlazar: ningún producto único de la app coincide con este nombre; enlázalo a mano abajo',
  qboHold_box_unknown: 'aún sin enlazar: pon lo que trae una caja en el producto y aplica otra vez',
  qboHold_store: 'se queda: una tienda de otra compañía, facturada por la app',
  qboHold_key_account: 'se queda: cuenta clave (su columna en el día de pedidos)',
  qboHold_portal: 'se queda: {n} acceso(s) al portal',
  qboHold_tier: 'se queda: tiene su propio nivel de precio',
  qboHold_orders: 'se queda: en {n} pedido(s) abiertos',
  qboHold_standing: 'se queda: lo usa un pedido fijo',
  qboHold_owes: 'se queda: todavía hay dinero pendiente',
  qboHold_on_hand: 'se queda: {n} en existencia',
  qboHold_purchase: 'se queda: en {n} línea(s) de orden de compra abiertas',
  qboHold_register: 'se queda: se vendió en la caja hace poco',
  qboHold_qbo_deleted: 'se queda: enlazado a un cliente que QuickBooks borró',
  qboCopyMaybe: 'quizás "{name}" de QuickBooks: enlázalo en Emparejar y aplica otra vez',
  qboField_email: 'correo',
  qboField_phone: 'teléfono',
  qboField_address: 'dirección',
  switchMatchTitle: '¿Listo para el cambio?',
  switchMatchReady: 'Listo: todo cliente de QuickBooks con saldo o movimiento en el último año está enlazado.',
  switchMatchNotReady: 'No está listo: {c} clientes de QuickBooks con saldo o movimiento en el último año no están enlazados. El día del cambio los espera (QuickBooks es la lista, arriba, lo hace en un paso).',
  switchMatchHint: 'Solo lo que el cambio necesita: clientes con saldo o con una venta, pago o crédito desde {since}, y productos vendidos desde entonces. Enlaza cada uno con el de la app; un cliente también se puede crear en la app, y un cargo marcarse como "No es producto".',
  switchMatchCustomers: 'Clientes por enlazar',
  switchMatchProducts: 'Productos sin enlace (no hacen falta para el cambio)',
  switchMatchVendors: 'Proveedores (no hacen falta para el cambio)',
  switchMatchNew: 'Nuevos en QuickBooks',
  switchMatchCustomerDetail: '{n} ventas, pagos o créditos · último {last}',
  switchMatchProductDetail: '{amount} vendidos en {n} líneas · último {last}',
  switchMatchVendorDetail: '{n} facturas o pagos · último {last}',
  switchMatchVendorsHint: 'Las facturas de proveedores se quedan en QuickBooks. Enlazado, las de un proveedor se ven por proveedor en la app; esto no detiene el cambio.',
  switchMatchNewHint: 'Clientes y productos de la app sin enlace a QuickBooks. Los marcados primero serían rechazados por QuickBooks con su primera factura después del cambio: enlázalos abajo ahora. Los demás se crean en QuickBooks entonces; si QuickBooks tiene uno con otro nombre, enlázalo abajo. Archiva los clientes de práctica.',
  switchMatchOwesOnly: 'debe, sin venta ni pago en el último año',
  switchMatchWhy_new: 'se crea nuevo en QuickBooks',
  switchMatchWhy_inactive: 'QuickBooks lo tiene, pero inactivo: actívalo allá o enlaza otro',
  switchMatchWhy_taken: 'QuickBooks tiene el nombre enlazado a otro registro de la app: enlaza el correcto',
  switchMatchWhy_other_kind: 'QuickBooks usa este nombre para un proveedor o un empleado: un cliente con el mismo nombre se rechaza; enlázalo a su cliente de QuickBooks o cámbiale el nombre',
  switchMatchWhy_not_sellable: 'QuickBooks tiene el nombre como paquete o grupo, no como producto para vender: enlázalo a su producto de QuickBooks',
  switchMatchNewCustomers: 'Clientes',
  switchMatchNewProducts: 'Productos',
  switchMatchNone: 'Nada aquí.',
  switchMatchCapped: 'Se muestran los primeros {n}.',
  notAProduct: 'No es producto',
  notAProductConfirm: '¿"{name}" no es un producto (un cargo, flete, un depósito)? No se vuelve a preguntar, aquí ni en la hoja de enlaces.',
  qboUnlinkedCaption: 'Sin enlazar: los activos primero, luego los saldos más grandes',
  qboInQuickBooksCol: 'En QuickBooks',
  qboBalanceCol: 'Debe en QuickBooks',
  qboAppRecordCol: 'En la app',
  qboPickApp: 'Escriba para elegir el registro de la app',
  qboInactive: 'inactivo',
  qboLink: 'Enlazar',
  qboMake: 'Crear en la app',
  qboMakeHint: 'Crea el registro en la app con el nombre y plazo de QuickBooks (el de un cliente, máximo 30 días), enlazado',
  qboBalancesHint: 'Lo que cada cliente enlazado debe en QuickBooks junto a lo que dice la app (la app cuenta desde {date}). Las diferencias más grandes primero.',
  qboBalancesNoLedger: 'La app aún no tiene fecha de inicio de cuentas por cobrar; solo se ve el lado de QuickBooks. Póngala en Libro y saldos iniciales.',
  qboOwedInQuickBooks: 'Se debe en QuickBooks',
  qboOwedInApp: 'Se debe en la app',
  qboDiffer: 'Clientes con diferencia',
  qboBalancesCaption: 'Clientes enlazados',
  qboAppCol: 'App',
  qboDiffCol: 'Diferencia',
  qboBalancesNone: 'Ningún cliente enlazado debe nada en ningún lado.',
  qboUnlinkedOwing: 'Lo que deben clientes aún sin enlazar',
  qboUnlink: 'Desenlazar',
  qboUnlinkConfirm: '¿Desenlazar {name}? Vuelve a la lista Sin enlazar y el emparejado por nombre no volverá a unir ese par.',
  qboOpenMatching: 'Emparejar y saldos',
  standingHeld: 'Sin borrador, en espera o sobre su límite de crédito: {names}. Tome su pedido a mano si debe recibirlo.',
  typedNotNumber: 'No es un número que podamos leer: escríbalo como 120 o 1,250.50',
  qboBalancesSource: 'Lo que dice QuickBooks',
  qboBalancesFromImport: 'Última importación',
  qboBalancesFromReport: 'Reporte de antigüedad, ahora',
  qboReportTotal: 'Reporte de antigüedad de cuentas por cobrar de QuickBooks, leído {when}: {total} en total (enlazados y no).',
  qboReportOff: '{amount} de eso no está en ninguna fila de abajo: un cliente de QuickBooks que la importación aún no copió, o una fila del reporte sin cliente. Importe y léalo de nuevo.',
  qboBalancesAsOf: 'QuickBooks según la última importación, {when}; la app está al día, así que lo registrado en QuickBooks desde entonces aparece como diferencia hasta la próxima importación.',
  qboCapped: 'Solo se ven los primeros {n}; el resto aparece al enlazar estos.',
  qboShowWhich: 'Mostrar',
  qboNotLinked: 'Sin enlazar',
  qboLinkedList: 'Enlazados',
  qboNoneLinked: 'Nada de este tipo está enlazado todavía.',
  qboFindEither: 'Parte de cualquiera de los nombres',
  qboHowCol: 'Cómo',
  qboHow_key: 'por el id de QuickBooks',
  qboHow_name: 'por nombre',
  qboHow_alias: 'por otro nombre',
  qboHow_manual: 'a mano',
  qboHow_created: 'creado desde QuickBooks',
  qboHow_pushed: 'creado en QuickBooks por la app',
  qboArchived: 'archivado',
  qboOutsideLinks: 'Se debe fuera de los enlaces',
  qboFind: 'Buscar',
  qboFindPlaceholder: 'Parte del nombre en QuickBooks',
  qboShowMore: 'Se ven {n} de {of}: ver más',
  qboHint: 'Conecte la empresa una vez. La app lee QuickBooks (clientes, artículos, facturas, pagos, cuentas por pagar, el estado de resultados) para importar el historial y mantener los reportes al día. Solo escribe en QuickBooks las facturas hechas en la app, cuando el dueño lo enciende abajo.',
  qboNotSetTitle: 'Las claves de la app no están configuradas',
  qboNotSet: 'QBO_CLIENT_ID y QBO_CLIENT_SECRET aún no están en el servidor. Agréguelas en GitHub → Settings → Secrets and variables → Actions y luego corra Deploy → deploy.',
  qboConnectTitle: 'Conectar QuickBooks',
  qboConnectHint: 'Va a Intuit, inicia sesión, elige la empresa y aprueba. Intuit lo regresa aquí.',
  qboEnv_production: 'Libros reales',
  qboEnv_sandbox: 'Empresa de prueba de Intuit',
  qboEnvHint_production: 'Su empresa. Requiere las claves de producción de Intuit.',
  qboEnvHint_sandbox: 'La empresa de prueba vacía a la que llegan las claves de desarrollo, para probar la conexión.',
  qboConnect: 'Conectar con QuickBooks',
  qboRedirectHint: 'Intuit debe tener esta dirección en los Redirect URIs de la app:',
  qboConnection: 'Conectado',
  qboCompany: 'Empresa',
  qboBooks: 'Libros',
  qboConnectedOn: 'Conectado',
  qboGoodUntil: 'Vigente hasta',
  qboGoodUntilHint: 'Intuit termina una conexión que no se usa en 100 días; cada uso la extiende.',
  qboCheck: 'Probar conexión',
  qboDisconnect: 'Desconectar',
  qboDisconnectConfirm: '¿Desconectar QuickBooks? La app deja de leerlo hasta que conecte de nuevo.',
  qboInQuickBooks: 'En QuickBooks',
  qboCheckHint: 'Lo que traerá la importación. No se copió ni cambió nada.',
  qboCount_customers: 'Clientes',
  qboCount_vendors: 'Proveedores',
  qboCount_items: 'Productos y servicios',
  qboCount_invoices: 'Facturas',
  qboCount_payments: 'Pagos',
  qboCount_bills: 'Cuentas por pagar',
  qboCount_accounts: 'Cuentas',
  qboKind_Term: 'Plazos',
  qboKind_Account: 'Cuentas',
  qboKind_Customer: 'Clientes',
  qboKind_Vendor: 'Proveedores',
  qboKind_Item: 'Productos y servicios',
  qboKind_Invoice: 'Facturas',
  qboKind_CreditMemo: 'Notas de crédito',
  qboKind_SalesReceipt: 'Recibos de venta',
  qboKind_RefundReceipt: 'Recibos de reembolso',
  qboKind_Payment: 'Pagos',
  qboKind_Bill: 'Facturas de proveedor',
  qboKind_BillPayment: 'Pagos a proveedores',
  qboKind_VendorCredit: 'Créditos de proveedor',
  qboKind_Purchase: 'Gastos y cheques',
  qboKind_Deposit: 'Depósitos',
  qboKind_Transfer: 'Transferencias',
  qboKind_JournalEntry: 'Asientos contables',
  qboReconnectNeeded: 'QuickBooks rechazó la conexión: conéctela de nuevo para seguir importando.',
  qboReconnect: 'Conectar de nuevo',
  qboOldestInvoice: 'Factura más antigua: {date}',
  qboConnectedNow: 'QuickBooks está conectado.',
  qboError_declined: 'La conexión no se aprobó en Intuit.',
  qboError_intuit: 'Intuit respondió con un error. Intente de nuevo.',
  qboError_state: 'Ese enlace venció o no se inició aquí. Presione Conectar otra vez.',
  qboError_signin: 'Inicie sesión como dueño en este navegador y presione Conectar otra vez.',
  qboError_exchange: 'Intuit no aceptó el inicio de sesión. Revise que la dirección de regreso esté guardada en Intuit e intente de nuevo.',
  qboError_production: 'Intuit rechazó los libros reales con estas claves. Las claves de desarrollo solo llegan a la empresa de prueba: elija “Empresa de prueba de Intuit” o termine las claves de producción en Intuit.',
  qboError_company: 'Conectado en Intuit, pero no se pudo leer la empresa. Intente de nuevo.',
  dismiss: 'Cerrar',
  privacyTitle: 'Política de privacidad',
  termsTitle: 'Términos de uso',
  legalUpdated: 'Morales Produce · actualizado octubre 2026',
  legalBack: 'Volver a la app',
  privacy1: 'Esta app es el sistema privado de operaciones de Morales Produce, empresa mayorista de frutas y verduras en Bernalillo, Nuevo México. La usan el personal de la empresa y los clientes que la empresa invita a pedir en línea.',
  privacy2: 'Qué guarda: los registros necesarios para operar — clientes y sus contactos, artículos, precios, pedidos, facturas, pagos, proveedores, cuentas por pagar, inventario y entregas — y los nombres, teléfonos y datos de acceso de quienes la usan.',
  privacy3: 'QuickBooks: cuando el dueño conecta QuickBooks Online, la app lee los registros contables de la empresa (clientes, proveedores, artículos, facturas, pagos, cuentas por pagar, cuentas y reportes) para mantener sus propios registros y reportes al día. Las claves de conexión se guardan cifradas. El dueño puede desconectar en cualquier momento desde la app o desde QuickBooks, y la app borra las claves.',
  privacy4: 'Cómo se usa: solo para operar el negocio de Morales Produce. Los datos no se venden, rentan ni comparten para publicidad. Los proveedores que hospedan la app (DigitalOcean), envían mensajes (Twilio) o leen documentos a pedido (Anthropic) reciben solo lo que cada tarea necesita.',
  privacy5: 'Dónde se guarda: en servidores en Estados Unidos, en una base de datos a la que solo llega la app, con respaldos diarios cifrados y probados.',
  privacy6: 'Preguntas o solicitudes para ver o borrar su información: comuníquese con la oficina de Morales Produce.',
  terms1: 'Esta app la ofrece Morales Produce a su personal y a clientes invitados. Usarla significa aceptar estos términos.',
  terms2: 'Las cuentas son personales: no comparta su contraseña ni sus códigos. La empresa puede suspender una cuenta mal usada.',
  terms3: 'Los precios, pedidos y facturas en la app son registros de la empresa; un pedido hecho en la app es una solicitud hasta que la empresa lo confirma.',
  terms4: 'La app se conecta a QuickBooks Online solo con la aprobación del dueño y se puede desconectar en cualquier momento.',
  terms5: 'La app se ofrece tal cual. Morales Produce procura mantenerla disponible y correcta, pero no responde por interrupciones ni pérdidas por su uso más allá de lo que exige la ley. Preguntas: oficina de Morales Produce.',
  practiceTitle: 'Solo práctica.',
  practiceSales: 'Hasta el cambio, QuickBooks es el registro real: las facturas, pagos y créditos reales se capturan en QuickBooks. Lo que se captura aquí es una PRUEBA: no cuenta en saldos, reportes ni inventario, el cliente nunca la ve y el día del cambio se borra.',
  testTag: 'PRUEBA',
  testStamp: 'PRUEBA: no es una factura real',
  practiceBills: 'Hasta el cambio, las cuentas por pagar y sus pagos reales se capturan en QuickBooks. Las que se capturan aquí son práctica; sus costos sí cuentan para los precios.',
  salesTitle: 'Desempeño de ventas',
  pointsUnit: 'pts',
  otherCategories: 'Otras',
  vsYearAgo: 'vs hace un año',
  kpiInvoices: 'Facturas',
  salesHint: 'El periodo contra el anterior y los mismos días hace un año: ventas, utilidad bruta, facturas y clientes, la tendencia, la mezcla y cada cliente y artículo. Las ventas son lo facturado, antes de créditos; las líneas sin artículo cuentan en los totales, no en Por artículo.',
  noSalesInPeriod: 'Sin facturas en este periodo ni en el anterior.',
  grossProfit: 'Utilidad bruta',
  avgInvoice: 'Factura promedio',
  activeCustomers: 'Clientes activos',
  vsPrior: 'vs periodo anterior',
  findCustomer: 'Buscar un cliente…',
  search: 'Buscar',
  priorPeriod: 'Periodo anterior',
  changeCol: 'Cambio',
  changePctCol: 'Cambio %',
  qtySold: 'Cant. vendida',
  nRows: '{n} filas',
  uncategorized: 'Sin categoría',
  lastYear: 'Año pasado',
  thisPeriod: 'Este periodo',
  weekOf: 'Semana del {date}',
  nDaysInPeriod: '{n} días en el periodo',
  grainLabel: 'Agrupar por',
  grain_day: 'Día',
  grain_week: 'Semana',
  grain_month: 'Mes',
  per_day: 'día',
  per_week: 'semana',
  per_month: 'mes',
  revenueBy: 'Ventas por {per}',
  gmBy: 'Margen bruto % por {per}',
  salesMix: 'Mezcla de ventas por categoría',
  topCustomers: 'Mejores clientes',
  topItems: 'Artículos principales',
  partialNote: 'Las columnas claras son periodos incompletos',
  salesByItem: 'Por artículo',
  salesByCustomer: 'Por cliente',
  arByAge: 'Cuentas por cobrar por antigüedad',
  salesLast12: 'Ventas, últimas 12 semanas',
  mixLast12: 'Mezcla de ventas, últimas 12 semanas',
  topCustomers12: 'Mejores clientes, últimas 12 semanas',
  salesOpen: 'Abrir desempeño de ventas',
  age_current: 'Al corriente',
  age_d1to30: '1–30 días',
  age_d31to60: '31–60 días',
  age_d61to90: '61–90 días',
  age_d90plus: 'Más de 90 días',
  signalsTitle: 'Resumen de desempeño',
  revenueWeek: 'Vendido esta semana',
  vsLastYear: 'Misma semana del año pasado',
  ordersWeek: 'Facturas esta semana',
  dso: 'Días para cobrar',
  costKnownFor: 'costo conocido en',
  errors24h: 'Errores en 24 horas',
  forecastScoreTitle: 'Precisión del pronóstico',
  forecastNotScored: 'Aún sin calificar: el calificador corre cada mañana en cuanto hay ventas que comparar.',
  forecastWeekdayAvg: 'Mismo día, promedio de 12 semanas',
  forecastLastWeek: 'La semana pasada tal cual',
  forecastScoreHint: 'En los últimos {days} días: 100% significa que cada día se vendió justo lo que dijo el pronóstico; el error se pesa por lo vendido. Abajo, los artículos donde más falló (+ de más, − de menos).',
  forecastSaid: 'Pronóstico',
  soldWas: 'vendido',
  jobsTitle: 'Procesos programados',
  jobsRunAt: 'cada mañana después de las {hour}:00',
  jobsHint: 'Lo que la app hace con su propio reloj. Cada corrida se guarda; una fallida muestra por qué. Corre ahora si se perdió una mañana.',
  job_calendar: 'Calendario de días de demanda',
  'job_qbo-pull': 'Importación de QuickBooks',
  'job_daily-signals': 'Señales diarias por artículo',
  'job_forecast-score': 'Calificación del pronóstico',
  'job_line-costs': 'Costos en las líneas de factura',
  job_weather: 'Pronóstico del clima',
  'job_clover-pull': 'Ventas de la caja Clover',
  job_usda: 'Precios de mercado del USDA',
  'job_gmail-pull': 'Papeles de proveedores desde Gmail',
  'job_prices-follow-cost': 'Precios auto siguen al costo',
  jobOk: 'corrió',
  jobIdle: 'Sin configurar',
  jobLate: 'Atrasado',
  jobFailed: 'falló',
  jobNeverRan: 'nunca corrió',
  jobRows: 'filas',
  runNow: 'Correr ahora',
  signature: 'Firma',
  signHere: 'Firme con el dedo',
  signed: 'Firmado',
  clearSignature: 'Borrar',
  deliveryTitle: 'Entrega',
  deliveryHint: 'A dónde va el camión y lo que el chofer necesita saber. Se ve en la ruta.',
  addressLine1: 'Calle',
  addressLine2: 'Suite, unidad, portón',
  city: 'Ciudad',
  stateCode: 'Estado',
  zip: 'Código postal',
  windowStart: 'Ventana desde',
  windowEnd: 'Ventana hasta',
  deliveryNotes: 'Instrucciones para el chofer (puerta trasera, preguntar por María, el cuarto frío a la izquierda)',
  nobodyWaiting: 'Todos los esperados hoy ya pidieron',
  shipTo: 'Enviar a',
  descriptionCol: 'Descripción',
  invoiceNoLabel: 'Factura núm.',
  notesLabel: 'Notas',
  stampPaid: 'Pagada',
  stampVoid: 'Anulada',
  letterheadTitle: 'Membrete de las facturas',
  letterheadHint: 'El nombre, la dirección, el teléfono y el correo de su empresa tal como se imprimen arriba de cada factura. Deje una línea en blanco para omitirla.',
  companyNameLabel: 'Nombre de la empresa',
  addressLine1Label: 'Dirección',
  addressLine2Label: 'Dirección, línea 2',
  stateLabel: 'Estado',
  saveLetterhead: 'Guardar membrete',
  letterheadSaved: 'Guardado. Todas las facturas se imprimen con él.',
  pacaOffUntilLicense: 'Nada sobre PACA se imprime en las facturas (el aviso del fideicomiso, la cláusula de intereses y honorarios, la licencia) hasta que se llene el número de licencia.',
  invoicesCapped: 'Se muestran las {n} facturas más recientes de estas fechas: elija un rango más corto para ver las anteriores.',
  letterheadMissing: 'Su dirección todavía no aparece en las facturas: agréguela en Ajustes, PACA y membrete.',
};

export type AppMessageKey = MessageKey | WebMessageKey;
export type Dictionary = Record<AppMessageKey, string>;

export const dictionaries: Record<Locale, Dictionary> = {
  en: { ...messages.en, ...webEn },
  es: { ...messages.es, ...webEs },
};
